// The event catalog (03 §Catalog, fix #4): every event type the slice writes, its payload schema
// and version. The tables in docs/spec/03 §"Slice 1 catalog" are generated from this module
// (`bun run schema:docs`); the API routes declare which types they emit (`api/*.ts`), background
// producers are listed in `backgroundProducers`, and `events-catalog.test.ts` cross-checks all
// three plus every `emit(…)`/`draftEvent(…)` call in services/api.
//
// Versioning (03 §Outbox rules 5): a payload change that removes or retypes a field is a new
// version. The entry keeps the previous version's schema and an `upgrade` to the new shape, and
// `parseEventPayload` accepts version N and N-1. M3-01: `agent_key.created` / `.revoked` are at
// version 2 (a `service` key has no seat); every other type is at version 1.
import { z } from "zod";
import { Actor, ActorColumns, actorFromColumns, actorToColumns } from "./actor";
import {
  ApprovalProposal,
  ReservationChain,
  RunHandBack,
  SourceRefJson as WakeupSourceRef,
} from "./agent-rows";
import { ApprovalRefusalCode } from "./approvals";
import { DeployError, PanelRef, PointerRef } from "./de-config-rows";
import {
  AccessChange,
  AccessFamily,
  AccessGrantLevel,
  AccessLevel,
  AccessPrincipalKind,
  AccessSubjectKind,
  AccountCompanyRelation,
  AddonKey,
  AgencyGrantLevel,
  AgendaSection,
  AgentActionClass,
  AgentKeyKind,
  AgentKeyScope,
  AgentPolicyMode,
  AgentProfileOwnerKind,
  AgentProfileRole,
  AgentRequestKind,
  AgentSpecPart,
  AgentSpecSource,
  AgentTemplateKind,
  AgentTemplateScope,
  AgentUseSource,
  ApprovalExecutionCode,
  ApprovalExecutionFailure,
  ApprovalKind,
  ApprovalStatus,
  AssessmentParticipantKind,
  AssessmentProposalKind,
  AssessmentProposalState,
  AssessmentQuestionnaire,
  AttachmentSubjectKind,
  AutomationPauseReason,
  AutomationRunOutcome,
  AutomationScope,
  AutomationSource,
  AutonomyMode,
  Band,
  BoardColor,
  BoardDigestInterval,
  BreakGlassReadSubjectKind,
  BreakGlassScope,
  BudgetPeriodType,
  BudgetSubjectKind,
  // BILLING-PLANS
  BuildProjectStatus,
  CalendarAttendeeResponse,
  CalendarEventSource,
  CalendarKind,
  CalendarShareGrantee,
  CalendarShareLevel,
  CanvasInteraction,
  CategoryKind,
  ChannelKind,
  ChannelMemberRole,
  ChannelNotifyLevel,
  ChannelPostingPolicy,
  CliLoginLocation,
  CollectionMethod,
  CommitmentKind,
  CommitmentState,
  CompanyAgentMode,
  CompanyKind,
  CompensationCadence,
  CosDigestKind,
  CostEventKind,
  CrewActionKind,
  CrewAgentSource,
  CrewCapPeriod,
  CrewContextLayer,
  CrewContextVisibility,
  CrewMemoryBucket,
  CrewProposalKind,
  CrewProposalSource,
  CrewProposalState,
  Currency,
  DecisionSource,
  DeployKind,
  DeployPath,
  DigestFrequency,
  DigitalEmployeeStatus,
  DiscountAppliesTo,
  DriftAuditTrigger,
  EntitlementSource,
  EntitlementStatus,
  ExternalIdentityKind,
  FavoriteKind,
  type FeatureKey,
  FieldAudience,
  FieldType,
  FinanceRecordStatus,
  FlowKey,
  FollowLevel,
  FollowSubjectKind,
  FounderBriefCadence,
  FounderBriefSource,
  GapPolicy,
  GateMode,
  GoalState,
  GoalTrack,
  HeadcountHolderKind,
  // SKILLSMITH-OPPORTUNITIES
  HelperOfferStatus,
  HelperRequestStage,
  ImportResource,
  ImportRunSource,
  InboundWebhookScreenMode,
  InboundWebhookStatus,
  InboundWebhookVerification,
  InboxItemKind,
  InboxSubjectKind,
  // ADMIN-INSIGHTS
  InsightAskVia,
  InsightMeasure,
  InsightVisibility,
  IntegrationErrorClass as IntakeErrorClass,
  IntegrationJobKind as IntakeJobKind,
  InboundRuleKind as IntakeRuleKind,
  InboundSourceKind as IntakeSourceKind,
  InboundVerification as IntakeVerification,
  InvitationRole,
  InvoicePaymentMethod,
  InvoiceSource,
  IssueEscalation,
  IssueResolutionKind,
  IssueSourceKind,
  IssueStatus,
  KnowledgeSourceKind,
  KpiGroup,
  KpiPrincipalKind,
  KpiScope,
  LegacyTemplateClass,
  LoopIncidentKind,
  MaintenanceTier,
  MeetingOutcomeKind,
  MembershipKind,
  MembershipStatus,
  MemoryScopeKind,
  MeterSettlementResolution,
  MetricAggregation,
  MetricCadence,
  MetricDirection,
  MetricNormalization,
  MetricSource,
  MetricUnit,
  ModelAccessMode,
  ModelCredentialKind,
  ModelCredentialScope,
  normaliseTemplateClass,
  ObservationSource,
  ObservationStatus,
  ExternalActionKind as OutboxActionKind,
  ConnectionFamily as OutboxConnectionFamily,
  ConnectionGranteeKind as OutboxGranteeKind,
  ConnectionGrantLevel as OutboxGrantLevel,
  PageKind,
  PageMemberLevel,
  PageMemberPrincipalKind,
  PeriodType,
  PersonalCrewPauseReason,
  PortfolioProjectionKind,
  PriceListScope,
  PriceUnit,
  QuoteStatus,
  RecentKind,
  RecurringCadence,
  RecurringExpenseKind,
  RecurringTakeoverScope,
  RecurringWorkStatus,
  ReminderOrigin,
  ReplyLength,
  ReportingMethod,
  ReportingRemind,
  ResponsibilityStatus,
  RevenueKind,
  RunDecisionResult,
  RunEffortRequest,
  RunEscalationKind,
  RunIdentityMode,
  RunState,
  RunStepKind,
  SandboxKind,
  SeatDeFill,
  SeatEdgeKind,
  SeatOrigin,
  SeatStatus,
  SensitiveValueName,
  ServiceEndpointKey,
  SharingRule,
  SharingSubjectKind,
  SkillAdditionVia,
  SkillPackScope,
  SpaceKind,
  SpaceShareLevel,
  StatusAskEscalation,
  StatusAskSubjectKind,
  StructureChangeKind,
  StructureSubjectKind,
  SubjectKind,
  SubscriptionCadence,
  SubscriptionStatus,
  SuggestionKind,
  SuggestionResultKind,
  TagColor,
  TariffScope,
  TaskDependencyKind,
  TaskLinkRelation,
  TaskOrigin,
  TeamComposition,
  TeamRollup,
  TemplateClass,
  TemplateEditSource,
  TemplateEvalResult,
  TemplateOverlayScope,
  TemplateVersionSource,
  ThreadKind,
  TimeEntryGrain,
  TimeEntryKind,
  TimeEntrySource,
  ToolConnectionProvider,
  ToolConnectionScope,
  ToolConnectionState,
  TopicMemberVia,
  TrackingMode,
  Verdict,
  VerificationState,
  ViewKind,
  Visibility,
  WakeupKind,
  WebhookStatus,
  WorkerRefusalReason,
  WorkforceAgentReportsToKind,
  WorkforceAgentStatus,
} from "./enums";
import { CostMeter, ExternalSubject, IngestAttrs } from "./ingest";
import {
  boundedRecord,
  DayKey,
  Decimal,
  DottedName,
  Email,
  IsoDate,
  LIMITS,
  MicrosAmount,
  MinorAmount,
  MonthKey,
  PeriodKey,
  Timestamp,
  Uuid,
  WeekKey,
} from "./primitives";
import { ReachField, ReachSnapshot } from "./reach-rows";
import { DueRule } from "./reporting";
import {
  CompanyModelPolicy,
  CompanySettings,
  EventQuery,
  HelperQuote,
  OrgCalendarConfig,
  RunEffortTierSpec,
  RuntimeRef,
  SeatEdgePolicy,
  SourceRef,
  TaskMetricQuery,
  TaskTemplate,
  WebhookFilter,
  WorkerRuntime,
} from "./rows";
import { MetricBands, MetricGate, ObservationProvenance } from "./scorecard";
import {
  BlockNoteDoc,
  BoardSettings,
  FieldOptions,
  SpaceSettings,
  StatusDef,
  TaskOriginRef,
  ViewFilter,
  ViewSort,
} from "./work-rows";

// Catalog machinery ---------------------------------------------------------------------------

/** What an event's `subject` may point at. */
export const EventSubjectKind = z.enum([
  "company",
  "membership",
  "invitation",
  "role",
  "seat",
  "digital_employee",
  "agent_key",
  "external_identity",
  "metric_def",
  // AGENT-REPORTING
  "reporting_duty",
  "target_rule",
  "observation",
  "scorecard_publication",
  "friday_report",
  "cost_event",
  "de_subscription",
  "time_entry",
  "import_batch",
  "event",
  // M1-02
  "team",
  "seat_edge",
  "responsibility",
  // CHART-KB (02 §goal)
  "goal",
  // MEETINGS (02 §Operating cadence)
  "issue",
  // FOUNDER-OS
  "founder_brief",
  "meeting_series",
  "meeting",
  // CALENDAR-C1 (calendar design §2.1)
  "calendar",
  "calendar_event",
  "calendar_feed",
  // M8-ASSESS (02 §Assessment)
  "assessment_round",
  "assessment_participant",
  "assessment_proposal",
  "recurring_work",
  "template",
  "webhook",
  // M2-01
  "space",
  "channel",
  "topic",
  "message",
  "attachment",
  "inbox_item",
  "board",
  "view",
  "field_def",
  "task",
  // B4-ITEM-TEMPLATES
  "item_template",
  // CLOSED-LOOPS
  "commitment",
  // WOS-BUNDLE
  "tag",
  // M7-02
  "page",
  // M3-01
  "agent_profile",
  "wakeup_request",
  "run",
  "approval",
  "delegation_request",
  "budget",
  "price_list",
  // M4-01
  "personal_crew",
  "crew_agent",
  "role_skill_policy",
  "crew_context_layer",
  // M4-05
  "agent_room",
  "status_ask",
  // DC-01
  "agent_template",
  "agent_template_version",
  "template_edit_proposal",
  "deploy",
  "model_credential",
  "subscription_attestation",
  // BYO-01
  "tool_connection",
  "de_template_override",
  "drift_audit",
  "launch_token",
  // M7-03
  "promotion",
  "decision",
  "reminder",
  "recall_notice",
  // M4-02
  "skill_pack",
  // M4-03
  "crew_proposal",
  // M5-03
  "category",
  "client",
  "revenue_event",
  "compensation",
  "recurring_expense",
  "scenario",
  "headcount_plan",
  // M5-05
  "break_glass_session",
  // M7-05
  "recall_memory_copy",
  // IMPORT (HARDEN)
  "import_run",
  // CONNECTIONS
  "service_endpoint",
  // RUN-EFFORT
  "effort_default",
  // LOOP-BREAKER
  "loop_incident",
  // WF-CONTROL
  "control_token",
  // Parity P5 (process engine)
  "automation",
  // WORKFORCE (2026-10-26)
  "workforce_agent",
  // WEBHOOKS-IN
  "inbound_webhook",
  // WALLET-P1
  "wallet_entry",
  "wallet_checkout",
  "tariff",
  "topup_bonus_tier",
  // MANAGED-POLICY
  "agent_request",
  // TEMPLATES-T3
  "template_overlay",
  // REACH-S1: a person's ask to talk to an agent outside its reach
  "agent_reach_request",
  // CHAT-SWITCH-B
  "chat_ask",
  "chat_webhook",
  "scheduled_message",
  // GUIDED-FLOW
  "flow_run",
  // BILLING-PLANS
  "company_subscription",
  "invoice",
  "company_discount",
  "billing_charge",
  "quote",
  "build_project",
  "billing_operation",
  // SKILLSMITH-OPPORTUNITIES: a paid helper quote
  "helper_offer",
  // ADMIN-INSIGHTS: a question asked in Ask, an answer pinned to Home
  "insight_ask",
  "insight_widget",
  // SCREENS-S1
  "screen_stack",
  "browser_account",
  "screen_lease",
  "screen_ask",
  // STEP1-OUTBOX
  "action_outbox",
  // INC2-INTAKE
  "inbound_event",
  "inbound_rule",
  "integration_job",
  // INC2-CONTACT
  "contact",
  // STRUCTURE-CHANGED
  "structure_change",
  // B5-DECIDE
  "decision_rubric",
  // BIZ-CLIENT-HEALTH
  "client_survey",
  // RECALL-RULES / RECALL-PROCEDURES
  "memory_rule",
  "procedure_run",
  // lane B: REQUEST-IDENTITY / REQUEST-RECOVERY
  "request",
  "request_recovery",
]);
export type EventSubjectKind = z.infer<typeof EventSubjectKind>;

/** Documentation grouping (and the 03/12 section the generated table lands in). */
export const EventDomain = z.enum([
  "org",
  "access",
  "measurement",
  "reports",
  "ledger",
  "ingest",
  "operations",
  "founder_os",
  "work",
  "chat",
  "agents",
  "finance",
  "de_config",
  "memory",
]);
export type EventDomain = z.infer<typeof EventDomain>;

export type PreviousVersion = {
  version: number;
  payload: z.ZodType;
  /** Maps a parsed version-(N-1) payload to the version-N input shape. */
  upgrade: (previous: unknown) => unknown;
};

export type EventEntry<T extends string = string, P extends z.ZodType = z.ZodType> = {
  type: T;
  version: number;
  payload: P;
  /** The subject kind(s) the envelope may carry. */
  subject: EventSubjectKind | readonly EventSubjectKind[];
  domain: EventDomain;
  /** Consumers skip a company whose feature is not active/trial (ADR-0016). */
  feature: z.infer<typeof FeatureKey>;
  summary: string;
  /** In-cell consumers (03 vocabulary: `proj.kpi`, `audit`, …). */
  consumers: readonly string[];
  previous: readonly PreviousVersion[];
};

type EntryMeta = Omit<EventEntry, "type" | "payload" | "version" | "previous"> & {
  version?: number;
  previous?: readonly PreviousVersion[];
};

export function defineEvent<const T extends string, P extends z.ZodType>(
  type: T,
  meta: EntryMeta,
  payload: P,
): EventEntry<T, P> {
  const { version = 1, previous = [], ...rest } = meta;
  return { type, version, payload, previous, ...rest };
}

export function subjectKindsOf(entry: EventEntry): readonly EventSubjectKind[] {
  return typeof entry.subject === "string" ? [entry.subject] : entry.subject;
}

export type Catalog<E extends EventEntry> = {
  entries: readonly E[];
  get(type: string): E | undefined;
  /**
   * Parses a stored payload of `version` and returns it in the current version's shape. Accepts
   * the current version and every retained previous one (at least N-1); throws otherwise.
   */
  parse(type: string, version: number, payload: unknown): unknown;
};

export function createCatalog<const E extends EventEntry>(entries: readonly E[]): Catalog<E> {
  const byType = new Map<string, E>();
  for (const entry of entries) {
    if (byType.has(entry.type)) throw new Error(`duplicate event type ${entry.type}`);
    if (!DottedName.safeParse(entry.type).success) throw new Error(`bad event type ${entry.type}`);
    const kept = entry.previous.map((p) => p.version);
    if (entry.version > 1 && !kept.includes(entry.version - 1))
      throw new Error(`${entry.type} v${entry.version} must keep version ${entry.version - 1}`);
    if (kept.some((v) => v >= entry.version))
      throw new Error(`${entry.type}: a previous version must be lower than ${entry.version}`);
    byType.set(entry.type, entry);
  }
  /** Parses and refuses any field the schema does not declare, at any depth. */
  const strictParse = (schema: z.ZodType, raw: unknown, label: string) => {
    const secrets = secretFields(raw);
    if (secrets.length > 0)
      throw new Error(`${label}: secret-like payload field(s) ${secrets.join(", ")}`);
    const parsed = schema.parse(raw);
    const extra = unknownFields(raw, parsed);
    if (extra.length > 0)
      throw new Error(`${label}: undeclared payload field(s) ${extra.join(", ")}`);
    return parsed;
  };
  return {
    entries,
    get: (type) => byType.get(type),
    parse(type, version, payload) {
      const entry = byType.get(type);
      if (!entry) throw new Error(`unknown event type ${type}`);
      if (version === entry.version) return strictParse(entry.payload, payload, type);
      const chain = [...entry.previous].sort((a, b) => a.version - b.version);
      const start = chain.findIndex((p) => p.version === version);
      if (start < 0) throw new Error(`${type} version ${version} is no longer accepted`);
      let value: unknown = strictParse(chain[start]?.payload as z.ZodType, payload, type);
      for (let i = start; i < chain.length; i++) value = chain[i]?.upgrade(value);
      return entry.payload.parse(value);
    },
  };
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

/**
 * Field names an event payload may never carry, at any depth and even inside open records
 * (`attrs`, `settings`): LiteLLM key material, hashes, sealed/encrypted values, key ids of
 * sealed values, tokens, passwords and plaintext secrets (finding #1). The pattern applies to
 * the normalized name (`normalizeFieldName`), so `key_hash`, `keyHash`, `key-hash` and
 * `KEY_HASH` are one name.
 */
export const SECRET_FIELD =
  /^litellm|(hash|hashed|enc|encrypted|sealed)$|password|secret|plaintext|privatekey|apikey|(access|refresh|id|session|bearer|auth)token$/;

/**
 * The key id of a sealed value (`*_kid`) is matched on word boundaries of the original name,
 * not on the normalized one: normalized, `task_id` is `taskid` and would read as `…kid`
 * (M1-02, needed by every task event). `kid`, `secret_kid`, `secretKid`, `SECRET-KID` match.
 */
const KEY_ID_WORD = /(^|[_-])kid$/i;
const KEY_ID_CAMEL = /[a-z0-9]Kid$/;

/** Lower-cased with `_` and `-` removed: one spelling for snake, camel and kebab case. */
export const normalizeFieldName = (name: string) => name.toLowerCase().replace(/[_-]/g, "");

export const isSecretField = (name: string) =>
  SECRET_FIELD.test(normalizeFieldName(name)) || KEY_ID_WORD.test(name) || KEY_ID_CAMEL.test(name);

/** Paths of every secret-like key in a value. */
export function secretFields(raw: unknown, path = ""): string[] {
  const at = (k: string | number) => (path ? `${path}.${k}` : String(k));
  if (Array.isArray(raw)) return raw.flatMap((v, i) => secretFields(v, at(i)));
  if (!isPlainObject(raw)) return [];
  return Object.entries(raw).flatMap(([k, v]) => [
    ...(isSecretField(k) ? [at(k)] : []),
    ...secretFields(v, at(k)),
  ]);
}

/**
 * Paths present in `raw` but dropped by parsing (zod strips undeclared keys of plain objects).
 * Event payloads are closed: an undeclared field, at any depth, is how a secret would leak.
 */
export function unknownFields(raw: unknown, parsed: unknown, path = ""): string[] {
  const at = (k: string | number) => (path ? `${path}.${k}` : String(k));
  if (Array.isArray(raw) && Array.isArray(parsed))
    return raw.flatMap((r, i) => unknownFields(r, parsed[i], at(i)));
  if (isPlainObject(raw) && isPlainObject(parsed))
    return Object.keys(raw).flatMap((k) =>
      k in parsed ? unknownFields(raw[k], parsed[k], at(k)) : [at(k)],
    );
  return [];
}

// Shared payload parts ------------------------------------------------------------------------

/** HELPERS-2: what an event carries in place of a DE's person name (never the name). */
export const PERSON_NAME_MASK = "(person name)";

/**
 * Field-level diff of an `.updated` event, `{field: {from, to}}`, over an explicit allowlist of
 * changeable fields with their value schemas. Secrets (sealed keys, hashes) are never in an
 * allowlist, so a diff can never carry one.
 */
function changesOf<const S extends Record<string, z.ZodType>>(fields: S) {
  const shape = Object.fromEntries(
    Object.entries(fields).map(([k, s]) => [
      k,
      z.strictObject({ from: s.nullable(), to: s.nullable() }).optional(),
    ]),
  ) as {
    [K in keyof S]: z.ZodOptional<
      z.ZodObject<{ from: z.ZodNullable<S[K]>; to: z.ZodNullable<S[K]> }>
    >;
  };
  return z
    .strictObject(shape)
    .refine((c) => Object.keys(c).length > 0, { message: "at least one change" });
}

/** The changeable fields of each `.updated` / `.changed` event (finding #1). */
export const changeAllowlists = {
  "company.updated": ["name", "settings", "calendar_config", "reporting_currency"],
  "membership.updated": ["display_name", "status", "avatar_key", "agent_direction"],
  "role.updated": ["name", "description", "default_metric_keys"],
  "seat.updated": [
    "role_id",
    "title",
    "supervisor_seat_id",
    "staff",
    "status",
    "settings",
    "sort_order",
    "de_fill",
  ],
  "invite.updated": ["roles"],
  "digital_employee.updated": [
    "template_key",
    "runtime_ref",
    "started_on",
    "role",
    "goal",
    "backstory",
    "standing_orders",
    "how_i_work_version",
    "person_name",
    "max_concurrency",
    "task_limits",
  ],
  "metric_def.updated": [
    "name",
    "description",
    "unit",
    "direction",
    "aggregation",
    "normalization",
    "cadence",
    "source",
    "event_query",
    "formula",
    "task_query",
    "visibility",
    "gap_policy",
    "needs_review",
    "bands",
    "gate",
  ],
  "friday_report.updated": ["supervisor_note"],
  "de_subscription.changed": ["amount_minor", "end_month", "scope"],
  "team.updated": ["name", "parent_team_id", "lead_seat_id", "composition", "human_owner_seat_id"],
  "workforce_agent.updated": [
    "name",
    "team_id",
    "runtime",
    "reports_to_membership_id",
    "status",
    "last_activity_at",
    "last_activity",
    "role",
    "goal",
    "backstory",
    "responsibilities",
    "standing_orders",
    "skills",
  ],
  "goal.updated": [
    "title",
    "period_key",
    "due_at",
    "state",
    "track",
    "metric_def_id",
    "target",
    "milestones",
  ],
  "assessment.round_updated": ["name", "invite_expiry_days"],
  "issue.updated": [
    "title",
    "body",
    "owner_seat_id",
    "priority",
    "team_id",
    "position",
    "archived_at",
  ],
  "responsibility.updated": ["name", "description", "weight", "kpi_metric_ids", "status"],
  "recurring_work.updated": ["schedule", "board_id", "task_template", "status", "next_run_at"],
  "template.updated": ["name", "description", "version"],
  "webhook.updated": ["url", "description", "types", "filter", "status"],
  // WOS-BUNDLE
  "tag.updated": ["name", "color"],
  "space.updated": ["name", "team_id", "icon", "description", "is_private", "settings"],
  "channel.updated": [
    "name",
    "description",
    "is_private",
    "is_agent_room",
    "posting_policy",
    "topic_policy",
    "broadcast_policy",
    "default_notify_level",
  ],
  "board.updated": [
    "name",
    "space_id",
    "status_set",
    "settings",
    "digest_interval",
    "color",
    "lead_seat_id",
    "page_id",
  ],
  "view.updated": [
    "name",
    "kind",
    "filter",
    "sort",
    "group_by",
    "visible_fields",
    "date_field",
    "card_fields",
    "is_default",
    "position",
  ],
  "field_def.updated": ["name", "options", "audience", "requires_approval", "position"],
  // B4-ITEM-TEMPLATES
  "item_template.updated": ["name", "description", "payload", "position"],
  "task.updated": ["title", "start_at", "estimate_minutes", "parent_task_id", "restricted"],
  "agent_profile.updated": ["role_in_owner", "template_version", "overrides", "memory_group_id"],
  "personal_crew.updated": ["heartbeat_schedule", "max_concurrency", "model_policy", "cap_minor"],
  "agent_room.updated": ["window_size", "max_agent_turns"],
} as const;

const PeriodKeys = z.strictObject({ day: DayKey, week: WeekKey, month: MonthKey });
/** LOOP-BREAKER: the loop thresholds (api/loop-breaker.ts `LoopThresholds`). */
const LoopThresholdsJson = z.strictObject({
  enabled: z.boolean(),
  repeat_limit: z.int(),
  intent_repeat_limit: z.int(),
  stall_minutes: z.int(),
  similarity_pct: z.int(),
});

const ObservationRef = {
  observation_id: Uuid,
  metric_def_id: Uuid,
  subject_kind: SubjectKind,
  subject_id: Uuid,
  period_type: PeriodType,
  period_key: PeriodKey,
};

const TimeEntryRef = {
  time_entry_id: Uuid,
  seat_id: Uuid,
  membership_id: Uuid,
  kind: TimeEntryKind,
  grain: TimeEntryGrain,
  work_date: IsoDate,
  period_key: PeriodKey,
  source: TimeEntrySource,
  source_event_id: z.string(),
  revision: z.int().min(1),
  approval_status: ApprovalStatus,
  visibility: Visibility,
};

const IngestedItem = {
  type: DottedName,
  occurred_at: Timestamp,
  quantity: z.number(),
  value: z.number().optional(),
  subject: ExternalSubject.optional(),
  attrs: IngestAttrs.optional(),
  /** Server-computed in the company calendar (13 §Ingest contract rule 4). */
  period_keys: PeriodKeys,
};

/** Ids of everything a template application created (`template.applied`). */
const TemplateCreated = z.strictObject({
  roles: z.array(Uuid),
  teams: z.array(Uuid),
  seats: z.array(Uuid),
  responsibilities: z.array(Uuid),
  recurring_work: z.array(Uuid),
  spaces: z.array(Uuid),
  channels: z.array(Uuid),
  boards: z.array(Uuid),
  tasks: z.array(Uuid),
});

const DeStatusChange = z.strictObject({
  from: DigitalEmployeeStatus,
  reason: z.string().optional(),
});

/**
 * PURGE-RETIRED-AGENTS: what a purge removed and kept (counts), where its open tasks went, its
 * memory (the Recall agent and the spaces only it used, archived or deleted after commit), and
 * what ClawPanel removes (the Hermes profile names CompanyOS allocated for it).
 */
const AgentPurged = z.strictObject({
  agent_kind: z.enum(["digital_employee", "workforce_agent"]),
  name: z.string(),
  /** What its messages, tasks and events now show as their author. */
  removed_label: z.string(),
  slug: z.string().nullable(),
  removed: z.record(z.string(), z.int().min(0)),
  kept: z.record(z.string(), z.int().min(0)),
  tasks_reassigned: z.int().min(0),
  tasks_to_seat_id: Uuid.nullable(),
  memory: z.strictObject({
    mode: z.enum(["archive", "delete"]),
    recall_agent: z.string().nullable(),
    spaces: z.array(z.string()),
    writes: z.int().min(0),
  }),
  clawpanel: z.strictObject({
    /** Every Hermes profile name CompanyOS allocated for the DE (`cp.panel_profile`). */
    profiles: z.array(z.string()),
    /** The DE's managed runtime binding, when it had one. */
    runtime_ref: RuntimeRef.nullable(),
    /** A self-hosted worker's container and profile (an AG's row, a DE's lead profile). */
    runtime: WorkerRuntime.nullable(),
  }),
});

const AccessTuple = z.strictObject({
  object_type: z.enum(["company", "seat", "space", "channel"]),
  object_id: Uuid,
  relation: z.string(),
  subject_type: z.enum(["membership", "agency_user"]),
  subject_id: Uuid,
  subject_relation: z.string().nullable(),
  /** `access_version.version` after the change (fix #25). */
  access_version: z.int().min(1),
});

const org = { domain: "org", feature: "core" } as const;
/** MEETINGS: the operating cadence (03 §Operating cadence), documented with org. */
const cadence = { domain: "org", feature: "core" } as const;
/** CALENDAR-C1: the native calendar (a base-product surface beside Work). */
const calendarDomain = { domain: "work", feature: "core" } as const;
/** M8-ASSESS: assessment rounds sit with the org (Setup › Structure). */
const assess = { domain: "org", feature: "core" } as const;

/** `task.checklist_changed` version 1 (M2): one flat checklist per task, item ops only. */
const TaskChecklistChangedV1 = z.strictObject({
  board_id: Uuid,
  item_id: Uuid,
  op: z.enum(["added", "updated", "removed"]),
  done: z.boolean(),
});

/** `agent_key.*` version 1 (S1): every key had a seat. Upgrades to v2 unchanged. */
const AgentKeyCreatedV1 = z.strictObject({
  kind: AgentKeyKind,
  membership_id: Uuid,
  seat_id: Uuid,
  key_prefix: z.string().length(12),
  scopes: z.array(AgentKeyScope),
  backfill: z.boolean(),
  meter_namespaces: z.array(z.string()),
  expires_at: Timestamp.nullable(),
});
const AgentKeyRevokedV1 = z.strictObject({
  membership_id: Uuid,
  seat_id: Uuid,
  reason: z.enum(["manual", "seat_closed", "membership_deactivated"]),
});
/** `reminder.*` version 1 (M7-03, M4-03): every reminder was a seat's. v2 adds `membership_id`. */
const ReminderCreatedV1 = z.strictObject({
  seat_id: Uuid,
  at: Timestamp,
  origin: ReminderOrigin,
  origin_ref: z.string().nullable(),
  subject_kind: z.string().nullable(),
  subject_id: Uuid.nullable(),
});
const ReminderFiredV1 = z.strictObject({
  seat_id: Uuid,
  at: Timestamp,
  recipients: z.int().min(0),
});
const ReminderDismissedV1 = z.strictObject({ seat_id: Uuid, dismissed_by_membership_id: Uuid });
/** v1 named the done-for-you class `htva` (now `managed`, migration 0162). */
const AgentTemplateCreatedV1 = z.strictObject({
  scope: AgentTemplateScope,
  agency_id: Uuid.nullable(),
  key: z.string(),
  name: z.string(),
  template_class: LegacyTemplateClass,
});
const kpi = { domain: "measurement", feature: "kpi" } as const;
const ledger = { domain: "ledger", feature: "core" } as const;
/** Spaces, boards, tasks, follows, the inbox and attachments: base plan (ADR-0016 §8). */
const work = { domain: "work", feature: "core" } as const;
/** Channels, topics and messages: the `chat` add-on. */
const chat = { domain: "chat", feature: "chat" } as const;
/** Runs, wake-ups, approvals, delegation, agent profiles: the `agents` add-on (ADR-0016). */
const agents = { domain: "agents", feature: "agents" } as const;
/** Budgets, prices and FX: finance controls apply to every cost, so they are base plan. */
const finance = { domain: "finance", feature: "core" } as const;
/**
 * DE configuration (DC-01; 03 §DE configuration). Catalog, deploy and credential events are
 * control-plane facts recorded in the event log of the company whose principal acted, so a
 * cell never holds another company's actor:
 *   - catalog (`agent_template*`, `template_edit.*`), `done_for_you` deploys, overrides and
 *     launch tokens: HTVA's own company (`cp.platform_setting gate_company_id`), where the
 *     Deployment engineer, the agency operators and the platform act;
 *   - a company owner's approval of a `done_for_you` deploy and every `self_serve` deploy event:
 *     the target company;
 *   - model credentials and attestations: the credential's company.
 * The target cell learns about its own DE only through `digital_employee.deployed` /
 * `.deploy_failed` (written by the `consumer:deploy.mirror`). The done-for-you service is base
 * plan; run-level events (verdicts, decisions, drift audits) are the `agents` add-on.
 */
const deConfig = { domain: "de_config", feature: "core" } as const;
const deRuns = { domain: "de_config", feature: "agents" } as const;
const Sha256 = z.string().regex(/^[0-9a-f]{64}$/);
const VersionRef = { template_id: Uuid, version: z.int().min(1) };
const ValidationOutcome = {
  ...VersionRef,
  skill_index_tokens: z.int().min(0).nullable(),
  skill_index_token_cap: z.int().min(1),
  has_verification_skill: z.boolean(),
  eval_results: z.record(z.string(), TemplateEvalResult),
  /** SHA-256 of the canonical version content (`content_hash`; named so it is not a secret). */
  content_digest: Sha256.nullable(),
  reasons: z.array(z.string().max(LIMITS.text)).max(LIMITS.list),
};
const DeployTarget = { target_company_id: Uuid, path: DeployPath };
const DeployApprover = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("user"), user_id: Uuid }),
  z.strictObject({ kind: z.literal("member"), membership_id: Uuid }),
]);
const DriftAuditRef = {
  de_membership_id: Uuid,
  template_version_id: Uuid,
  trigger: DriftAuditTrigger,
};
/** Recall sync, callbacks and the memory rows they create: the `memory` add-on (07). */
const memory = { domain: "memory", feature: "memory" } as const;
const importOps = { domain: "operations", feature: "core", subject: "import_run" } as const;
// M5-05: break-glass sessions (cp rows, emitted in the company they open).
const breakGlass = { domain: "access", feature: "core", subject: "break_glass_session" } as const;
const BreakGlassPerson = z.strictObject({ user_id: Uuid, name: z.string() });
const BreakGlassRef = {
  agency_id: Uuid,
  requested_by: BreakGlassPerson,
  scope: BreakGlassScope,
  scope_id: Uuid.nullable(),
};

// Catalog -------------------------------------------------------------------------------------

export const eventEntries = [
  // Company and org
  defineEvent(
    "company.created",
    {
      ...org,
      subject: "company",
      summary: "Operator created a client company",
      consumers: ["audit"],
    },
    z.strictObject({
      slug: z.string(),
      name: z.string(),
      kind: CompanyKind,
      reporting_currency: Currency,
      calendar_config: OrgCalendarConfig,
    }),
  ),
  defineEvent(
    "company.updated",
    {
      ...org,
      subject: "company",
      summary: "Company settings, calendar or currency changed",
      consumers: ["proj.kpi", "audit"],
    },
    z.strictObject({
      changes: changesOf({
        name: z.string(),
        settings: CompanySettings,
        calendar_config: OrgCalendarConfig,
        reporting_currency: Currency,
      }),
    }),
  ),
  defineEvent(
    "company.model_policy_changed",
    {
      ...org,
      subject: "company",
      summary: "An owner or admin set the company's model policy (Setup › Model policy)",
      consumers: ["run admission (next run)", "audit"],
    },
    z.strictObject({
      changed: z
        .array(z.enum(["residency", "allowed_models", "default_model", "role_defaults"]))
        .min(1),
      policy: CompanyModelPolicy,
    }),
  ),
  // EXPORT-CORE: an owner took the Company Archive out (offboarding-portability §5 "Audit").
  // Requested in the request's transaction; completed or failed once the read-only build ended.
  defineEvent(
    "company.export_requested",
    {
      ...org,
      subject: "company",
      summary: "An owner asked for the Company Archive (re-authenticated)",
      consumers: ["audit"],
    },
    z.strictObject({ export_id: Uuid, format: z.string(), reauth: z.enum(["password", "totp"]) }),
  ),
  defineEvent(
    "company.export_completed",
    {
      ...org,
      subject: "company",
      summary: "The Company Archive passed the secret scan and was downloaded",
      consumers: ["audit"],
    },
    z.strictObject({
      export_id: Uuid,
      format: z.string(),
      files: z.int().min(0),
      bytes: z.int().min(0),
      manifest_sha256: z.string(),
      redacted_fields: z.int().min(0),
      ip: z.string().nullable(),
    }),
  ),
  defineEvent(
    "company.export_failed",
    {
      ...org,
      subject: "company",
      summary:
        "The Company Archive was not handed out: the secret scan found a credential, or the build failed",
      consumers: ["audit"],
    },
    z.strictObject({
      export_id: Uuid,
      reason: z.enum(["secret_found", "error"]),
      /** Which files and which patterns (names only, never the matched text). */
      findings: z.array(z.strictObject({ path: z.string(), pattern: z.string() })),
      error: z.string().nullable(),
    }),
  ),
  defineEvent(
    "company.model_access_changed",
    {
      ...org,
      subject: "company",
      summary:
        "How model calls are paid changed: an owner or admin chose ours (billed) or the company's own keys (Connections › API keys), or the company's plan stopped allowing own keys",
      consumers: ["run admission (next run)", "deploys.model_keys", "proj.inbox", "audit"],
    },
    z.strictObject({
      mode: ModelAccessMode,
      fallback_to_ours: z.boolean(),
      previous_mode: ModelAccessMode,
      /**
       * `plan_closed`: the operator turned the plan's own-keys setting off (a system event; the
       * company's owners and admins are told). Absent: the company's own choice.
       */
      reason: z.enum(["plan_closed"]).optional(),
    }),
  ),
  defineEvent(
    "company.entitlement_changed",
    {
      domain: "access",
      feature: "core",
      subject: "company",
      summary: "Operator set an add-on entitlement (ADR-0016)",
      consumers: ["entitlement cache", "proj.kpi (rebuild on re-enable)", "audit"],
    },
    z.strictObject({
      feature_key: AddonKey,
      status: EntitlementStatus,
      previous_status: EntitlementStatus.nullable(),
      source: EntitlementSource,
      bundled_by_feature_key: AddonKey.nullable(),
      starts_on: IsoDate,
      ends_on: IsoDate.nullable(),
      read_only_until: IsoDate.nullable(),
      note: z.string().nullable(),
    }),
  ),
  defineEvent(
    "agency_grant.created",
    {
      domain: "access",
      feature: "core",
      subject: "company",
      summary: "Agency granted access to a company",
      consumers: ["audit"],
    },
    z.strictObject({
      grant_id: Uuid,
      agency_id: Uuid,
      level: AgencyGrantLevel,
      granted_by_user_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "membership.created",
    {
      ...org,
      subject: "membership",
      summary: "Person invited or DE membership created",
      consumers: ["audit"],
    },
    z.strictObject({
      user_id: Uuid.nullable(),
      kind: MembershipKind,
      status: MembershipStatus,
      display_name: z.string(),
      invited_email: Email.optional(),
    }),
  ),
  defineEvent(
    "invite.created",
    {
      ...org,
      subject: "invitation",
      summary: "Pending membership created; its delivery consumer mails the invite token",
      consumers: ["mail.invite", "audit"],
    },
    z.strictObject({
      email: Email,
      display_name: z.string(),
      roles: z.array(InvitationRole).min(1),
      expires_at: Timestamp,
      superseded_invitation_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "invite.accepted",
    {
      ...org,
      subject: "invitation",
      summary: "Invitee with a verified email accepted; membership active, relations granted",
      consumers: ["audit"],
    },
    z.strictObject({
      membership_id: Uuid,
      user_id: Uuid,
      roles: z.array(InvitationRole).min(1),
    }),
  ),
  defineEvent(
    "invite.revoked",
    {
      ...org,
      subject: "invitation",
      summary: "A pending invitation was withdrawn; its token stops working",
      consumers: ["audit"],
    },
    z.strictObject({
      email: Email,
      roles: z.array(InvitationRole).min(1),
    }),
  ),
  defineEvent(
    "invite.updated",
    {
      ...org,
      subject: "invitation",
      summary: "GUIDED-FLOW: a pending invitation's access level (roles) changed in seat setup",
      consumers: ["audit"],
    },
    z.strictObject({
      changes: changesOf({ roles: z.array(InvitationRole).min(1) }),
    }),
  ),
  // GUIDED-FLOW (build order increment 2): a flow run's life. Answers are never in a payload
  // (they are the real fields' own events: `seat.updated`, `digital_employee.updated`, …); a
  // step save names the fields it wrote.
  defineEvent(
    "flow.sent",
    {
      ...org,
      subject: "flow_run",
      summary: "A guided flow was sent to a person (or an invitee) about a seat",
      consumers: ["audit"],
    },
    z.strictObject({
      flow_key: FlowKey,
      seat_id: Uuid,
      assignee_membership_id: Uuid.nullable(),
      invitation_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "flow.step_saved",
    {
      ...org,
      subject: "flow_run",
      summary: "A step of a guided flow was saved (the field keys it wrote; never values)",
      consumers: ["audit"],
    },
    z.strictObject({
      flow_key: FlowKey,
      step_key: z.string().min(1).max(64),
      fields: z.array(z.string().min(1).max(64)).max(32),
    }),
  ),
  defineEvent(
    "flow.completed",
    {
      ...org,
      subject: "flow_run",
      summary: "A guided flow was submitted with every required field in place",
      consumers: ["audit"],
    },
    z.strictObject({ flow_key: FlowKey, seat_id: Uuid }),
  ),
  defineEvent(
    "flow.cancelled",
    {
      ...org,
      subject: "flow_run",
      summary: "An open guided flow was withdrawn",
      consumers: ["audit"],
    },
    z.strictObject({ flow_key: FlowKey, seat_id: Uuid }),
  ),
  // CANVAS-BLOCKS: a person's click on their Live page (the op row is the agent's context; the
  // event is the record that it happened). Ids only, never the block's content.
  defineEvent(
    "live.interacted",
    {
      ...org,
      subject: "membership",
      summary: "A person interacted with their Live workspace (opened a card, played a video)",
      consumers: [],
    },
    z.strictObject({
      session_id: Uuid,
      op_id: Uuid,
      action: CanvasInteraction,
      block_id: z.string().max(40).nullable(),
      item_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "sensitive_value.sealed",
    {
      ...org,
      subject: "membership",
      summary:
        "A person sealed pay, bank or ID details in a secure step (the names only; values never leave the vault)",
      consumers: ["audit"],
    },
    z.strictObject({
      seat_id: Uuid.nullable(),
      names: z.array(SensitiveValueName).min(1),
    }),
  ),
  // OOO-DELEGATE: a person's out-of-office window (the subject is the person's membership)
  defineEvent(
    "ooo.window_set",
    {
      ...org,
      subject: "membership",
      summary: "A person set an out-of-office window with an optional delegate",
      consumers: ["audit"],
    },
    z.strictObject({
      window_id: Uuid,
      seat_id: Uuid.nullable(),
      delegate_membership_id: Uuid.nullable(),
      starts_at: Timestamp,
      ends_at: Timestamp,
    }),
  ),
  defineEvent(
    "ooo.window_ended",
    {
      ...org,
      subject: "membership",
      summary: "An out-of-office window was ended early; routing is restored",
      consumers: ["audit"],
    },
    z.strictObject({ window_id: Uuid }),
  ),
  defineEvent(
    "ooo.summary_seen",
    {
      ...org,
      subject: "membership",
      summary: "A person dismissed the return summary of an out-of-office window",
      consumers: ["audit"],
    },
    z.strictObject({ window_id: Uuid, routed: z.number().int().min(0) }),
  ),
  defineEvent(
    "membership.updated",
    {
      ...org,
      subject: "membership",
      summary: "Display name or status (active/paused) changed",
      consumers: ["audit"],
    },
    z.strictObject({
      changes: changesOf({
        display_name: z.string(),
        status: MembershipStatus,
        avatar_key: z.string(),
        // COS-MODES: the person's "Who may direct my agents" (`settings.agent_direction`)
        agent_direction: z.record(z.string(), z.unknown()),
      }),
    }),
  ),
  defineEvent(
    "membership.deactivated",
    { ...org, subject: "membership", summary: "Membership deactivated", consumers: ["audit"] },
    z.strictObject({ previous_status: MembershipStatus }),
  ),
  defineEvent(
    "access.granted",
    {
      domain: "access",
      feature: "core",
      subject: ["company", "seat", "space", "channel"],
      summary: "Relation tuple written; subject is the tuple's object",
      consumers: ["audit"],
    },
    AccessTuple,
  ),
  defineEvent(
    "access.revoked",
    {
      domain: "access",
      feature: "core",
      subject: ["company", "seat", "space", "channel"],
      summary: "Relation tuple deleted; subject is the tuple's object",
      consumers: ["audit"],
    },
    AccessTuple,
  ),
  defineEvent(
    "role.created",
    { ...org, subject: "role", summary: "Role created", consumers: ["audit"] },
    z.strictObject({
      name: z.string(),
      description: z.string().nullable(),
      default_metric_keys: z.array(z.string()),
    }),
  ),
  defineEvent(
    "role.updated",
    { ...org, subject: "role", summary: "Role changed", consumers: ["audit"] },
    z.strictObject({
      changes: changesOf({
        name: z.string(),
        description: z.string(),
        default_metric_keys: z.array(z.string()),
      }),
    }),
  ),
  // ACTIONS: only a role nothing uses (no seat, skill policy or skill pack names it).
  defineEvent(
    "role.deleted",
    { ...org, subject: "role", summary: "Unused role deleted", consumers: ["audit"] },
    z.strictObject({ name: z.string() }),
  ),
  defineEvent(
    "seat.created",
    { ...org, subject: "seat", summary: "Seat created", consumers: ["proj.kpi", "audit"] },
    z.strictObject({
      role_id: Uuid,
      title: z.string(),
      supervisor_seat_id: Uuid.nullable(),
      status: SeatStatus,
      origin: SeatOrigin,
    }),
  ),
  defineEvent(
    "seat.updated",
    {
      ...org,
      subject: "seat",
      summary: "Seat changed (If-Match version bumped)",
      consumers: ["proj.kpi", "audit"],
    },
    z.strictObject({
      changes: changesOf({
        role_id: Uuid,
        title: z.string(),
        supervisor_seat_id: Uuid,
        staff: z.boolean(),
        status: SeatStatus,
        settings: z.record(z.string(), z.unknown()),
        /** CHART-TABLE-ORDER: the place among its supervisor's seats (`PUT org-chart/order`). */
        sort_order: z.int().min(0),
        /** DE-SEAT-OWNER: the seat's switch (flag `de_seat_owner`). */
        de_fill: SeatDeFill,
      }),
      version: z.int().min(1),
    }),
  ),
  defineEvent(
    "seat.assigned",
    {
      ...org,
      subject: "seat",
      summary: "Person or DE assigned to a seat",
      consumers: ["proj.kpi", "audit"],
    },
    z.strictObject({
      assignment_id: Uuid,
      membership_id: Uuid,
      membership_kind: MembershipKind,
      share: Decimal,
      active_from: Timestamp,
    }),
  ),
  defineEvent(
    "seat.unassigned",
    { ...org, subject: "seat", summary: "Assignment ended", consumers: ["proj.kpi", "audit"] },
    z.strictObject({
      assignment_id: Uuid,
      membership_id: Uuid,
      membership_kind: MembershipKind,
      active_to: Timestamp,
    }),
  ),
  defineEvent(
    "seat.closed",
    {
      ...org,
      subject: "seat",
      summary: "Seat closed; its assignments end and its keys are revoked in the same transaction",
      consumers: ["proj.kpi", "audit"],
    },
    z.strictObject({ ended_assignment_ids: z.array(Uuid), revoked_key_ids: z.array(Uuid) }),
  ),
  // TEAM-KPI (founder-approved 2026-10-21): a team's KPI set (scorecards are computed on read).
  defineEvent(
    "team_metric.changed",
    {
      ...kpi,
      subject: "team",
      summary: "A team's KPI set changed: one metric assigned, re-weighted or taken off",
      consumers: ["audit"],
    },
    z.strictObject({
      items: z.array(
        z.strictObject({
          metric_def_id: Uuid,
          rollup: TeamRollup,
          kpi_weight: Decimal,
          position: z.int(),
        }),
      ),
      removed_metric_def_ids: z.array(Uuid),
    }),
  ),
  defineEvent(
    "seat_metric.changed",
    {
      ...kpi,
      subject: "seat",
      summary: "Seat metric set and weights replaced",
      consumers: ["proj.kpi"],
    },
    z.strictObject({
      items: z.array(
        z.strictObject({
          metric_def_id: Uuid,
          kpi_weight: Decimal,
          goal_weight: Decimal,
          tracking_mode: TrackingMode,
          penalty_only: z.boolean(),
          position: z.int(),
        }),
      ),
      removed_metric_def_ids: z.array(Uuid),
    }),
  ),
  defineEvent(
    "digital_employee.created",
    {
      ...org,
      subject: "digital_employee",
      summary: "DE created (membership, assignment, template metrics)",
      consumers: ["proj.kpi", "audit"],
    },
    z.strictObject({
      seat_id: Uuid,
      supervisor_seat_id: Uuid,
      template_key: z.string(),
      runtime_ref: RuntimeRef.nullable(),
      started_on: IsoDate.nullable(),
      status: DigitalEmployeeStatus,
    }),
  ),
  defineEvent(
    "digital_employee.updated",
    {
      ...org,
      subject: "digital_employee",
      summary: "DE profile fields changed (not status)",
      consumers: ["audit"],
    },
    z.strictObject({
      changes: changesOf({
        template_key: z.string(),
        runtime_ref: RuntimeRef,
        started_on: IsoDate,
        role: z.string(),
        goal: z.string(),
        backstory: z.string(),
        standing_orders: z.string(),
        /**
         * CHART-HOWIWORK: a new "How I work" version was saved (its text and author are the
         * version row's, `de_process_version`; the event carries only the numbers).
         */
        how_i_work_version: z.int().min(0),
        /**
         * HELPERS: the optional person name before the role, masked (HELPERS-2: a name is
         * personal; the stream says that it was set, changed or cleared, never the name itself;
         * the row holds it). `PERSON_NAME_MASK` for a name, null for none.
         */
        person_name: z.literal(PERSON_NAME_MASK),
        /** DISPATCHER-CORE: how many subjects the DE may work on at once (1..32). */
        max_concurrency: z.int().min(1).max(32),
        /** TASK-BUDGETS: the DE's per-task limit override (null: none). */
        task_limits: z.strictObject({
          steps: z.int().min(1).optional(),
          cost_micros: z.int().min(1).optional(),
          time_ms: z.int().min(1).optional(),
        }),
      }),
    }),
  ),
  defineEvent(
    "digital_employee.activated",
    {
      ...org,
      subject: "digital_employee",
      summary: "onboarding → active",
      consumers: ["proj.kpi", "audit"],
    },
    DeStatusChange,
  ),
  defineEvent(
    "digital_employee.paused",
    { ...org, subject: "digital_employee", summary: "DE paused", consumers: ["proj.kpi", "audit"] },
    DeStatusChange,
  ),
  defineEvent(
    "digital_employee.resumed",
    {
      ...org,
      subject: "digital_employee",
      summary: "DE resumed",
      consumers: ["proj.kpi", "audit"],
    },
    DeStatusChange,
  ),
  defineEvent(
    "digital_employee.retired",
    {
      ...org,
      subject: "digital_employee",
      summary: "DE retired",
      consumers: ["proj.kpi", "audit"],
    },
    DeStatusChange,
  ),
  defineEvent(
    "digital_employee.removed",
    {
      ...org,
      subject: "digital_employee",
      summary:
        "DE removed (AGENT-MENTION): retired, every key revoked, its LiteLLM keys queued for deletion; hidden from the roster",
      consumers: ["audit"],
    },
    DeStatusChange,
  ),
  defineEvent(
    "agent.purged",
    {
      ...org,
      subject: ["digital_employee", "workforce_agent"],
      summary:
        "Retired DE or AG removed for good (PURGE-RETIRED-AGENTS): its record, profile, seat history, keys and endpoints deleted; its work and history kept under 'Removed agent (name)'; ClawPanel drops its Hermes profile, kanban and bridge entry; Recall archives (or deletes) its memory",
      consumers: ["audit", "recall.purge", "webhooks (ClawPanel)"],
    },
    AgentPurged,
  ),
  defineEvent(
    "digital_employee.job_changed",
    {
      ...org,
      subject: "digital_employee",
      summary:
        "DE-WIZARD: the DE's job set-up changed (path, submitted, home channel, the KPIs it reports itself)",
      consumers: ["audit"],
    },
    z.strictObject({
      changed: z.array(z.enum(["path", "submitted", "home_channel", "reporting"])).min(1),
      path: DeployPath,
      submitted: z.boolean(),
      version: z.int().min(1),
    }),
  ),
  defineEvent(
    "digital_employee.litellm_key_created",
    {
      ...org,
      subject: "digital_employee",
      summary: "The DE's own LiteLLM virtual key and sealed read credential stored (never the key)",
      consumers: ["audit"],
    },
    z.strictObject({
      key_alias: z.string(),
      max_budget_minor: MinorAmount,
      currency: Currency,
      models: z.array(z.string()),
    }),
  ),
  defineEvent(
    "digital_employee.provisioning_failed",
    {
      ...org,
      subject: "digital_employee",
      summary: "Provisioning step failed; the DE stays onboarding with the error",
      consumers: ["audit"],
    },
    z.strictObject({ step: z.enum(["litellm_key"]), error: z.string() }),
  ),
  defineEvent(
    "digital_employee.litellm_key_delivered",
    {
      ...org,
      subject: "digital_employee",
      summary: "The issued LiteLLM key was acknowledged by its recipient; the sealed copy is wiped",
      consumers: ["audit"],
    },
    z.strictObject({ attempt: z.int().min(1), key_alias: z.string() }),
  ),
  defineEvent(
    "agent_key.created",
    {
      domain: "access",
      feature: "core",
      subject: "agent_key",
      summary: "Agent key issued (plaintext returned once, never stored)",
      consumers: ["audit"],
      version: 2,
      previous: [{ version: 1, payload: AgentKeyCreatedV1, upgrade: (p) => p }],
    },
    z.strictObject({
      kind: AgentKeyKind,
      membership_id: Uuid,
      /** v2 (M3-01): null for a `service` key. */
      seat_id: Uuid.nullable(),
      key_prefix: z.string().length(12),
      scopes: z.array(AgentKeyScope),
      /** Ingest rule 7: may post items older than 90 days. */
      backfill: z.boolean(),
      meter_namespaces: z.array(z.string()),
      expires_at: Timestamp.nullable(),
    }),
  ),
  defineEvent(
    "agent_key.revoked",
    {
      domain: "access",
      feature: "core",
      subject: "agent_key",
      summary: "Agent key revoked",
      consumers: ["audit"],
      version: 2,
      previous: [{ version: 1, payload: AgentKeyRevokedV1, upgrade: (p) => p }],
    },
    z.strictObject({
      membership_id: Uuid,
      /** v2 (M3-01): null for a `service` key. */
      seat_id: Uuid.nullable(),
      reason: z.enum(["manual", "seat_closed", "membership_deactivated"]),
    }),
  ),
  defineEvent(
    "external_identity.created",
    {
      ...org,
      subject: "external_identity",
      summary: "External id mapped (CSV import)",
      consumers: ["audit"],
    },
    z.strictObject({
      system: z.string(),
      kind: ExternalIdentityKind,
      external_id: z.string(),
      membership_id: Uuid.nullable(),
      seat_id: Uuid.nullable(),
      client_ref: z.string().nullable(),
      valid_from: IsoDate,
    }),
  ),
  defineEvent(
    "external_identity.closed",
    {
      ...org,
      subject: "external_identity",
      summary: "Live mapping closed (valid_to set)",
      consumers: ["audit"],
    },
    z.strictObject({ valid_to: IsoDate, replaced_by_id: Uuid.nullable() }),
  ),

  // WORKFORCE (2026-10-26): agents (AG), registered by ops, changed by the people above them.
  defineEvent(
    "workforce_agent.registered",
    {
      ...org,
      subject: "workforce_agent",
      summary: "Agent (AG) registered: a task executor with no seat, reporting to a DE or a person",
      consumers: ["audit", "recall.sync", "deploys.recall_keys"],
    },
    z.strictObject({
      slug: z.string(),
      name: z.string(),
      team_id: Uuid.nullable(),
      runtime: WorkerRuntime.nullable(),
      reports_to_membership_id: Uuid,
      reports_to_kind: WorkforceAgentReportsToKind,
      status: WorkforceAgentStatus,
      /** WF-POLISH: what it does, when it was given one (the create dialog, the profile sync). */
      role: z.string().nullable().optional(),
    }),
  ),
  defineEvent(
    "workforce_agent.updated",
    {
      ...org,
      subject: "workforce_agent",
      summary:
        "Agent (AG) renamed, moved, paused/resumed/retired, re-pointed or its activity noted",
      consumers: ["audit", "recall.sync", "deploys.recall_keys"],
    },
    z.strictObject({
      changes: changesOf({
        name: z.string(),
        team_id: Uuid,
        runtime: WorkerRuntime,
        reports_to_membership_id: Uuid,
        status: WorkforceAgentStatus,
        last_activity_at: Timestamp,
        last_activity: z.string(),
        role: z.string(),
        goal: z.string(),
        backstory: z.string(),
        responsibilities: z.array(z.string()),
        standing_orders: z.string(),
        skills: z.array(z.string()),
      }),
    }),
  ),
  // HELPERS-2: a DE started or stopped using a helper it does not lead (its home is unchanged).
  defineEvent(
    "workforce_agent.use_changed",
    {
      ...org,
      subject: "workforce_agent",
      summary: "A DE started or stopped using an agent (AG) it does not lead",
      consumers: ["audit"],
    },
    z.strictObject({
      de_membership_id: Uuid,
      used: z.boolean(),
      source: AgentUseSource,
    }),
  ),
  // Teams, org chart, responsibilities, recurring work, templates, webhooks (M1-02)
  defineEvent(
    "team.created",
    {
      ...org,
      subject: "team",
      summary: "Team created",
      consumers: ["channel auto-create", "recall", "permission cache", "audit"],
    },
    z.strictObject({
      name: z.string(),
      parent_team_id: Uuid.nullable(),
      lead_seat_id: Uuid.nullable(),
      composition: TeamComposition,
      human_owner_seat_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "team.updated",
    {
      ...org,
      subject: "team",
      summary: "Team renamed, re-parented, lead/owner or derived composition changed",
      consumers: ["recall", "permission cache", "audit"],
    },
    z.strictObject({
      changes: changesOf({
        name: z.string(),
        parent_team_id: Uuid,
        lead_seat_id: Uuid,
        composition: TeamComposition,
        human_owner_seat_id: Uuid,
      }),
    }),
  ),
  defineEvent(
    "team.deleted",
    {
      ...org,
      subject: "team",
      summary: "Team deleted (its member rows go with it; seats stay)",
      consumers: ["recall", "permission cache", "audit"],
    },
    z.strictObject({ name: z.string(), removed_seat_ids: z.array(Uuid) }),
  ),
  defineEvent(
    "team.member_changed",
    {
      ...org,
      subject: "team",
      summary: "A seat joined or left the team",
      consumers: ["channel membership", "recall", "permission cache", "audit"],
    },
    z.strictObject({ seat_id: Uuid, op: z.enum(["added", "removed"]) }),
  ),
  defineEvent(
    "seat_edge.changed",
    {
      ...org,
      subject: "seat_edge",
      summary: "Org chart edge added, re-policied or removed (03 `seat.edge_changed`)",
      consumers: ["permission cache", "audit"],
    },
    z.strictObject({
      from_seat_id: Uuid,
      to_seat_id: Uuid,
      kind: SeatEdgeKind,
      policy: SeatEdgePolicy,
      op: z.enum(["added", "updated", "removed"]),
    }),
  ),
  // STRUCTURE-CHANGED (docs/design/2026-10-30-shared-des-and-fulfillment.md §3.2): a reporting
  // line moved (a seat's parent, an AG's reports-to, a seat's serves). Written once per source
  // event by the `org.structure` consumer: the affected agents were told, the stored brief
  // recipients and queued DE starts re-pointed, the old leader's open items listed for re-routing.
  defineEvent(
    "structure.changed",
    {
      ...org,
      subject: "structure_change",
      summary: "A reporting line moved; its agents were told and its open items listed",
      consumers: ["audit", "inbox (the old and the new leader)"],
    },
    z.strictObject({
      kind: StructureChangeKind,
      subject_kind: StructureSubjectKind,
      subject_id: Uuid,
      from_id: Uuid.nullable(),
      to_id: Uuid.nullable(),
      old_leader_membership_id: Uuid.nullable(),
      new_leader_membership_id: Uuid.nullable(),
      /** The write's own event (`seat.updated`, `workforce_agent.updated`, a serves change). */
      source_event_id: Uuid,
      agents_told: z.int().min(0),
      reroute_items: z.int().min(0),
    }),
  ),
  // STRUCTURE-CHANGED: one listed item was taken over by the new leader or left with the old one.
  defineEvent(
    "structure.rerouted",
    {
      ...org,
      subject: "structure_change",
      summary: "An item listed after a structure change was taken over or left as it was",
      consumers: ["audit"],
    },
    z.strictObject({
      item_kind: z.enum(["task", "approval", "status_ask"]),
      item_id: Uuid,
      action: z.enum(["take_over", "leave"]),
      /** Who now holds it (the new leader on a take-over; the old one when left). */
      to_membership_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "responsibility.created",
    {
      ...org,
      subject: "responsibility",
      summary: "Responsibility added to a seat",
      consumers: ["proj.kpi", "audit"],
    },
    z.strictObject({
      seat_id: Uuid,
      name: z.string(),
      weight: Decimal,
      kpi_metric_ids: z.array(Uuid),
    }),
  ),
  defineEvent(
    "responsibility.updated",
    {
      ...org,
      subject: "responsibility",
      summary: "Responsibility changed",
      consumers: ["proj.kpi", "audit"],
    },
    z.strictObject({
      seat_id: Uuid,
      changes: changesOf({
        name: z.string(),
        description: z.string(),
        weight: Decimal,
        kpi_metric_ids: z.array(Uuid),
        status: ResponsibilityStatus,
      }),
    }),
  ),
  defineEvent(
    "responsibility.removed",
    {
      ...org,
      subject: "responsibility",
      summary: "Responsibility removed with its recurring work",
      consumers: ["proj.kpi", "audit"],
    },
    z.strictObject({ seat_id: Uuid, name: z.string(), removed_recurring_work_ids: z.array(Uuid) }),
  ),
  // CHART-KB (03 §Knowledge, memory, goals: `goal.*`; 02 §goal). A quarterly priority is a goal
  // of kind `priority`; the inbox, kpi and recall consumers the spec names land with M5.
  defineEvent(
    "goal.created",
    {
      ...org,
      subject: "goal",
      summary: "Goal added to a seat (a quarterly priority by default)",
      consumers: ["inbox", "proj.kpi", "recall", "audit"],
    },
    z.strictObject({
      seat_id: Uuid,
      kind: z.enum(["goal", "priority", "annual"]),
      title: z.string(),
      period_key: z.string().nullable(),
      track: GoalTrack,
      milestones: z.int().min(0),
    }),
  ),
  defineEvent(
    "goal.updated",
    {
      ...org,
      subject: "goal",
      summary: "Goal changed (title, period, due date, state, track, target, milestones)",
      consumers: ["inbox", "proj.kpi", "recall", "audit"],
    },
    z.strictObject({
      seat_id: Uuid,
      changes: changesOf({
        title: z.string(),
        period_key: z.string(),
        due_at: Timestamp,
        state: GoalState,
        track: GoalTrack,
        metric_def_id: Uuid,
        target: Decimal,
        /** Wave 9: "done/total" before and after, when the milestones moved. */
        milestones: z.string().max(32),
      }),
      /** The milestones after the change: how many, how many done. */
      milestones: z.object({ total: z.int().min(0), done: z.int().min(0) }),
    }),
  ),
  defineEvent(
    "goal.track_changed",
    {
      ...org,
      subject: "goal",
      summary: "A goal's track moved (on / off / at risk)",
      consumers: ["inbox", "proj.kpi", "recall", "audit"],
    },
    z.strictObject({
      seat_id: Uuid,
      from: GoalTrack,
      to: GoalTrack,
      reason: z.string().nullable(),
    }),
  ),
  defineEvent(
    "goal.milestone_done",
    {
      ...org,
      subject: "goal",
      summary: "A goal's milestone was marked done",
      consumers: ["proj.kpi", "audit"],
    },
    z.strictObject({ seat_id: Uuid, milestone_id: Uuid, title: z.string() }),
  ),
  defineEvent(
    "goal.deleted",
    {
      ...org,
      subject: "goal",
      summary: "Goal removed with its milestones",
      consumers: ["proj.kpi", "audit"],
    },
    z.strictObject({ seat_id: Uuid, title: z.string(), removed_milestone_ids: z.array(Uuid) }),
  ),
  // KPI-VISIBILITY (05 §KPI visibility, founder-approved 2026-10-22): a grant that widens whose
  // KPI data a principal reads was added, changed or removed (Setup › KPI visibility).
  defineEvent(
    "kpi_grant.changed",
    {
      ...org,
      subject: "company",
      summary: "A KPI visibility grant was added, changed or removed",
      consumers: ["audit"],
    },
    z.strictObject({
      action: z.enum(["created", "updated", "deleted"]),
      grant_id: Uuid,
      principal_kind: KpiPrincipalKind,
      principal_id: Uuid,
      scopes: z.array(KpiScope),
      groups: z.array(KpiGroup),
    }),
  ),
  // MEETINGS (03 §Operating cadence: `issue.*`, `meeting.*`, `vision_page.updated`; 02
  // §Operating cadence). Additions to 03's list, each a write the spec's screens make:
  // `issue.deleted` (the board's delete), `meeting_series.changed` (a series added, edited or
  // archived), `meeting.updated` (notes, pre-read, the live item), `meeting.agenda_changed` (a
  // custom line added, edited or removed) and `meeting.preread_requested` (the wake-up the day
  // before, or `pending` when no agent of the team is live).
  defineEvent(
    "issue.raised",
    {
      ...cadence,
      subject: "issue",
      summary: "Issue raised on a team's list (or the company's)",
      consumers: ["inbox (owner)", "meeting agenda builder", "kpi (issues_open)", "digest"],
    },
    z.strictObject({
      team_id: Uuid.nullable(),
      owner_seat_id: Uuid.nullable(),
      priority: z.int().min(1).max(3),
      status: IssueStatus,
      title: z.string(),
      source_kind: IssueSourceKind,
      source_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "issue.updated",
    {
      ...cadence,
      subject: "issue",
      summary: "Issue changed (title, details, owner, priority, team, order, archived)",
      consumers: ["meeting agenda builder", "audit"],
    },
    z.strictObject({
      team_id: Uuid.nullable(),
      changes: changesOf({
        title: z.string(),
        body: z.string(),
        owner_seat_id: Uuid,
        priority: z.int(),
        team_id: Uuid,
        position: z.int(),
        archived_at: Timestamp,
      }),
    }),
  ),
  defineEvent(
    "issue.status_changed",
    {
      ...cadence,
      subject: "issue",
      summary: "Issue moved between identify, discuss and solve (or reopened)",
      consumers: ["inbox (owner)", "meeting agenda builder", "kpi (issue_age)"],
    },
    z.strictObject({
      team_id: Uuid.nullable(),
      owner_seat_id: Uuid.nullable(),
      from: IssueStatus,
      to: IssueStatus,
    }),
  ),
  defineEvent(
    "issue.resolved",
    {
      ...cadence,
      subject: "issue",
      summary: "Issue solved: its resolution made tasks, a decision, or nothing",
      consumers: ["inbox (owner)", "kpi (issues_resolved)", "recall (resolution as lesson)"],
    },
    z.strictObject({
      team_id: Uuid.nullable(),
      owner_seat_id: Uuid.nullable(),
      priority: z.int().min(1).max(3),
      resolution_kind: IssueResolutionKind,
      resolution_refs: z.array(z.strictObject({ kind: z.enum(["task", "decision"]), id: Uuid })),
    }),
  ),
  defineEvent(
    "issue.dropped",
    {
      ...cadence,
      subject: "issue",
      summary: "Issue dropped without a resolution",
      consumers: ["kpi (issues_open)", "audit"],
    },
    z.strictObject({ team_id: Uuid.nullable(), owner_seat_id: Uuid.nullable(), from: IssueStatus }),
  ),
  defineEvent(
    "issue.escalated",
    {
      ...cadence,
      subject: "issue",
      summary: "Issue escalated up the chart (team → company)",
      consumers: ["inbox (target owner)", "portfolio projection (escalation, to = portfolio)"],
    },
    z.strictObject({
      team_id: Uuid.nullable(),
      from: IssueEscalation,
      to: IssueEscalation,
      parent_issue_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "issue.deescalated",
    {
      ...cadence,
      subject: "issue",
      summary: "Issue handed back down to its team",
      consumers: ["inbox (team owner)", "portfolio projection (escalation)"],
    },
    z.strictObject({
      team_id: Uuid.nullable(),
      from: IssueEscalation,
      to: IssueEscalation,
      parent_issue_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "issue.deleted",
    {
      ...cadence,
      subject: "issue",
      summary: "Issue deleted from the list (archiving is the default)",
      consumers: ["audit"],
    },
    z.strictObject({ team_id: Uuid.nullable(), title: z.string(), status: IssueStatus }),
  ),
  // M8-ASSESS (02 §Assessment; x-ray.md §11.1 `event` types; founder-approved 2026-10-22 (fix
  // everything)): rounds, respondents (members and guests), answers, reports, proposals. A guest's
  // writes carry the `system` actor (no membership); the payload names the participant. No
  // payload carries an email, a token or a free-text answer.
  defineEvent(
    "assessment.round_created",
    {
      ...assess,
      subject: "assessment_round",
      summary: "An assessment round was created (a draft, or opened at once)",
      consumers: ["audit"],
    },
    z.strictObject({
      number: z.int().min(1),
      name: z.string(),
      questionnaire: AssessmentQuestionnaire,
      participants: z.int().min(0),
    }),
  ),
  defineEvent(
    "assessment.round_updated",
    {
      ...assess,
      subject: "assessment_round",
      summary: "A round was renamed or its links' lifetime changed",
      consumers: ["audit"],
    },
    z.strictObject({
      changes: changesOf({ name: z.string(), invite_expiry_days: z.int() }),
    }),
  ),
  defineEvent(
    "assessment.round_deleted",
    {
      ...assess,
      subject: "assessment_round",
      summary: "A draft round was deleted",
      consumers: ["audit"],
    },
    z.strictObject({ number: z.int().min(1), name: z.string() }),
  ),
  defineEvent(
    "assessment.round_opened",
    {
      ...assess,
      subject: "assessment_round",
      summary: "A round opened: every respondent's magic link was minted",
      consumers: ["inbox (respondent members)", "audit"],
    },
    z.strictObject({
      number: z.int().min(1),
      invited: z.int().min(0),
      expires_at: Timestamp,
    }),
  ),
  defineEvent(
    "assessment.round_closed",
    {
      ...assess,
      subject: "assessment_round",
      summary: "A round closed; pending respondents expired",
      consumers: ["audit"],
    },
    z.strictObject({
      number: z.int().min(1),
      invited: z.int().min(0),
      completed: z.int().min(0),
      expired: z.int().min(0),
    }),
  ),
  defineEvent(
    "assessment.report_generated",
    {
      ...assess,
      subject: "assessment_round",
      summary: "A report revision of the round was computed, with its org-chart proposals",
      consumers: ["inbox (owner)", "audit"],
    },
    z.strictObject({
      report_id: Uuid,
      version: z.int().min(1),
      scoring_version: z.string(),
      proposals: z.int().min(0),
    }),
  ),
  defineEvent(
    "assessment.participant_invited",
    {
      ...assess,
      subject: "assessment_participant",
      summary: "Someone was asked to answer a round (a member, or a guest by email)",
      consumers: ["audit"],
    },
    z.strictObject({
      round_id: Uuid,
      kind: AssessmentParticipantKind,
      membership_id: Uuid.nullable(),
      seat_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "assessment.participant_removed",
    {
      ...assess,
      subject: "assessment_participant",
      summary: "A respondent who had not answered was removed (their link stops working)",
      consumers: ["audit"],
    },
    z.strictObject({ round_id: Uuid, kind: AssessmentParticipantKind }),
  ),
  defineEvent(
    "assessment.participant_reminded",
    {
      ...assess,
      subject: "assessment_participant",
      summary: "A respondent was reminded with a fresh link (the previous one stops working)",
      consumers: ["audit"],
    },
    z.strictObject({
      round_id: Uuid,
      reminder_count: z.int().min(1),
      expires_at: Timestamp,
    }),
  ),
  defineEvent(
    "assessment.link_issued",
    {
      ...assess,
      subject: "assessment_participant",
      summary: "A member took a fresh link to their own questionnaire",
      consumers: ["audit"],
    },
    z.strictObject({ round_id: Uuid, expires_at: Timestamp }),
  ),
  defineEvent(
    "assessment.response_saved",
    {
      ...assess,
      subject: "assessment_participant",
      summary: "A respondent saved draft answers",
      consumers: ["round progress"],
    },
    z.strictObject({
      round_id: Uuid,
      answered_share: z.number().min(0).max(1),
    }),
  ),
  defineEvent(
    "assessment.response_submitted",
    {
      ...assess,
      subject: "assessment_participant",
      summary: "A respondent submitted their answers (frozen from now on)",
      consumers: ["round progress", "audit"],
    },
    z.strictObject({ round_id: Uuid, kind: AssessmentParticipantKind }),
  ),
  defineEvent(
    "assessment.proposal_decided",
    {
      ...assess,
      subject: "assessment_proposal",
      summary: "The owner accepted or rejected an org-chart proposal (or set it back)",
      consumers: ["audit"],
    },
    z.strictObject({
      round_id: Uuid,
      key: z.string(),
      kind: AssessmentProposalKind,
      state: AssessmentProposalState,
    }),
  ),
  defineEvent(
    "assessment.proposal_applied",
    {
      ...assess,
      subject: "assessment_proposal",
      summary: "An accepted proposal was applied to the chart (what it created)",
      consumers: ["audit"],
    },
    z.strictObject({
      round_id: Uuid,
      key: z.string(),
      kind: AssessmentProposalKind,
      applied_ref: z.strictObject({
        seat_id: Uuid.optional(),
        role_id: Uuid.optional(),
        responsibility_id: Uuid.optional(),
        metric_def_id: Uuid.optional(),
      }),
    }),
  ),
  defineEvent(
    "meeting_series.changed",
    {
      ...cadence,
      subject: "meeting_series",
      summary: "A recurring team meeting was added, edited or archived",
      consumers: ["meeting scheduler", "audit"],
    },
    z.strictObject({
      op: z.enum(["created", "updated", "archived", "unarchived"]),
      team_id: Uuid.nullable(),
      name: z.string(),
      schedule: z.string(),
      attendees: z.int().min(0),
      fields: z.array(z.string()),
    }),
  ),
  defineEvent(
    "meeting.scheduled",
    {
      ...cadence,
      subject: "meeting",
      summary: "A meeting of a series was scheduled",
      consumers: ["inbox (attendees)", "wake-up (pre-read agent, the day before)"],
    },
    z.strictObject({ series_id: Uuid, scheduled_at: Timestamp }),
  ),
  defineEvent(
    "meeting.agenda_built",
    {
      ...cadence,
      subject: "meeting",
      summary:
        "A meeting's agenda was built from the live data (misses, off-track, issues, to-dos)",
      consumers: ["inbox (attendees)", "wake-up (pre-read agent)"],
    },
    z.strictObject({
      series_id: Uuid,
      items: z.partialRecord(AgendaSection, z.int().min(0)),
      rebuilt: z.boolean(),
    }),
  ),
  defineEvent(
    "meeting.agenda_changed",
    {
      ...cadence,
      subject: "meeting",
      summary: "An agenda line was added, edited or removed by hand",
      consumers: ["audit"],
    },
    z.strictObject({
      series_id: Uuid,
      item_id: Uuid,
      op: z.enum(["added", "updated", "removed"]),
      section: AgendaSection,
    }),
  ),
  defineEvent(
    "meeting.started",
    {
      ...cadence,
      subject: "meeting",
      summary: "A meeting started",
      consumers: ["inbox (attendees)", "kpi (on_time)"],
    },
    z.strictObject({ series_id: Uuid, scheduled_at: Timestamp, started_at: Timestamp }),
  ),
  defineEvent(
    "meeting.updated",
    {
      ...cadence,
      subject: "meeting",
      summary: "A meeting's notes, pre-read or live item changed",
      consumers: ["recall (notes)", "audit"],
    },
    z.strictObject({
      series_id: Uuid,
      fields: z.array(z.enum(["notes", "preread_note", "current_item_id"])).min(1),
    }),
  ),
  defineEvent(
    "meeting.outcome_recorded",
    {
      ...cadence,
      subject: "meeting",
      summary:
        "An outcome captured in a meeting (a to-do, a decision, an issue resolved or raised)",
      consumers: ["task/decision creation events follow", "audit"],
    },
    z.strictObject({
      series_id: Uuid,
      item_id: Uuid.nullable(),
      outcome_kind: MeetingOutcomeKind,
      outcome_ref: Uuid,
    }),
  ),
  defineEvent(
    "meeting.rated",
    {
      ...cadence,
      subject: "meeting",
      summary: "A meeting was rated (1–10)",
      consumers: ["kpi (meeting rating)"],
    },
    z.strictObject({ series_id: Uuid, rating: z.int().min(1).max(10) }),
  ),
  defineEvent(
    "meeting.ended",
    {
      ...cadence,
      subject: "meeting",
      summary: "A meeting ended; its summary posted to the team channel's topic",
      consumers: [
        "kpi (meeting_held)",
        "recall (notes)",
        "portfolio projection (meetings)",
        "inbox (attendees)",
      ],
    },
    z.strictObject({
      series_id: Uuid,
      duration_s: z.int().min(0),
      items: z.int().min(0),
      items_done: z.int().min(0),
      outcomes: z.int().min(0),
      rating: z.int().min(1).max(10).nullable(),
      summary_message_id: Uuid.nullable(),
    }),
  ),
  // ACTIONS: a meeting not started yet is cancelled (state `cancelled`; its agenda kept).
  defineEvent(
    "meeting.cancelled",
    {
      ...cadence,
      subject: "meeting",
      summary: "A scheduled meeting was cancelled before it started",
      consumers: ["rt", "audit"],
    },
    z.strictObject({ series_id: Uuid, scheduled_at: Timestamp }),
  ),
  defineEvent(
    "meeting.preread_requested",
    {
      ...cadence,
      subject: "meeting",
      summary: "The pre-read was asked of the team's agent (pending when none is live)",
      consumers: ["wake-up (pre-read agent)", "audit"],
    },
    z.strictObject({
      series_id: Uuid,
      state: z.enum(["requested", "pending"]),
      agent_membership_id: Uuid.nullable(),
      wakeup_id: Uuid.nullable(),
      reason: z.string().nullable(),
    }),
  ),
  defineEvent(
    "vision_page.updated",
    {
      ...cadence,
      subject: "page",
      summary: "The company's vision page changed (core values, focus, targets, plans)",
      consumers: ["recall", "priorities (link check)", "portfolio projection summary"],
    },
    z.strictObject({ sections: z.array(z.string()).min(1), version: z.int().min(1) }),
  ),
  // CALENDAR-C1 (docs/design/2026-10-28-calendar.md): calendars, their shares and follows, events
  // with recurrence scopes, responses and the ICS feed. A projected meeting event is the meeting's
  // view (its `meeting.*` events are the trail), so it writes no `calendar_event.*` of its own.
  defineEvent(
    "calendar.updated",
    {
      ...calendarDomain,
      subject: "calendar",
      summary: "A calendar's name, colour, default visibility or time zone changed",
      consumers: ["calendar views (refresh)", "audit"],
    },
    z.strictObject({ kind: CalendarKind, fields: z.array(z.string()).min(1) }),
  ),
  // CAL-WORK-LAYERS (design 2026-10-30-calendars-and-meetings §C.1): a board's calendar view
  // published into the Calendar as a work calendar, and taken out again (the row archived).
  defineEvent(
    "calendar.published",
    {
      ...calendarDomain,
      subject: "calendar",
      summary: "A board's calendar view was published into the Calendar as a work calendar",
      consumers: ["calendar views (refresh)", "audit"],
    },
    z.strictObject({ view_id: Uuid, board_id: Uuid }),
  ),
  defineEvent(
    "calendar.unpublished",
    {
      ...calendarDomain,
      subject: "calendar",
      summary: "A work calendar was taken out of the Calendar (its board view stays)",
      consumers: ["calendar views (refresh)", "audit"],
    },
    z.strictObject({ view_id: Uuid }),
  ),
  defineEvent(
    "calendar.shared",
    {
      ...calendarDomain,
      subject: "calendar",
      summary: "A calendar was shared with a member, a team or the company (or a share changed)",
      consumers: ["calendar views (refresh)", "audit"],
    },
    z.strictObject({
      share_id: Uuid,
      grantee_kind: CalendarShareGrantee,
      grantee_id: Uuid,
      level: CalendarShareLevel,
    }),
  ),
  defineEvent(
    "calendar.share_revoked",
    {
      ...calendarDomain,
      subject: "calendar",
      summary: "A calendar share was revoked (details hidden again at the next read)",
      consumers: ["calendar views (refresh)", "audit"],
    },
    z.strictObject({ share_id: Uuid, grantee_kind: CalendarShareGrantee, grantee_id: Uuid }),
  ),
  defineEvent(
    "calendar.follow_changed",
    {
      ...calendarDomain,
      subject: "calendar",
      summary: "A person followed, hid, recoloured or unfollowed a calendar in their overlay",
      consumers: ["calendar views (the person's)"],
    },
    z.strictObject({
      membership_id: Uuid,
      op: z.enum(["followed", "updated", "unfollowed"]),
      shown: z.boolean(),
    }),
  ),
  defineEvent(
    "calendar_event.created",
    {
      ...calendarDomain,
      subject: "calendar_event",
      summary:
        "An event was added to a calendar (or a series was split by a 'this and following' edit)",
      consumers: ["calendar views (refresh)", "inbox (attendees)", "audit"],
    },
    z.strictObject({
      calendar_id: Uuid,
      source: CalendarEventSource,
      starts_at: Timestamp,
      recurring: z.boolean(),
      attendees: z.int().min(0),
      /** The series a "this and following" edit split it from. */
      split_from: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "calendar_event.updated",
    {
      ...calendarDomain,
      subject: "calendar_event",
      summary: "An event was edited: one occurrence, this and following, or the whole series",
      consumers: ["calendar views (refresh)", "inbox (attendees)", "audit"],
    },
    z.strictObject({
      calendar_id: Uuid,
      scope: z.enum(["single", "this", "following", "all"]),
      occurrence_start: Timestamp.nullable(),
      fields: z.array(z.string()).min(1),
      version: z.int().min(1),
    }),
  ),
  defineEvent(
    "calendar_event.cancelled",
    {
      ...calendarDomain,
      subject: "calendar_event",
      summary:
        "An event, one occurrence, or this and following were cancelled (kept as a tombstone)",
      consumers: ["calendar views (refresh)", "inbox (attendees)", "audit"],
    },
    z.strictObject({
      calendar_id: Uuid,
      scope: z.enum(["single", "this", "following", "all"]),
      occurrence_start: Timestamp.nullable(),
    }),
  ),
  defineEvent(
    "calendar_event.responded",
    {
      ...calendarDomain,
      subject: "calendar_event",
      summary: "An attendee accepted, declined or answered maybe",
      consumers: ["calendar views (refresh)", "inbox (organizer)"],
    },
    z.strictObject({
      calendar_id: Uuid,
      membership_id: Uuid,
      response: CalendarAttendeeResponse,
      occurrence_start: Timestamp.nullable(),
    }),
  ),
  defineEvent(
    "calendar_feed.created",
    {
      ...calendarDomain,
      subject: "calendar_feed",
      summary: "A person made a new ICS feed link (any earlier link stops working)",
      consumers: ["audit"],
    },
    z.strictObject({ membership_id: Uuid, replaced: z.boolean() }),
  ),
  defineEvent(
    "calendar_feed.revoked",
    {
      ...calendarDomain,
      subject: "calendar_feed",
      summary: "A person revoked their ICS feed link",
      consumers: ["audit"],
    },
    z.strictObject({ membership_id: Uuid }),
  ),
  defineEvent(
    "recurring_work.created",
    {
      ...org,
      subject: "recurring_work",
      summary: "Schedule added to a responsibility",
      consumers: ["scheduler", "audit"],
    },
    z.strictObject({
      responsibility_id: Uuid,
      schedule: z.string(),
      board_id: Uuid.nullable(),
      next_run_at: Timestamp.nullable(),
    }),
  ),
  defineEvent(
    "recurring_work.updated",
    {
      ...org,
      subject: "recurring_work",
      summary: "Schedule, board, task template or status (active/paused) changed",
      consumers: ["scheduler", "audit"],
    },
    z.strictObject({
      responsibility_id: Uuid,
      changes: changesOf({
        schedule: z.string(),
        board_id: Uuid,
        task_template: TaskTemplate,
        status: RecurringWorkStatus,
        next_run_at: Timestamp,
      }),
    }),
  ),
  defineEvent(
    "recurring_work.deleted",
    {
      ...org,
      subject: "recurring_work",
      summary: "Schedule removed",
      consumers: ["scheduler", "audit"],
    },
    z.strictObject({ responsibility_id: Uuid }),
  ),
  defineEvent(
    "recurring_work.materialised",
    {
      ...org,
      subject: "recurring_work",
      summary: "A scheduled occurrence came due (a task once boards exist, M2-03)",
      consumers: ["proj.kpi (expected)", "inbox"],
    },
    z.strictObject({
      responsibility_id: Uuid,
      /** Null until the boards module materialises tasks (M1-05 before M2-03). */
      task_id: Uuid.nullable(),
      scheduled_for: Timestamp,
      period_key: PeriodKey,
      next_run_at: Timestamp.nullable(),
      trigger: z.enum(["schedule", "manual"]),
    }),
  ),
  defineEvent(
    "template.created",
    { ...org, subject: "template", summary: "Template created", consumers: ["audit"] },
    z.strictObject({ name: z.string(), version: z.int().min(1) }),
  ),
  defineEvent(
    "template.updated",
    {
      ...org,
      subject: "template",
      summary: "Template renamed or its spec replaced (version bumped)",
      consumers: ["audit"],
    },
    z.strictObject({
      changes: changesOf({ name: z.string(), description: z.string(), version: z.int() }),
    }),
  ),
  defineEvent(
    "template.deleted",
    { ...org, subject: "template", summary: "Template deleted", consumers: ["audit"] },
    z.strictObject({ name: z.string() }),
  ),
  defineEvent(
    "template.applied",
    {
      ...org,
      subject: "template",
      summary: "Template applied in one transaction; lists what it created",
      consumers: ["audit"],
    },
    z.strictObject({
      version: z.int().min(1),
      params: z.record(z.string(), z.string()),
      created: TemplateCreated,
    }),
  ),
  defineEvent(
    "webhook.created",
    {
      ...org,
      subject: "webhook",
      summary: "Outbound webhook registered (secret shown once)",
      consumers: ["webhook delivery", "audit"],
    },
    z.strictObject({ url: z.string(), types: z.array(z.string()), filter: WebhookFilter }),
  ),
  defineEvent(
    "webhook.updated",
    {
      ...org,
      subject: "webhook",
      summary: "Webhook URL, types, filter or status changed",
      consumers: ["webhook delivery", "audit"],
    },
    z.strictObject({
      changes: changesOf({
        url: z.string(),
        description: z.string(),
        types: z.array(z.string()),
        filter: WebhookFilter,
        status: WebhookStatus,
      }),
    }),
  ),
  defineEvent(
    "webhook.deleted",
    {
      ...org,
      subject: "webhook",
      summary: "Webhook removed with its deliveries",
      consumers: ["webhook delivery", "audit"],
    },
    z.strictObject({ url: z.string() }),
  ),
  // WOS-BUNDLE: Work OS auto-pause (`WEBHOOK_PAUSE_THRESHOLD = 5`, `domain/events.ts:212`).
  defineEvent(
    "webhook.paused",
    {
      ...org,
      subject: "webhook",
      summary:
        "Five events in a row exhausted their delivery retries; the hook is paused until an admin resumes it",
      consumers: ["inbox", "audit"],
    },
    z.strictObject({ consecutive_failures: z.int().min(1), last_delivery_id: Uuid }),
  ),
  defineEvent(
    "webhook.resumed",
    {
      ...org,
      subject: "webhook",
      summary: "An admin resumed a paused (or disabled) hook; its failure count starts again at 0",
      consumers: ["webhook delivery", "audit"],
    },
    z.strictObject({ from_status: WebhookStatus }),
  ),
  defineEvent(
    "webhook.tested",
    { ...org, subject: "webhook", summary: "A signed test ping was sent", consumers: ["audit"] },
    z.strictObject({
      delivery_id: Uuid,
      response_status: z.int().nullable(),
      ok: z.boolean(),
    }),
  ),
  defineEvent(
    "webhook.secret_rotated",
    {
      ...org,
      subject: "webhook",
      summary: "The signing secret was replaced (the new one is shown once, never in the event)",
      consumers: ["audit"],
    },
    z.strictObject({}),
  ),
  defineEvent(
    "webhook.delivery_retried",
    {
      ...org,
      subject: "webhook",
      summary: "A failed or abandoned delivery was sent again by hand",
      consumers: ["audit"],
    },
    z.strictObject({
      delivery_id: Uuid,
      response_status: z.int().nullable(),
      ok: z.boolean(),
    }),
  ),
  defineEvent(
    "webhook.disabled",
    {
      ...org,
      subject: "webhook",
      summary: "Deliveries failed for 24 h; the hook is disabled and admins get an inbox item",
      consumers: ["inbox", "audit"],
    },
    z.strictObject({ reason: z.string(), failing_since: Timestamp, last_delivery_id: Uuid }),
  ),

  // WEBHOOKS-IN (design 2026-10-27 §2, §5.4): incoming webhooks. A hook is configuration (a
  // manager's); `received` is the one event an outside sender causes, and it never carries the
  // body (events are kept forever and leave through outgoing hooks): the body stays on the
  // delivery row, purged after the hook's retention. Rejected requests are logged on the
  // delivery row only (no event: a stranger's bad signatures must not grow the event log).
  defineEvent(
    "inbound_webhook.created",
    {
      ...org,
      subject: "inbound_webhook",
      summary: "Incoming webhook created (its secret shown once)",
      consumers: ["audit"],
    },
    z.strictObject({ name: z.string(), verification: InboundWebhookVerification }),
  ),
  defineEvent(
    "inbound_webhook.updated",
    {
      ...org,
      subject: "inbound_webhook",
      summary: "Incoming webhook settings changed",
      consumers: ["audit"],
    },
    z.strictObject({
      changes: changesOf({
        name: z.string(),
        description: z.string(),
        target_membership_id: Uuid,
        for_agent_id: Uuid,
        board_id: Uuid,
        allowed_cidrs: z.array(z.string()),
        max_bytes: z.int(),
        rate_per_minute: z.int(),
        payload_retention_days: z.int(),
        screen_mode: InboundWebhookScreenMode,
        status: InboundWebhookStatus,
      }),
    }),
  ),
  defineEvent(
    "inbound_webhook.deleted",
    {
      ...org,
      subject: "inbound_webhook",
      summary: "Incoming webhook removed with its delivery log",
      consumers: ["audit"],
    },
    z.strictObject({ name: z.string() }),
  ),
  defineEvent(
    "inbound_webhook.secret_rotated",
    {
      ...org,
      subject: "inbound_webhook",
      summary: "The secret was replaced; the previous one verifies for 24 h (never in the event)",
      consumers: ["audit"],
    },
    z.strictObject({ previous_until: Timestamp }),
  ),
  defineEvent(
    "inbound_webhook.received",
    {
      ...org,
      subject: "inbound_webhook",
      summary:
        "A verified delivery was accepted (the body stays on the delivery row); it becomes a task for the hook's DE",
      consumers: ["inbound task", "audit"],
    },
    z.strictObject({
      delivery_id: Uuid,
      bytes: z.int().min(0),
      content_type: z.string().nullable(),
      screen: z.strictObject({
        verdict: z.enum(["clean", "flagged", "off"]),
        score: z.number(),
        quarantined: z.boolean(),
      }),
      /** A manual replay or the dialog's sample (null: a sender's delivery). */
      via: z.enum(["replay", "sample"]).nullable(),
    }),
  ),
  defineEvent(
    "inbound_webhook.paused",
    {
      ...org,
      subject: "inbound_webhook",
      summary: "Paused by a person (signed senders get 503 and retry later)",
      consumers: ["audit"],
    },
    z.strictObject({ reason: z.string() }),
  ),
  defineEvent(
    "inbound_webhook.signatures_failing",
    {
      ...org,
      subject: "inbound_webhook",
      summary:
        "One address failed the signature 20 times in 10 minutes and is refused for the window (the hook stays on); at most hourly per hook",
      consumers: ["inbox", "audit"],
    },
    z.strictObject({
      source_ip: z.string(),
      rejected: z.int().min(1),
      window_minutes: z.int().min(1),
    }),
  ),
  defineEvent(
    "inbound_webhook.resumed",
    {
      ...org,
      subject: "inbound_webhook",
      summary: "A paused or disabled hook takes deliveries again",
      consumers: ["audit"],
    },
    z.strictObject({ from_status: InboundWebhookStatus }),
  ),
  defineEvent(
    "inbound_webhook.disabled",
    {
      ...org,
      subject: "inbound_webhook",
      summary: "Disabled by a person: senders get 410 and stop",
      consumers: ["audit"],
    },
    z.strictObject({}),
  ),

  // Skill packs (M4-02; 03 §Agents and crews `skill_pack.*`). A pack version reaches a company
  // when the catalog mirror copies it (`published`) and leaves when the catalog deprecates it;
  // `roles_changed` is an admin assigning it to roles (the seats whose runs mount it).
  defineEvent(
    "skill_pack.published",
    {
      ...agents,
      subject: "skill_pack",
      summary: "A skill pack version became available to the company (catalog mirror)",
      consumers: ["run admission (pin versions)", "audit"],
    },
    z.strictObject({
      key: z.string(),
      version: z.string(),
      scope: SkillPackScope,
      /** The pack's content hash (named `digest`: event fields ending in `hash` are refused). */
      content_digest: z.string().regex(/^[0-9a-f]{64}$/),
      source_pack_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "skill_pack.deprecated",
    {
      ...agents,
      subject: "skill_pack",
      summary: "A skill pack version was deprecated (no longer mounted on new runs)",
      consumers: ["run admission (pin versions)", "audit"],
    },
    z.strictObject({
      key: z.string(),
      version: z.string(),
      scope: SkillPackScope,
      content_digest: z.string().regex(/^[0-9a-f]{64}$/),
    }),
  ),
  defineEvent(
    "skill_pack.roles_changed",
    {
      ...agents,
      subject: "skill_pack",
      summary: "An admin changed which roles' seats get a skill pack",
      consumers: ["run admission (mounts)", "audit"],
    },
    z.strictObject({
      key: z.string(),
      version: z.string(),
      role_ids: z.array(Uuid),
      added_role_ids: z.array(Uuid),
      removed_role_ids: z.array(Uuid),
    }),
  ),
  // AGENT-SAFETY: a pack no run used for the company's `settings.skills.archive_unused_after_days`
  // (or one an admin archived) stops being mounted; it is never deleted, and an admin restores it.
  defineEvent(
    "skill_pack.archived",
    {
      ...agents,
      subject: "skill_pack",
      summary: "A skill pack was archived (kept, no longer mounted on new runs)",
      consumers: ["run admission (mounts)", "audit"],
    },
    z.strictObject({
      key: z.string(),
      version: z.string(),
      scope: SkillPackScope,
      reason: z.enum(["unused", "manual"]),
      /** The setting that applied (`unused` only). */
      unused_days: z.int().min(1).nullable(),
      last_used_at: Timestamp.nullable(),
    }),
  ),
  defineEvent(
    "skill_pack.restored",
    {
      ...agents,
      subject: "skill_pack",
      summary: "An admin restored an archived skill pack (mounted again)",
      consumers: ["run admission (mounts)", "audit"],
    },
    z.strictObject({ key: z.string(), version: z.string(), scope: SkillPackScope }),
  ),
  // SKILL-OVERRIDES: a manager wrote, reset or reviewed the company's additions to a skill (never
  // the text). The subject is the published pack the additions sit on.
  defineEvent(
    "skill_pack.additions_changed",
    {
      ...agents,
      subject: "skill_pack",
      summary:
        "A manager changed the company's additions to a skill (edit, reset to default, or a new default reviewed)",
      consumers: ["run admission (skill additions)", "audit"],
    },
    z.strictObject({
      key: z.string(),
      n: z.int().min(1),
      via: SkillAdditionVia,
      has_additions: z.boolean(),
      base_version: z.string(),
    }),
  ),
  // Agents, runs, approvals, delegation (M3-01; 03 §Approvals, runs, agents, cost; ADR-0021)
  defineEvent(
    "agent_profile.created",
    {
      ...agents,
      subject: "agent_profile",
      summary: "Agent profile added to a DE's composition (HTVA only; never customer-managed)",
      consumers: ["audit"],
    },
    z.strictObject({
      owner_membership_id: Uuid,
      owner_kind: AgentProfileOwnerKind,
      role_in_owner: AgentProfileRole,
      template_id: Uuid,
      template_version: z.int().min(1),
      kind: AgentTemplateKind,
    }),
  ),
  defineEvent(
    "agent_profile.updated",
    {
      ...agents,
      subject: "agent_profile",
      summary: "Agent profile re-pinned, re-roled or its overrides changed",
      consumers: ["audit", "run admission (pin)"],
    },
    z.strictObject({
      owner_membership_id: Uuid,
      changes: changesOf({
        role_in_owner: AgentProfileRole,
        template_version: z.int().min(1),
        overrides: z.record(z.string(), z.unknown()),
        memory_group_id: z.string(),
      }),
    }),
  ),
  defineEvent(
    "agent_profile.retired",
    { ...agents, subject: "agent_profile", summary: "Agent profile retired", consumers: ["audit"] },
    z.strictObject({ owner_membership_id: Uuid, role_in_owner: AgentProfileRole }),
  ),
  defineEvent(
    "wakeup.requested",
    {
      ...agents,
      subject: "wakeup_request",
      summary: "A reason to wake an agent (mention, heartbeat, inbox, approval, delegation, …)",
      consumers: ["run admission (coalesce → run.queued)"],
    },
    z.strictObject({
      agent_membership_id: Uuid,
      kind: WakeupKind,
      source_ref: WakeupSourceRef.nullable(),
    }),
  ),
  defineEvent(
    "wakeup.scheduled",
    {
      ...agents,
      subject: "run",
      summary:
        "AGENT-PLUMBING: a run asked to wake its DE on the same subject later (wait for helpers)",
      consumers: ["audit"],
    },
    z.strictObject({
      timer_id: Uuid,
      agent_membership_id: Uuid,
      source_ref: WakeupSourceRef,
      due_at: Timestamp,
      reason: z.string().max(500),
    }),
  ),
  // B5-DECIDE: the Activity line of a `decide` call under its run (counts, never item text).
  defineEvent(
    "classifier.decided",
    {
      ...agents,
      subject: "run",
      summary:
        "B5-DECIDE: a run classified items against a rubric (Jev or the local model); per-band counts",
      consumers: ["audit"],
    },
    z.strictObject({
      call_id: Uuid,
      agent_membership_id: Uuid,
      provider: z.enum(["jev", "local"]),
      model: z.string().max(128),
      rubric_id: Uuid.nullable(),
      rubric_key: z.string().max(64).nullable(),
      rubric_version: z.int().min(1).nullable(),
      items: z.int().min(1),
      questions: z.int().min(1),
      act: z.int().min(0),
      review: z.int().min(0),
      unsure: z.int().min(0),
      fallback_reason: z.string().max(200).nullable(),
      cost_micros: z.int().min(0),
    }),
  ),
  defineEvent(
    "decision_rubric.saved",
    {
      ...agents,
      subject: "decision_rubric",
      summary: "B5-DECIDE: a DE's classifier rubric got a new version (by the DE or a person)",
      consumers: ["audit"],
    },
    z.strictObject({
      agent_membership_id: Uuid,
      key: z.string().max(64),
      version: z.int().min(1),
      previous_id: Uuid.nullable(),
      note: z.string().max(300).nullable(),
    }),
  ),
  defineEvent(
    "wakeup.coalesced",
    {
      ...agents,
      subject: "wakeup_request",
      summary: "Wake-up folded into a run",
      consumers: ["audit", "runtime.steer (MID-RUN-STEER: a message for a running turn)"],
    },
    z.strictObject({ agent_membership_id: Uuid, run_id: Uuid }),
  ),
  defineEvent(
    "wakeup.dropped",
    {
      ...agents,
      subject: "wakeup_request",
      summary: "Wake-up dropped (budget, taken over, paused, policy)",
      consumers: ["audit", "inbox (owner on budget)"],
    },
    z.strictObject({
      agent_membership_id: Uuid,
      // WALLET-P1: `wallet` = the company's credit cannot cover the run's reservation.
      reason: z.enum([
        "budget",
        "taken_over",
        "paused",
        "policy",
        "duplicate",
        "expired",
        "wallet",
        // T2-BOARD-WAKE: the board's daily wake cap was reached (only `now` events wake past it).
        "board_cap",
        // PAUSE-NO-DROP (c): the subject is closed (a done task, an ended commitment).
        "closed",
        // SETTLE-SUBJECTS: the DE settled the conversation and no person wrote there since.
        "settled",
      ]),
    }),
  ),
  defineEvent(
    "run.queued",
    {
      ...agents,
      subject: "run",
      summary: "Run admitted with its budget reserved",
      consumers: ["rt", "budget ledger", "runtime webhook", "commitments.run_request"],
    },
    z.strictObject({
      membership_id: Uuid,
      seat_id: Uuid.nullable(),
      trigger_kind: WakeupKind,
      wakeup_ids: z.array(Uuid),
      parent_run_id: Uuid.nullable(),
      delegation_depth: z.int().min(0),
      budget_reserved_micros: MicrosAmount,
    }),
  ),
  defineEvent(
    "run.started",
    {
      ...agents,
      subject: "run",
      summary: "The runtime claimed the run (lease taken)",
      consumers: ["rt"],
    },
    z.strictObject({ membership_id: Uuid, seat_id: Uuid.nullable(), started_at: Timestamp }),
  ),
  defineEvent(
    "run.step_recorded",
    {
      ...agents,
      subject: "run",
      summary: "Run step appended (the agent-token charge basis)",
      consumers: ["cost ledger (incremental)", "rt"],
    },
    z.strictObject({
      step_id: Uuid,
      seq: z.int().min(1),
      kind: RunStepKind,
      model: z.string().nullable(),
      tokens_in: z.int().min(0),
      tokens_out: z.int().min(0),
      /** HTVA-RUN-FIXES: of `tokens_in`, the cached part (report_usage), for per-model prices. */
      cache_read: z.int().min(0).optional(),
      /** PRICE-TIERS: how many model calls a reported step adds up (tiers are per call). */
      calls: z.int().min(1).optional(),
      cost_micros: MicrosAmount,
      duration_ms: z.int().min(0).nullable(),
      /** DE-QUICK-ACK: what the step was for, when tagged (`quick_reply`: the instant first reply). */
      tag: z.enum(["quick_reply"]).optional(),
      /**
       * MODEL-KEYS-PLANS: `own` — the call ran on the company's own provider key: its cost is our
       * estimate, for visibility, and the wallet never charges it. Absent: our keys.
       */
      model_source: z.enum(["own"]).optional(),
    }),
  ),
  defineEvent(
    "run.waiting",
    {
      ...agents,
      subject: "run",
      summary: "Run waits for input, an approval, its children or budget",
      consumers: ["inbox (supervisor if approval/budget)", "rt"],
    },
    z.strictObject({
      reason: z.enum(["input", "approval", "children", "budget"]),
      approval_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "run.completed",
    {
      ...agents,
      subject: "run",
      summary: "Run completed with its hand-back",
      consumers: ["kpi", "inbox", "recall (outcome)", "digest", "budget settle", "handoff.run_end"],
    },
    z.strictObject({
      seat_id: Uuid.nullable(),
      cost_micros: MicrosAmount,
      tokens_in: z.int().min(0),
      tokens_out: z.int().min(0),
      summary: z.string().nullable(),
      hand_back: RunHandBack.nullable(),
    }),
  ),
  defineEvent(
    "run.failed",
    {
      ...agents,
      subject: "run",
      summary: "Run failed (agent error, runtime lost, budget, timeout)",
      consumers: ["inbox (supervisor)", "kpi", "budget settle", "handoff.run_end"],
    },
    z.strictObject({
      seat_id: Uuid.nullable(),
      reason: z.string(),
      cost_micros: MicrosAmount,
      tokens_in: z.int().min(0),
      tokens_out: z.int().min(0),
    }),
  ),
  defineEvent(
    "run.cancelled",
    {
      ...agents,
      subject: "run",
      summary: "A person cancelled the run",
      consumers: ["rt", "budget settle", "audit"],
    },
    z.strictObject({ seat_id: Uuid.nullable(), by_membership_id: Uuid, cost_micros: MicrosAmount }),
  ),
  // FAILURE-STOP: a person continued a run the MCP server stopped after repeated failures (its
  // error counters and the stop were cleared).
  defineEvent(
    "run.failures_continued",
    {
      ...agents,
      subject: "run",
      summary: "A person continued a run stopped after repeated failures; its error counts reset",
      consumers: ["audit"],
    },
    z.strictObject({
      seat_id: Uuid.nullable(),
      by_membership_id: Uuid,
      was_stopped: z.boolean(),
      errors_cleared: z.int().min(0),
      /** The run had ended: its DE was woken on the run's task again. */
      rewoken: z.boolean(),
    }),
  ),
  // lane B (REQUEST-IDENTITY): a request was delivered for review, and a person (or the
  // company's auto rule) decided it. Lane A consumes them for the onboarding first-task check.
  defineEvent(
    "request.delivered",
    {
      ...agents,
      subject: "request",
      summary: "A DE delivered a request for review (not yet human-approved complete)",
      consumers: ["audit", "onboarding readiness"],
    },
    z.strictObject({
      origin: z.string().min(1).max(20),
      station_membership_id: Uuid,
      task_id: Uuid.nullable(),
      run_id: Uuid.nullable(),
      revision: z.int().min(0),
    }),
  ),
  defineEvent(
    "request.accepted",
    {
      ...agents,
      subject: "request",
      summary: "A person accepted a delivered request as complete",
      consumers: ["audit", "onboarding readiness", "kpi (completions)"],
    },
    z.strictObject({
      origin: z.string().min(1).max(20),
      station_membership_id: Uuid,
      task_id: Uuid.nullable(),
      by_membership_id: Uuid.nullable(),
      via: z.enum(["review", "chat", "auto"]),
    }),
  ),
  defineEvent(
    "request.rejected",
    {
      ...agents,
      subject: "request",
      summary: "A person rejected a delivered request",
      consumers: ["audit"],
    },
    z.strictObject({
      station_membership_id: Uuid,
      task_id: Uuid.nullable(),
      by_membership_id: Uuid,
      /** lane B2: the person's reason (the chat card's Reject asks for one). */
      note: z.string().max(2000).nullable().optional(),
    }),
  ),
  // lane B (REQUEST-RECOVERY): a request was interrupted (one open recovery per request) and a
  // person resolved it (Continue / Take over / Dismiss).
  defineEvent(
    "recovery.opened",
    {
      ...agents,
      subject: "request_recovery",
      summary: "A request was interrupted; its accountable person decides what happens next",
      consumers: ["inbox (accountable person)", "monitoring alert", "audit"],
    },
    z.strictObject({
      request_id: Uuid,
      run_id: Uuid.nullable(),
      cause: z.string().min(1).max(40),
      station_membership_id: Uuid,
      accountable_membership_id: Uuid.nullable(),
      via: z.string().min(1).max(20).nullable(),
    }),
  ),
  defineEvent(
    "recovery.resolved",
    {
      ...agents,
      subject: "request_recovery",
      summary: "A person continued, took over or dismissed an interrupted request",
      consumers: ["audit"],
    },
    z.strictObject({
      request_id: Uuid,
      action: z.enum(["continue", "take_over", "dismiss", "auto"]),
      by_membership_id: Uuid.nullable(),
      next_run_id: Uuid.nullable(),
    }),
  ),
  // LOOP-BREAKER: the one warning before a loop stop (steered into the running turn), and the
  // stop itself (the run ends `failed` / `loop_stopped`; the task's agent shows blocked; the
  // owning DE and the seat's person are told; the incident keeps what it burned).
  defineEvent(
    "run.loop_warned",
    {
      ...agents,
      subject: "run",
      summary: "The run repeats itself without progress: it was warned once to change approach",
      consumers: ["runtime.loop_signal"],
    },
    z.strictObject({
      seat_id: Uuid.nullable(),
      kind: LoopIncidentKind,
      what: z.string(),
      repeats: z.int().min(0),
      steer: z.string(),
    }),
  ),
  defineEvent(
    "run.loop_stopped",
    {
      ...agents,
      subject: "run",
      summary: "The run was stopped as a loop (repeating without progress); a person decides next",
      consumers: ["runtime.loop_signal", "run.loop_inbox"],
    },
    z.strictObject({
      incident_id: Uuid,
      seat_id: Uuid.nullable(),
      task_id: Uuid.nullable(),
      kind: LoopIncidentKind,
      what: z.string(),
      reason: z.string(),
      repeats: z.int().min(0),
      tokens_in: z.int().min(0),
      tokens_out: z.int().min(0),
      cost_micros: MicrosAmount,
    }),
  ),
  defineEvent(
    "loop_incident.resolved",
    {
      ...agents,
      subject: "loop_incident",
      summary: "A loop incident's owner retried it with a hint, marked it reassigned, or closed it",
      consumers: ["audit"],
    },
    z.strictObject({
      run_id: Uuid,
      action: z.enum(["retry", "reassign", "close"]),
      seat_id: Uuid.nullable(),
      by_membership_id: Uuid,
    }),
  ),
  defineEvent(
    "run.delegated",
    {
      ...agents,
      subject: "run",
      summary: "The run delegated work (a delegation request was sent)",
      consumers: ["audit", "budget"],
    },
    z.strictObject({
      delegation_request_id: Uuid,
      to_membership_id: Uuid,
      to_seat_id: Uuid.nullable(),
      depth: z.int().min(1),
    }),
  ),
  // DC-08 (05 §Delegation rules 8): written after the refused request rolled back, so the
  // Agents › Wake-ups admission log shows why a worker never ran.
  defineEvent(
    "run.worker_refused",
    {
      ...agents,
      subject: "run",
      summary:
        "Admission refused an ephemeral worker of the run (no verbatim standing orders, depth, budget)",
      consumers: ["audit", "admission log"],
    },
    z.strictObject({
      agent_membership_id: Uuid,
      worker_profile_id: Uuid,
      reason: WorkerRefusalReason,
      detail: z.string().max(500),
    }),
  ),
  defineEvent(
    "sandbox.started",
    {
      ...agents,
      subject: "run",
      summary: "Browser or computer sandbox started for a run",
      consumers: ["audit"],
    },
    z.strictObject({ kind: SandboxKind }),
  ),
  defineEvent(
    "sandbox.stopped",
    {
      ...agents,
      subject: "run",
      summary: "Sandbox stopped (the sandbox-minute charge basis)",
      consumers: ["cost ledger", "audit"],
    },
    z.strictObject({ kind: SandboxKind, duration_ms: z.int().min(0), cost_micros: MicrosAmount }),
  ),
  defineEvent(
    "approval.requested",
    {
      ...agents,
      subject: "approval",
      summary: "Approval requested of a seat (by a run or a person)",
      consumers: ["inbox (approver)", "rt", "kpi (approvals_pending)", "recall"],
    },
    z.strictObject({
      kind: ApprovalKind,
      subject_kind: z.string(),
      subject_id: Uuid,
      approver_seat_id: Uuid,
      requested_by_seat_id: Uuid.nullable(),
      run_id: Uuid.nullable(),
      proposal: ApprovalProposal,
      expires_at: Timestamp.nullable(),
      // OOO-DELEGATE: set when routed around an absent approver ("covering for X")
      covering_for: z
        .strictObject({
          membership_id: Uuid,
          seat_id: Uuid,
          via: z.enum(["delegate", "next_up"]),
        })
        .optional(),
      // CORE-CARDS: filed with an escalation card (the Slack notice reads it); absent: none
      has_card: z.boolean().optional(),
    }),
  ),
  defineEvent(
    "approval.decided",
    {
      ...agents,
      subject: "approval",
      summary: "Approved, rejected or approved with edits (idempotent: the first decision stands)",
      consumers: ["run resumer", "apply-proposal worker", "inbox (requester)", "kpi", "recall"],
    },
    z.strictObject({
      kind: ApprovalKind,
      state: z.enum(["approved", "rejected", "edited"]),
      decided_by_membership_id: Uuid,
      reason: z.string().nullable(),
      run_id: Uuid.nullable(),
      // AGENT-RECORD: the task the approved work belongs to, as known at the decision (the
      // approval's subject when a task, else its run's task); absent on older events.
      task_id: Uuid.nullable().optional(),
      // B2-GATES: a rejection that is "send back with a note" (`reason` is the note)
      sent_back: z.boolean().optional(),
      // CORE-CARDS: the card option chosen (lane B's rulings record it); absent: none
      chosen_option: z.string().max(40).optional(),
    }),
  ),
  defineEvent(
    "approval.expired",
    {
      ...agents,
      subject: "approval",
      summary: "Approval expired undecided",
      consumers: ["inbox", "run cancel"],
    },
    z.strictObject({ kind: ApprovalKind, approver_seat_id: Uuid, run_id: Uuid.nullable() }),
  ),
  defineEvent(
    "approval.executed",
    {
      ...agents,
      subject: "approval",
      summary:
        "Phase 1 inc 2: what became of an approved payload (applied, or not applied with a reason and a permanent/transient class); apart from the decision",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      kind: ApprovalKind,
      outcome: z.enum(["applied", "failed"]),
      failure: ApprovalExecutionFailure.nullable(),
      code: ApprovalExecutionCode.nullable(),
      reason: z.string().nullable(),
      attempts: z.int().min(0),
      run_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "delegation_request.sent",
    {
      ...agents,
      subject: "delegation_request",
      summary: "Work delegated to another principal",
      consumers: ["inbox", "wakeup (to agent)", "audit", "budget"],
    },
    z.strictObject({
      from_membership_id: Uuid,
      to_membership_id: Uuid,
      to_seat_id: Uuid.nullable(),
      task_id: Uuid.nullable(),
      budget_micros: MicrosAmount,
      deadline_at: Timestamp.nullable(),
      depth: z.int().min(1),
      root_request_id: Uuid,
    }),
  ),
  defineEvent(
    "delegation_request.accepted",
    {
      ...agents,
      subject: "delegation_request",
      summary: "Delegation accepted",
      consumers: ["inbox", "audit"],
    },
    z.strictObject({ to_membership_id: Uuid, child_run_id: Uuid.nullable() }),
  ),
  defineEvent(
    "delegation_request.declined",
    {
      ...agents,
      subject: "delegation_request",
      summary: "Delegation declined",
      consumers: ["inbox", "wakeup (from agent)", "audit"],
    },
    z.strictObject({ to_membership_id: Uuid, reason: z.string().nullable() }),
  ),
  defineEvent(
    "delegation_request.expired",
    {
      ...agents,
      subject: "delegation_request",
      summary: "Delegation passed its deadline unanswered",
      consumers: ["inbox", "wakeup (from agent)", "audit"],
    },
    z.strictObject({ to_membership_id: Uuid, deadline_at: Timestamp }),
  ),
  defineEvent(
    "delegation_request.completed",
    {
      ...agents,
      subject: "delegation_request",
      summary: "Delegated work handed back",
      consumers: ["inbox", "wakeup (from agent)", "audit", "budget"],
    },
    z.strictObject({ to_membership_id: Uuid, result: RunHandBack.nullable() }),
  ),
  defineEvent(
    "agent_key.rotated",
    {
      domain: "access",
      feature: "core",
      subject: "agent_key",
      summary: "Key rotated by itself: the new key is live, the old one valid for 10 more minutes",
      consumers: ["audit", "permission cache"],
    },
    z.strictObject({
      membership_id: Uuid,
      seat_id: Uuid.nullable(),
      new_key_id: Uuid,
      new_key_prefix: z.string().length(12),
      old_valid_until: Timestamp,
    }),
  ),

  // Personal crews (M4-01; 03 §Crews; 05 §Personal crews)
  defineEvent(
    "personal_crew.created",
    {
      ...agents,
      subject: "personal_crew",
      summary: "A person's crew created with the membership: agent, personal space, #brief",
      consumers: ["recall (personal group)", "audit"],
    },
    z.strictObject({
      owner_membership_id: Uuid,
      personal_space_id: Uuid,
      brief_channel_id: Uuid,
      display_name: z.string(),
    }),
  ),
  defineEvent(
    "personal_crew.updated",
    {
      ...agents,
      subject: "personal_crew",
      summary: "The owner changed the crew's schedule, concurrency, model policy or cap",
      consumers: ["audit"],
    },
    z.strictObject({
      owner_membership_id: Uuid,
      changes: changesOf({
        heartbeat_schedule: z.array(z.string()),
        max_concurrency: z.int(),
        model_policy: z.record(z.string(), z.unknown()),
        cap_minor: MinorAmount,
      }),
    }),
  ),
  defineEvent(
    "personal_crew.paused",
    {
      ...agents,
      subject: "personal_crew",
      summary: "The crew stops starting runs (owner, cap reached, or membership paused)",
      consumers: ["wakeup.admission", "inbox (owner)", "audit"],
    },
    z.strictObject({ owner_membership_id: Uuid, reason: PersonalCrewPauseReason }),
  ),
  defineEvent(
    "personal_crew.resumed",
    {
      ...agents,
      subject: "personal_crew",
      summary: "The crew runs again",
      consumers: ["audit"],
    },
    z.strictObject({ owner_membership_id: Uuid }),
  ),
  defineEvent(
    "personal_crew.deactivated",
    {
      ...agents,
      subject: "personal_crew",
      summary: "The crew is deactivated with its owner's membership",
      consumers: ["recall (freeze personal group)", "audit"],
    },
    z.strictObject({ owner_membership_id: Uuid }),
  ),
  defineEvent(
    "personal_crew.onboarding_answered",
    {
      ...agents,
      subject: "personal_crew",
      summary: "The owner answered interview questions (resumable; `complete` scaffolds context)",
      consumers: ["audit"],
    },
    z.strictObject({
      owner_membership_id: Uuid,
      question_ids: z.array(z.string()),
      complete: z.boolean(),
    }),
  ),
  defineEvent(
    "crew_agent.added",
    {
      ...agents,
      subject: "crew_agent",
      summary: "A crew agent added (the role default with the crew, or by the owner)",
      consumers: ["audit"],
    },
    z.strictObject({
      crew_membership_id: Uuid,
      agent_profile_id: Uuid,
      name: z.string(),
      skill_pack_ids: z.array(Uuid),
      source: CrewAgentSource,
    }),
  ),
  defineEvent(
    "crew_agent.removed",
    {
      ...agents,
      subject: "crew_agent",
      summary: "A crew agent the owner added was removed (disabled; its profile retired)",
      consumers: ["audit"],
    },
    z.strictObject({ crew_membership_id: Uuid, agent_profile_id: Uuid }),
  ),
  defineEvent(
    "role_skill_policy.changed",
    {
      ...agents,
      subject: "role_skill_policy",
      summary: "An admin set or removed a role's crew ceiling",
      consumers: ["crews (re-evaluate autonomy)", "permission cache", "audit"],
    },
    z.strictObject({
      role_id: Uuid,
      allowed_skill_pack_ids: z.array(Uuid),
      default_skill_pack_ids: z.array(Uuid),
      budget_money_cents: MinorAmount.nullable(),
      budget_period: CrewCapPeriod,
      allowed_action_kinds: z.array(CrewActionKind),
      removed: z.boolean(),
    }),
  ),
  defineEvent(
    "autonomy_policy.changed",
    {
      ...agents,
      subject: "personal_crew",
      summary: "The owner set the crew's autonomy (the whole set; absent kinds are defaults)",
      consumers: ["permission cache", "audit"],
    },
    z.strictObject({
      owner_membership_id: Uuid,
      policies: z.array(
        z.strictObject({
          action_kind: CrewActionKind,
          mode: AutonomyMode,
          identity_mode: RunIdentityMode,
          constraints: z.record(z.string(), z.unknown()),
        }),
      ),
    }),
  ),
  defineEvent(
    "sharing_policy.changed",
    {
      ...agents,
      subject: "personal_crew",
      summary: "The owner set what the crew discloses (the whole set; absent kinds are defaults)",
      consumers: ["permission cache", "audit"],
    },
    z.strictObject({
      owner_membership_id: Uuid,
      rules: z.array(z.strictObject({ subject_kind: SharingSubjectKind, rule: SharingRule })),
    }),
  ),
  defineEvent(
    "crew_context.updated",
    {
      ...agents,
      subject: "crew_context_layer",
      summary: "A crew context layer written (it supersedes the layer's previous row)",
      consumers: ["audit"],
    },
    z.strictObject({
      crew_membership_id: Uuid,
      layer: CrewContextLayer,
      visibility: CrewContextVisibility,
      supersedes_id: Uuid.nullable(),
      source: z.enum(["onboarding", "owner", "run"]),
    }),
  ),
  defineEvent(
    "crew_cap.changed",
    {
      ...agents,
      subject: "company",
      summary: "An admin set the company's crew caps",
      consumers: ["wakeup.admission", "audit"],
    },
    z.strictObject({
      per_crew_cap_minor: MinorAmount.nullable(),
      company_cap_minor: MinorAmount.nullable(),
      period_type: CrewCapPeriod,
    }),
  ),

  // Crew features (M4-03; 00 §4; 10 §M4; OPEN-QUESTIONS 37)
  defineEvent(
    "personal_crew.features_changed",
    {
      ...agents,
      subject: "personal_crew",
      summary: "The owner set the crew's brief, inbox triage and heartbeat schedule",
      consumers: ["audit"],
    },
    z.strictObject({
      owner_membership_id: Uuid,
      triage_enabled: z.boolean(),
      brief: z.strictObject({
        enabled: z.boolean(),
        time: z.string(),
        days: z.array(z.int().min(1).max(7)),
      }),
      heartbeat_schedule: z.array(z.string()),
    }),
  ),
  // COS-SLICE1 (docs/design/2026-10-28-chief-of-staff-and-assistants.md §2.5, §5.3): the
  // Chief of Staff's digest, the follow-up cards (subject: the message they sit under, so their
  // pings reach exactly the message's readers) and the handoff park.
  defineEvent(
    "chief_of_staff.mode_suggested",
    {
      ...agents,
      subject: "company",
      summary:
        "COS-MODES: in Solo mode a person other than the CEO now leads agents, and the CEO is asked once per new head whether to switch to Team; or, in Team mode, the last such head left and Solo is suggested once (never switched automatically)",
      consumers: ["proj.inbox", "audit"],
    },
    z.strictObject({
      suggest: z.enum(["team", "solo"]),
      ceo_membership_id: Uuid,
      /** Team: the new head; Solo: null. */
      head_membership_id: Uuid.nullable(),
      head_name: z.string().nullable(),
      agents: z.int().min(0),
    }),
  ),
  defineEvent(
    "chief_of_staff.mode_changed",
    {
      ...agents,
      subject: "company",
      summary:
        "COS-MODES: the Chief of Staff switched Solo ↔ Team; each current department head is told once (Team: it acts on their agents at once and tells them; their own level is in Setup › Who may direct my agents)",
      consumers: ["proj.inbox", "audit"],
    },
    z.strictObject({
      from: z.enum(["solo", "team"]),
      to: z.enum(["solo", "team"]),
      head_membership_ids: z.array(Uuid).max(500),
    }),
  ),
  defineEvent(
    "cos_digest.posted",
    {
      ...agents,
      subject: "digital_employee",
      summary:
        "The check (or an immediate kind) posted a digest for a DE with the digest on, mentioning it",
      consumers: ["audit"],
    },
    z.strictObject({
      digest_id: Uuid,
      kind: CosDigestKind,
      message_id: Uuid,
      thread_id: Uuid,
      channel_id: Uuid,
      items: z.int().min(1),
    }),
  ),
  defineEvent(
    "suggestion.created",
    {
      ...chat,
      subject: "message",
      summary: "The detector offered a follow-up card under a message (task, reminder or meeting)",
      consumers: ["rt"],
    },
    z.strictObject({
      suggestion_id: Uuid,
      kind: SuggestionKind,
      thread_id: Uuid,
      channel_id: Uuid,
    }),
  ),
  defineEvent(
    "suggestion.accepted",
    {
      ...chat,
      subject: "message",
      summary: "A person accepted a follow-up card; it made the task, reminder or meeting named",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      suggestion_id: Uuid,
      kind: SuggestionKind,
      thread_id: Uuid,
      result_kind: SuggestionResultKind,
      result_id: Uuid,
    }),
  ),
  defineEvent(
    "suggestion.dismissed",
    {
      ...chat,
      subject: "message",
      summary: "A person dismissed a follow-up card (never raised again for that message)",
      consumers: ["rt"],
    },
    z.strictObject({ suggestion_id: Uuid, kind: SuggestionKind, thread_id: Uuid }),
  ),
  defineEvent(
    "crew_brief.posted",
    {
      ...agents,
      subject: "personal_crew",
      summary: "The crew posted the owner's daily brief into #brief (once a day)",
      consumers: ["audit"],
    },
    z.strictObject({
      crew_membership_id: Uuid,
      owner_membership_id: Uuid,
      brief_channel_id: Uuid,
      message_id: Uuid,
      on: IsoDate,
    }),
  ),
  defineEvent(
    "crew_proposal.created",
    {
      ...agents,
      subject: "crew_proposal",
      summary: "The crew proposed a triage, reply, reminder or task update (or applied it alone)",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      crew_membership_id: Uuid,
      owner_membership_id: Uuid,
      kind: CrewProposalKind,
      state: CrewProposalState,
      source: CrewProposalSource,
      inbox_item_id: Uuid.nullable(),
      approval_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "crew_proposal.decided",
    {
      ...agents,
      subject: "crew_proposal",
      summary: "The owner accepted or dismissed a proposal, or it expired with its item",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      crew_membership_id: Uuid,
      owner_membership_id: Uuid,
      kind: CrewProposalKind,
      state: z.enum(["accepted", "dismissed", "applied", "expired"]),
      decided_by: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "crew_memory_grant.changed",
    {
      ...agents,
      subject: "personal_crew",
      summary: "The owner set the role memory their crew may read (OQ 37: explicit grants only)",
      consumers: ["recall (crew key scopes, M7-05)", "audit"],
    },
    z.strictObject({
      crew_membership_id: Uuid,
      owner_membership_id: Uuid,
      granted: z.int().min(0),
      revoked: z.int().min(0),
    }),
  ),
  defineEvent(
    "recall.personal_grant_changed",
    {
      ...agents,
      subject: "personal_crew",
      summary:
        "The owner let their crew read their personal memory in Recall, or took it back (D12)",
      consumers: ["audit"],
    },
    z.strictObject({
      owner_membership_id: Uuid,
      access: z.enum(["read", "none"]),
      /** Recall's answer: what the crew's agent holds on the personal space now. */
      effective: z.string().max(32),
    }),
  ),
  defineEvent(
    "crew.hired_as_de",
    {
      ...agents,
      subject: "personal_crew",
      summary:
        "The owner hired their crew as a DE: a new seat and DE, the crew's chosen memory copied into it (M7-05)",
      consumers: ["recall (memory copy via import)", "audit"],
    },
    z.strictObject({
      crew_membership_id: Uuid,
      owner_membership_id: Uuid,
      new_seat_id: Uuid,
      new_de_membership_id: Uuid,
      copied_memory_groups: z
        .array(z.strictObject({ bucket: CrewMemoryBucket, subject_id: Uuid }))
        .max(32),
    }),
  ),

  // Finance controls (M3-01; 03 §Finance; 11 §Controls)
  defineEvent(
    "budget.changed",
    {
      ...finance,
      subject: "budget",
      summary: "Budget created, changed, archived or restored",
      consumers: ["budget cache", "audit"],
    },
    z.strictObject({
      op: z.enum(["created", "updated", "archived", "unarchived"]),
      subject_kind: BudgetSubjectKind,
      subject_id: Uuid,
      period_type: BudgetPeriodType,
      period_key: z.string().nullable(),
      amount_minor: MinorAmount,
      currency: Currency,
      soft_thresholds: z.array(Decimal),
      hard_cap_minor: MinorAmount.nullable(),
      inherit: z.boolean(),
    }),
  ),
  defineEvent(
    "budget.reserved",
    {
      ...finance,
      subject: "run",
      summary: "Budget held over the run's chain before it starts",
      consumers: ["cost ledger", "audit"],
    },
    z.strictObject({
      reservation_id: Uuid,
      chain: ReservationChain,
      estimate_micros: MicrosAmount,
      expires_at: Timestamp,
    }),
  ),
  defineEvent(
    "budget.settled",
    {
      ...finance,
      subject: "run",
      summary: "Reservation settled at actual spend; the difference is released",
      consumers: ["cost ledger", "audit"],
    },
    z.strictObject({
      reservation_id: Uuid,
      estimate_micros: MicrosAmount,
      actual_micros: MicrosAmount,
    }),
  ),
  defineEvent(
    "budget.released",
    {
      ...finance,
      subject: "run",
      summary: "Reservation released unused (cancelled, failed before spend, expired)",
      consumers: ["cost ledger", "audit"],
    },
    z.strictObject({
      reservation_id: Uuid,
      estimate_micros: MicrosAmount,
      reason: z.enum(["cancelled", "failed", "expired"]),
    }),
  ),
  defineEvent(
    "budget.reservation_refused",
    {
      ...finance,
      subject: ["run", "wakeup_request"],
      summary: "Admission refused: a level of the chain has no room",
      consumers: ["inbox (supervisor/owner)", "budget_incident"],
    },
    z.strictObject({
      level: BudgetSubjectKind,
      budget_id: Uuid,
      estimate_micros: MicrosAmount,
      shortfall_micros: MicrosAmount,
    }),
  ),
  defineEvent(
    "budget.threshold_crossed",
    {
      ...finance,
      subject: "budget",
      summary: "Spend crossed a soft threshold (once per threshold per period)",
      consumers: ["inbox (supervisor / owner / team lead)", "budget_incident"],
    },
    z.strictObject({
      threshold: Decimal,
      period_key: z.string().nullable(),
      actual_minor: MinorAmount,
      amount_minor: MinorAmount,
    }),
  ),
  defineEvent(
    "budget.exceeded",
    {
      ...finance,
      subject: "budget",
      summary: "Hard cap reached: the agent pauses and a budget_overrun approval is raised",
      consumers: ["agent pause", "approval.requested (budget_overrun)", "budget_incident"],
    },
    z.strictObject({
      period_key: z.string().nullable(),
      actual_minor: MinorAmount,
      cap_minor: MinorAmount,
    }),
  ),
  defineEvent(
    "budget.pace_alert",
    {
      ...finance,
      subject: "budget",
      summary:
        "DE-BUDGET-PACE: a DE's spend is ahead of pace for its limit (once per DE and period, again past the second threshold)",
      consumers: ["inbox (the seat's person, company admins)"],
    },
    z.strictObject({
      membership_id: Uuid,
      // PAUSE-NO-DROP: the percent warnings, the surge warning, the last message and the cap.
      level: z.enum(["pace", "pace_2", "t80", "t90", "t95", "t98", "surge", "final", "cap"]),
      period_key: z.string().nullable(),
      spent_minor: MinorAmount,
      limit_minor: MinorAmount,
      projected_minor: MinorAmount,
      runout_at: Timestamp.nullable(),
      recipients: z.array(Uuid),
      dm_message_id: Uuid.nullable(),
      summary: z.string(),
    }),
  ),
  defineEvent(
    "budget.reserve_reached",
    {
      ...finance,
      subject: "budget",
      summary:
        "DE-BUDGET-PACE: a DE's spend reached its limit less the reserve; no new runs start (the run in progress finishes its step)",
      consumers: ["inbox (the seat's person, company admins)", "budget.notice_mail"],
    },
    z.strictObject({
      membership_id: Uuid,
      period_key: z.string().nullable(),
      spent_minor: MinorAmount,
      limit_minor: MinorAmount,
      reserve_minor: MinorAmount,
      resets_at: Timestamp.nullable(),
      recipients: z.array(Uuid),
      summary: z.string(),
    }),
  ),
  defineEvent(
    "budget.period_reset",
    {
      ...finance,
      subject: "budget",
      summary:
        "DE-BUDGET-PACE: a DE paused at its reserve resumed by itself because the budget's period ended",
      consumers: ["inbox (the people the reserve notice told)"],
    },
    z.strictObject({
      membership_id: Uuid,
      recipients: z.array(Uuid),
      summary: z.string(),
    }),
  ),
  defineEvent(
    "budget.overrun_approved",
    {
      ...finance,
      subject: "budget",
      summary: "A budget_overrun approval raised the cap for the period (the agent resumes)",
      consumers: ["budget cache", "agent resume"],
    },
    z.strictObject({ approval_id: Uuid, extra_minor: MinorAmount, until: Timestamp }),
  ),
  defineEvent(
    "price_list.changed",
    {
      ...finance,
      subject: "price_list",
      summary: "A company price added or a platform/agency price mirrored into the cell",
      consumers: ["cost rollup rebuild (affected periods)", "audit"],
    },
    z.strictObject({
      scope: PriceListScope,
      unit: PriceUnit,
      model: z.string().nullable(),
      price_micros: MicrosAmount,
      currency: Currency,
      effective_from: IsoDate,
    }),
  ),
  defineEvent(
    "fx_rate.set",
    {
      ...finance,
      subject: "company",
      summary: "FX rate set for a period (no live FX)",
      consumers: ["cost rollup rebuild (affected periods)", "audit"],
    },
    z.strictObject({
      period_key: z.string(),
      from_currency: Currency,
      to_currency: Currency,
      rate: Decimal,
      previous_rate: Decimal.nullable(),
    }),
  ),

  // WALLET-P1: prepaid AI credits (docs/design/2026-10-27-wallet-pricing.md). Amounts are micros
  // of USD; no payload names a model, a card or a Stripe secret.
  defineEvent(
    "wallet.settings_changed",
    {
      ...finance,
      subject: "company",
      summary: "The wallet was switched on or off, or its limits or warning level changed",
      consumers: ["audit"],
    },
    z.strictObject({
      enabled: z.boolean(),
      min_topup_micros: MicrosAmount,
      max_topup_micros: MicrosAmount,
      low_watermark_bp: z.int().min(0).max(10_000),
      changed: z
        .array(z.enum(["enabled", "min_topup_micros", "max_topup_micros", "low_watermark_bp"]))
        .min(1),
    }),
  ),
  defineEvent(
    "wallet.checkout_started",
    {
      ...finance,
      subject: "wallet_checkout",
      summary: "A top-up went to Stripe Checkout (test mode in phase 1); credit lands when paid",
      consumers: ["audit"],
    },
    z.strictObject({
      amount_micros: MicrosAmount,
      bonus_micros: MicrosAmount,
      bonus_bp: z.int().min(0).max(10_000),
      tier_label: z.string().max(60).nullable(),
    }),
  ),
  defineEvent(
    "wallet.credited",
    {
      ...finance,
      subject: "wallet_entry",
      summary: "A paid top-up (and its ladder bonus) landed in the wallet",
      consumers: ["wallet.controls (resume DEs paused for no credit)", "audit"],
    },
    z.strictObject({
      checkout_id: Uuid,
      amount_micros: MicrosAmount,
      bonus_micros: MicrosAmount,
      balance_micros: MicrosAmount,
    }),
  ),
  defineEvent(
    "wallet.adjusted",
    {
      ...finance,
      subject: "wallet_entry",
      summary: "An operator credited or debited the wallet by hand, with a reason",
      consumers: ["wallet.controls", "audit"],
    },
    z.strictObject({
      amount_micros: MicrosAmount,
      reason: z.string().min(1).max(500),
      balance_micros: MicrosAmount,
    }),
  ),
  defineEvent(
    "wallet.refunded",
    {
      ...finance,
      subject: "wallet_entry",
      summary: "Part or all of a top-up went back to the card, its bonus clawed back pro rata",
      consumers: ["wallet.controls", "audit"],
    },
    z.strictObject({
      checkout_id: Uuid,
      amount_micros: MicrosAmount,
      bonus_clawback_micros: MicrosAmount,
      balance_micros: MicrosAmount,
    }),
  ),
  defineEvent(
    "wallet.low",
    {
      ...finance,
      subject: "company",
      summary: "Credit dropped to the warning level (once until the next credit)",
      consumers: ["inbox (owners and admins)"],
    },
    z.strictObject({ balance_micros: MicrosAmount, threshold_micros: MicrosAmount }),
  ),
  defineEvent(
    "wallet.empty",
    {
      ...finance,
      subject: "company",
      summary: "Credit reached zero: the company's DEs pause until a top-up",
      consumers: ["wallet.controls (pause DEs)", "inbox (owners and admins)"],
    },
    z.strictObject({ balance_micros: MicrosAmount }),
  ),
  defineEvent(
    "wallet.restored",
    {
      ...finance,
      subject: "company",
      summary: "A usage correction lifted the credit from zero or below back above zero",
      consumers: ["wallet.controls (resume DEs paused for credit)"],
    },
    z.strictObject({ balance_micros: MicrosAmount }),
  ),
  defineEvent(
    "tariff.changed",
    {
      ...finance,
      subject: "tariff",
      summary: "A company tariff override added or retired, or a platform version mirrored",
      consumers: ["audit"],
    },
    z.strictObject({
      action: z.enum(["created", "retired"]),
      scope: TariffScope,
      version: z.int().min(1),
      label: z.string().max(60),
      effective_from: IsoDate,
      input_per_m_micros: MicrosAmount,
      output_per_m_micros: MicrosAmount,
      image_micros: MicrosAmount,
      reported_cost_markup_bp: z.int().min(0),
    }),
  ),
  defineEvent(
    "tariff_catalog.changed",
    {
      ...finance,
      subject: "tariff",
      summary: "The platform tariff got a new version, or a future version was retired",
      consumers: ["tariff.mirror (next run)", "audit"],
    },
    z.strictObject({
      action: z.enum(["created", "retired"]),
      version: z.int().min(1),
      label: z.string().max(60),
      effective_from: IsoDate,
      input_per_m_micros: MicrosAmount,
      output_per_m_micros: MicrosAmount,
      image_micros: MicrosAmount,
      reported_cost_markup_bp: z.int().min(0),
    }),
  ),
  defineEvent(
    "topup_bonus_tier.changed",
    {
      ...finance,
      subject: "topup_bonus_tier",
      summary: "A rung of the top-up bonus ladder added, edited or removed",
      consumers: ["audit"],
    },
    z.strictObject({
      action: z.enum(["created", "updated", "deleted"]),
      min_amount_micros: MicrosAmount,
      bonus_bp: z.int().min(0).max(10_000),
      label: z.string().max(60).nullable(),
    }),
  ),

  // BILLING-PLANS: recurring plans, invoices, quotes and builds (docs/design/
  // 2026-10-27-wallet-pricing.md §3.5–§3.7). Amounts are integer cents; no payload carries a
  // Stripe id, a card number or a secret. Every billing state change is one of these (the audit).
  defineEvent(
    "billing.account_changed",
    {
      ...finance,
      subject: "company",
      summary: "How the company pays changed: card or invoice, net terms, payment methods",
      consumers: ["audit"],
    },
    z.strictObject({
      collection_method: CollectionMethod,
      net_terms_days: z.int().min(0).max(120).nullable(),
      payment_methods: z.array(InvoicePaymentMethod).nullable(),
      changed: z.array(z.string().max(40)).min(1).max(10),
    }),
  ),
  defineEvent(
    "billing.period_closed",
    {
      ...finance,
      subject: "company",
      summary:
        "An operator closed an ended billing month: its totals, unresolved count and worksheet fingerprint were recorded in the ledger state",
      consumers: ["audit"],
    },
    z.strictObject({
      period_key: z.string().max(7),
      unresolved_count: z.int().min(0),
      late_count: z.int().min(0),
      invoice_total_minor: z.int(),
      worksheet_sha256: z.string().max(64),
    }),
  ),
  defineEvent(
    "billing.price_changed",
    {
      ...finance,
      subject: "company",
      summary:
        "A platform admin changed a price list (plan or tier version, charge catalog, defaults, a template's tier or setup fee); recorded in the platform company's log",
      consumers: ["audit", "model_access.plan_closed"],
    },
    z.strictObject({
      // `cost_price`: the platform cost price list (HTVA-RUN-FIXES).
      // `plan_model_access`: a plan's "bring your own model keys" setting; `model_provider_map`:
      // one alias of the model → provider map (MODEL-KEYS-PLANS).
      list: z.enum([
        "plan",
        "maintenance_plan",
        "charge_catalog",
        "billing_default",
        "template",
        "cost_price",
        "plan_model_access",
        "model_provider_map",
      ]),
      action: z.enum(["created", "updated", "retired", "deleted"]),
      key: z.string().max(80),
      version: z.int().min(1).nullable(),
      price_minor: MinorAmount.nullable(),
    }),
  ),
  defineEvent(
    "subscription.created",
    {
      ...finance,
      subject: "company_subscription",
      summary: "The operator put the company on a platform plan (one Stripe subscription)",
      consumers: ["audit"],
    },
    z.strictObject({
      plan_key: z.string().max(80),
      plan_version: z.int().min(1),
      status: SubscriptionStatus,
      trial_end: Timestamp.nullable(),
    }),
  ),
  defineEvent(
    "subscription.updated",
    {
      ...finance,
      subject: "company_subscription",
      summary: "The plan, its prices, the trial, the grace days or the status changed",
      consumers: ["audit"],
    },
    z.strictObject({
      plan_key: z.string().max(80),
      plan_version: z.int().min(1),
      status: SubscriptionStatus,
      changed: z.array(z.string().max(40)).min(1).max(20),
    }),
  ),
  defineEvent(
    "subscription.quantity_changed",
    {
      ...finance,
      subject: "company_subscription",
      summary:
        "Extra people or a maintenance tier's DE count changed on the subscription (prorated on the next invoice)",
      consumers: ["audit"],
    },
    z.strictObject({
      item: z.enum(["extra_seats", "maintenance"]),
      tier: MaintenanceTier.nullable(),
      from: z.int().min(0),
      to: z.int().min(0),
      membership_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "subscription.past_due",
    {
      ...finance,
      subject: "company_subscription",
      summary: "A payment failed or an invoice is overdue: the grace period starts",
      consumers: ["inbox (owners and admins)", "audit"],
    },
    z.strictObject({
      invoice_id: Uuid.nullable(),
      reason: z.enum(["payment_failed", "overdue"]),
      grace_until: Timestamp,
    }),
  ),
  defineEvent(
    "subscription.reminded",
    {
      ...finance,
      subject: "company_subscription",
      summary: "The reminder of a past-due subscription went out (once per grace period)",
      consumers: ["inbox (owners and admins)", "audit"],
    },
    z.strictObject({ invoice_id: Uuid.nullable(), grace_until: Timestamp }),
  ),
  defineEvent(
    "subscription.suspended",
    {
      ...finance,
      subject: "company_subscription",
      summary:
        "Grace ran out: the platform is read-only and the DEs pause with reason `subscription`",
      consumers: ["inbox (owners and admins)", "audit"],
    },
    z.strictObject({ invoice_id: Uuid.nullable(), paused_des: z.int().min(0) }),
  ),
  defineEvent(
    "subscription.restored",
    {
      ...finance,
      subject: "company_subscription",
      summary: "Payment arrived: the platform is writable again and DEs paused for it resume",
      consumers: ["inbox (owners and admins)", "audit"],
    },
    z.strictObject({ invoice_id: Uuid.nullable(), resumed_des: z.int().min(0) }),
  ),
  defineEvent(
    "subscription.cancelled",
    {
      ...finance,
      subject: "company_subscription",
      summary: "The subscription was cancelled (now, or at the end of the period)",
      consumers: ["audit"],
    },
    z.strictObject({ at_period_end: z.boolean() }),
  ),
  defineEvent(
    "de_maintenance.changed",
    {
      ...finance,
      subject: "digital_employee",
      summary:
        "A DE's maintenance line started, changed tier or ended (the proration path on the subscription)",
      consumers: ["audit"],
    },
    z.strictObject({
      membership_id: Uuid,
      from_tier: MaintenanceTier.nullable(),
      to_tier: MaintenanceTier.nullable(),
      /** Absent or `now`: the line changed. `scheduled`: a downgrade set for the renewal at
       *  `effective_at`; `unscheduled`: one cancelled before it (the tier stays). */
      when: z.enum(["now", "scheduled", "unscheduled"]).optional(),
      effective_at: Timestamp.nullable().optional(),
    }),
  ),
  defineEvent(
    "workforce_agent.cap_refused",
    {
      ...finance,
      subject: "digital_employee",
      summary: "An AG was refused under a DE at its tier's AG cap (upgrade the tier to go over)",
      consumers: ["audit"],
    },
    z.strictObject({
      membership_id: Uuid,
      tier: MaintenanceTier,
      ag_cap: z.int().min(0),
      ag_count: z.int().min(0),
      next_tier: MaintenanceTier.nullable(),
    }),
  ),
  defineEvent(
    "discount.granted",
    {
      ...finance,
      subject: "company_discount",
      summary: "A founding-client discount: a percentage off the recurring invoices for N months",
      consumers: ["audit"],
    },
    z.strictObject({
      label: z.string().max(60),
      percent_bp: z.int().min(1).max(10_000),
      months: z.int().min(1).max(36),
      applies_to: DiscountAppliesTo,
      starts_on: IsoDate,
      ends_on: IsoDate,
    }),
  ),
  defineEvent(
    "discount.ended",
    {
      ...finance,
      subject: "company_discount",
      summary: "A discount was ended early by the operator (off at the next renewal)",
      consumers: ["audit"],
    },
    z.strictObject({ label: z.string().max(60) }),
  ),
  defineEvent(
    "billing_operation.failed",
    {
      ...finance,
      subject: "billing_operation",
      summary:
        "A billing change failed for good (refused, or five failures in a row); the queue goes on",
      consumers: ["audit"],
    },
    z.strictObject({
      kind: z.string().max(40),
      code: z.string().max(60),
      attempts: z.int().min(0),
      dead_letter: z.boolean(),
    }),
  ),
  defineEvent(
    "billing_operation.retried",
    {
      ...finance,
      subject: "billing_operation",
      summary: "The operator retried a failed billing change",
      consumers: ["audit"],
    },
    z.strictObject({ kind: z.string().max(40) }),
  ),
  defineEvent(
    "billing_operation.dismissed",
    {
      ...finance,
      subject: "billing_operation",
      summary: "The operator dismissed a failed billing change",
      consumers: ["audit"],
    },
    z.strictObject({ kind: z.string().max(40) }),
  ),
  defineEvent(
    "billing_charge.created",
    {
      ...finance,
      subject: "billing_charge",
      summary: "A one-off charge (a DE's setup fee) was put on the company's next invoice",
      consumers: ["audit"],
    },
    z.strictObject({
      kind: InvoiceSource,
      amount_minor: MinorAmount,
      membership_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "invoice.created",
    {
      ...finance,
      subject: "invoice",
      summary:
        "An invoice first seen by the mirror: one CompanyOS drafted (a milestone, a setup fee, a custom charge, credit) or one Stripe made (a renewal, a quote's)",
      consumers: ["audit"],
    },
    // a draft may have no lines yet (its lines are added after it is created)
    z.strictObject({ source: InvoiceSource, total_minor: MinorAmount, lines: z.int().min(0) }),
  ),
  defineEvent(
    "invoice.finalized",
    {
      ...finance,
      subject: "invoice",
      summary: "An invoice was finalised (and, for invoice collection, sent with its due date)",
      consumers: ["audit"],
    },
    z.strictObject({
      source: InvoiceSource,
      total_minor: MinorAmount,
      due_date: IsoDate.nullable(),
    }),
  ),
  defineEvent(
    "invoice.paid",
    {
      ...finance,
      subject: "invoice",
      summary: "An invoice was paid (card, ACH, bank transfer, or marked paid out of band)",
      consumers: ["billing dunning (restore)", "audit"],
    },
    z.strictObject({
      source: InvoiceSource,
      total_minor: MinorAmount,
      payment_method: z.string().max(40).nullable(),
      out_of_band: z.boolean(),
    }),
  ),
  defineEvent(
    "invoice.payment_failed",
    {
      ...finance,
      subject: "invoice",
      summary: "A card or bank debit on an invoice failed (Stripe retries; dunning starts)",
      consumers: ["billing dunning", "audit"],
    },
    z.strictObject({ source: InvoiceSource, amount_due_minor: MinorAmount }),
  ),
  defineEvent(
    "invoice.overdue",
    {
      ...finance,
      subject: "invoice",
      summary: "A sent invoice is still open after its due date",
      consumers: ["billing dunning", "audit"],
    },
    z.strictObject({
      source: InvoiceSource,
      due_date: IsoDate,
      amount_due_minor: MinorAmount,
    }),
  ),
  defineEvent(
    "invoice.reminded",
    {
      ...finance,
      subject: "invoice",
      summary: "The operator sent a payment reminder for an open invoice",
      consumers: ["audit"],
    },
    z.strictObject({ due_date: IsoDate.nullable(), amount_due_minor: MinorAmount }),
  ),
  defineEvent(
    "invoice.sent",
    {
      ...finance,
      subject: "invoice",
      summary: "The operator sent an invoice to the client (by Stripe's email, or charged a card)",
      consumers: ["audit"],
    },
    z.strictObject({ source: InvoiceSource, due_date: IsoDate.nullable() }),
  ),
  defineEvent(
    "invoice.marked_paid",
    {
      ...finance,
      subject: "invoice",
      summary: "The operator recorded an invoice as paid outside Stripe (a wire, a check)",
      consumers: ["audit"],
    },
    z.strictObject({ source: InvoiceSource, total_minor: MinorAmount }),
  ),
  defineEvent(
    "invoice.voided",
    {
      ...finance,
      subject: "invoice",
      summary: "An invoice was voided (nothing is owed; a milestone on it is pending again)",
      consumers: ["audit"],
    },
    z.strictObject({ source: InvoiceSource, total_minor: MinorAmount }),
  ),
  defineEvent(
    "invoice.marked_uncollectible",
    {
      ...finance,
      subject: "invoice",
      summary:
        "An invoice was written off as uncollectible in Stripe (still unpaid: it keeps the company past due until paid or voided)",
      consumers: ["audit"],
    },
    z.strictObject({ source: InvoiceSource, amount_due_minor: MinorAmount }),
  ),
  defineEvent(
    "wallet.topup_invoiced",
    {
      ...finance,
      subject: "invoice",
      summary: "Credit was bought by invoice: it lands in the wallet when the invoice is paid",
      consumers: ["audit"],
    },
    z.strictObject({ amount_micros: MicrosAmount, due_date: IsoDate.nullable() }),
  ),
  defineEvent(
    "quote.changed",
    {
      ...finance,
      subject: "quote",
      summary: "A quote was drafted, sent, accepted (its invoice follows), canceled or expired",
      consumers: ["audit"],
    },
    z.strictObject({
      action: z.enum(["created", "sent", "accepted", "canceled", "expired"]),
      status: QuoteStatus,
      total_minor: MinorAmount,
    }),
  ),
  // ADMIN-INSIGHTS: an owner or admin asked Ask a question (the text stays out of the event: it
  // is the asker's, kept 30 days in `insight_ask`), and pinned or unpinned an answer on Home.
  defineEvent(
    "insight.asked",
    {
      ...work,
      subject: "insight_ask",
      summary: "An owner or admin asked a question in Ask; how it was understood",
      consumers: ["audit"],
    },
    z.strictObject({
      via: InsightAskVia,
      measure: InsightMeasure.nullable(),
    }),
  ),
  defineEvent(
    "insight_widget.changed",
    {
      ...work,
      subject: "insight_widget",
      summary: "An answer was pinned to Home as a widget, or unpinned",
      consumers: ["rt (the Home of the people who see it)", "audit"],
    },
    z.strictObject({
      action: z.enum(["pinned", "removed"]),
      measure: InsightMeasure,
      visibility: InsightVisibility,
    }),
  ),
  defineEvent(
    "helper_offer.changed",
    {
      ...finance,
      subject: "helper_offer",
      summary:
        "A helper quote was sent to the company, approved (invoiced, its monthly line added, the build started), declined, canceled, or its helper added",
      consumers: ["inbox (owners and admins, on sent)", "billing.lines", "audit"],
    },
    z.strictObject({
      action: z.enum(["sent", "approved", "declined", "canceled", "added"]),
      status: HelperOfferStatus,
      helper_name: z.string().min(1).max(120),
      one_off_minor: MinorAmount,
      monthly_minor: MinorAmount,
      currency: Currency,
    }),
  ),
  defineEvent(
    "project.changed",
    {
      ...finance,
      subject: "build_project",
      summary: "A build project was created, or its status or maintenance shape changed",
      consumers: ["audit"],
    },
    z.strictObject({
      action: z.enum(["created", "updated"]),
      status: BuildProjectStatus,
      total_minor: MinorAmount,
      changed: z.array(z.string().max(40)).max(10),
    }),
  ),
  defineEvent(
    "project.milestone_invoiced",
    {
      ...finance,
      subject: "build_project",
      summary: "A build milestone (or a yearly maintenance) was invoiced",
      consumers: ["audit"],
    },
    z.strictObject({
      milestone_id: Uuid.nullable(),
      invoice_id: Uuid,
      amount_minor: MinorAmount,
      kind: z.enum(["milestone", "maintenance_yearly"]),
    }),
  ),

  // Finance views (M5-03; 11 §Revenue attribution, §Planning). Payloads of restricted rows
  // (compensation, benefits) name what changed, never an amount: the event log is read more
  // widely than the rows (05 §finance).
  defineEvent(
    "category.changed",
    {
      ...finance,
      subject: "category",
      summary: "Chart-of-accounts category created or changed",
      consumers: ["exports", "audit"],
    },
    z.strictObject({
      op: z.enum(["created", "updated"]),
      name: z.string(),
      kind: CategoryKind,
      code: z.string().nullable(),
      status: FinanceRecordStatus,
    }),
  ),
  defineEvent(
    "client.changed",
    {
      ...finance,
      subject: "client",
      summary: "Client created or changed (name, status, seat shares)",
      consumers: ["finance views (margin per client)", "audit"],
    },
    z.strictObject({
      op: z.enum(["created", "updated"]),
      name: z.string(),
      status: FinanceRecordStatus,
      seats: z.array(z.strictObject({ seat_id: Uuid, share: Decimal })),
    }),
  ),
  defineEvent(
    "revenue.posted",
    {
      ...finance,
      subject: "revenue_event",
      summary: "Revenue row posted (a correction is a negative row)",
      consumers: ["finance views (margin)", "audit"],
    },
    z.strictObject({
      occurred_at: Timestamp,
      period_key: MonthKey,
      amount_minor: MinorAmount,
      currency: Currency,
      client_id: Uuid.nullable(),
      seat_id: Uuid.nullable(),
      project_ref: z.string().nullable(),
      kind: RevenueKind,
      category_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "compensation.changed",
    {
      ...finance,
      subject: "compensation",
      summary: "Compensation of a seat set or changed (no amount in the payload)",
      consumers: ["finance views", "forecast", "audit"],
    },
    z.strictObject({
      op: z.enum(["created", "updated"]),
      seat_id: Uuid,
      membership_id: Uuid.nullable(),
      cadence: CompensationCadence,
      currency: Currency,
      effective_from: IsoDate,
      effective_to: IsoDate.nullable(),
      fields: z.array(z.string()),
    }),
  ),
  defineEvent(
    "recurring_expense.changed",
    {
      ...finance,
      subject: "recurring_expense",
      summary: "Benefit, overhead or software subscription set or changed (no amount)",
      consumers: ["finance views", "forecast", "audit"],
    },
    z.strictObject({
      op: z.enum(["created", "updated"]),
      name: z.string(),
      kind: RecurringExpenseKind,
      cadence: RecurringCadence,
      currency: Currency,
      seat_id: Uuid.nullable(),
      team_id: Uuid.nullable(),
      start_month: MonthKey,
      end_month: MonthKey.nullable(),
      fields: z.array(z.string()),
    }),
  ),
  defineEvent(
    "forecast.snapshotted",
    {
      ...finance,
      subject: "company",
      summary: "Nightly forecast of the month stored for the company, its teams and seats",
      consumers: ["finance views"],
    },
    z.strictObject({
      period_key: MonthKey,
      as_of: IsoDate,
      subjects: z.int().min(1),
      currency: Currency,
      company_projected_minor: MinorAmount,
    }),
  ),
  defineEvent(
    "scenario.created",
    { ...finance, subject: "scenario", summary: "Scenario created", consumers: ["audit"] },
    z.strictObject({ name: z.string(), base_period: MonthKey, changes: z.int().min(0) }),
  ),
  // ACTIONS: edited (name, base period or changes; re-evaluated in the same call) or deleted.
  defineEvent(
    "scenario.updated",
    { ...finance, subject: "scenario", summary: "Scenario edited", consumers: ["audit"] },
    z.strictObject({ name: z.string(), base_period: MonthKey, changes: z.int().min(0) }),
  ),
  defineEvent(
    "scenario.deleted",
    { ...finance, subject: "scenario", summary: "Scenario deleted", consumers: ["audit"] },
    z.strictObject({ name: z.string() }),
  ),
  defineEvent(
    "scenario.evaluated",
    {
      ...finance,
      subject: "scenario",
      summary: "Scenario evaluated by the engine (result stored on the scenario)",
      consumers: ["audit"],
    },
    z.strictObject({
      base_period: MonthKey,
      currency: Currency,
      cost_delta_minor: MinorAmount,
      scenario_margin_minor: MinorAmount,
    }),
  ),
  defineEvent(
    "headcount_plan.changed",
    {
      ...finance,
      subject: "headcount_plan",
      summary: "Headcount plan line created, changed or archived",
      consumers: ["finance views", "audit"],
    },
    z.strictObject({
      op: z.enum(["created", "updated", "archived"]),
      title: z.string(),
      holder_kind: HeadcountHolderKind,
      team_id: Uuid.nullable(),
      seat_id: Uuid.nullable(),
      start_month: MonthKey,
      end_month: MonthKey.nullable(),
      fields: z.array(z.string()),
    }),
  ),
  defineEvent(
    "finance.exported",
    {
      ...finance,
      subject: "company",
      summary: "Finance export produced (CSV/XLSX)",
      consumers: ["audit"],
    },
    z.strictObject({
      kind: z.enum(["cost_events", "revenue_events", "rollup"]),
      format: z.enum(["csv", "xlsx"]),
      period_from: MonthKey,
      period_to: MonthKey,
      rows: z.int().min(0),
    }),
  ),

  // Spaces, chat, inbox, boards and tasks (M2-01)
  defineEvent(
    "space.created",
    {
      ...work,
      subject: "space",
      summary: "Space created",
      consumers: ["channel auto-create", "recall", "search"],
    },
    z.strictObject({
      name: z.string(),
      kind: SpaceKind,
      team_id: Uuid.nullable(),
      owner_membership_id: Uuid.nullable(),
      is_private: z.boolean(),
    }),
  ),
  defineEvent(
    "space.updated",
    {
      ...work,
      subject: "space",
      summary: "Space renamed, re-teamed or its settings changed",
      consumers: ["recall", "search"],
    },
    z.strictObject({
      changes: changesOf({
        name: z.string(),
        team_id: Uuid,
        icon: z.string(),
        description: z.string(),
        is_private: z.boolean(),
        settings: SpaceSettings,
      }),
    }),
  ),
  defineEvent(
    "space.archived",
    {
      ...work,
      subject: "space",
      summary: "Space archived with its channels and boards (nothing is deleted)",
      consumers: ["recall", "search"],
    },
    z.strictObject({ archived_channel_ids: z.array(Uuid), archived_board_ids: z.array(Uuid) }),
  ),
  // ACTIONS: back from the archive with exactly what its archive took (the channels and boards
  // archived at the same instant); ones archived before it stay archived.
  defineEvent(
    "space.unarchived",
    {
      ...work,
      subject: "space",
      summary: "Space restored with the channels and boards its archive took",
      consumers: ["recall", "search"],
    },
    z.strictObject({ restored_channel_ids: z.array(Uuid), restored_board_ids: z.array(Uuid) }),
  ),
  defineEvent(
    "space.moved",
    {
      ...work,
      subject: "space",
      summary: "Space moved in the company's order of spaces (BOARD-ORDER)",
      consumers: ["rt"],
    },
    z.strictObject({
      before_space_id: Uuid.nullable(),
      space_ids: z.array(Uuid).describe("The company's live shared spaces in their new order"),
    }),
  ),
  defineEvent(
    "channel.created",
    {
      ...chat,
      subject: "channel",
      summary: "Channel created (also DMs and board home channels)",
      consumers: ["rt", "search"],
    },
    z.strictObject({
      space_id: Uuid.nullable(),
      kind: ChannelKind,
      name: z.string(),
      board_id: Uuid.nullable(),
      team_id: Uuid.nullable(),
      is_private: z.boolean(),
      is_agent_room: z.boolean(),
      member_ids: z.array(Uuid),
    }),
  ),
  // MULTI-SPACE-ACCESS: the spaces a channel is shared into (beside its home) changed. `from` /
  // `to` are the share lists (space and level) before and after.
  defineEvent(
    "channel.shares_changed",
    {
      ...chat,
      subject: "channel",
      summary: "Channel shared into more spaces, or a share's level changed or was removed",
      consumers: ["audit"],
    },
    z.strictObject({
      space_id: Uuid.nullable(),
      from: z.array(z.strictObject({ space_id: Uuid, level: SpaceShareLevel })),
      to: z.array(z.strictObject({ space_id: Uuid, level: SpaceShareLevel })),
    }),
  ),
  defineEvent(
    "channel.updated",
    {
      ...chat,
      subject: "channel",
      summary: "Channel renamed or its privacy / agent-room flag / posting policy changed",
      consumers: ["rt", "search"],
    },
    z.strictObject({
      changes: changesOf({
        name: z.string(),
        description: z.string(),
        is_private: z.boolean(),
        is_agent_room: z.boolean(),
        posting_policy: ChannelPostingPolicy,
        topic_policy: ChannelPostingPolicy,
        broadcast_policy: ChannelPostingPolicy,
        default_notify_level: ChannelNotifyLevel,
      }),
    }),
  ),
  defineEvent(
    "channel.archived",
    { ...chat, subject: "channel", summary: "Channel archived", consumers: ["rt", "search"] },
    z.strictObject({ space_id: Uuid.nullable() }),
  ),
  defineEvent(
    "channel.restored",
    {
      ...chat,
      subject: "channel",
      summary: "Archived channel restored by a manager (CHAT-CONTROLS)",
      consumers: ["rt", "search"],
    },
    z.strictObject({ space_id: Uuid.nullable() }),
  ),
  defineEvent(
    "channel.deleted",
    {
      ...chat,
      subject: "channel",
      summary: "Channel deleted by a manager: a tombstone, its messages kept (CHAT-CONTROLS)",
      consumers: ["rt", "search", "recall (tombstone)"],
    },
    z.strictObject({ space_id: Uuid.nullable(), name: z.string() }),
  ),
  defineEvent(
    "channel.member_changed",
    {
      ...chat,
      subject: "channel",
      summary:
        "A member joined or left a channel, or changed role, notification level or mute (M2-02)",
      consumers: ["rt", "inbox"],
    },
    z.strictObject({
      membership_id: Uuid,
      role: ChannelMemberRole.nullable(),
      op: z.enum(["added", "removed", "updated"]),
    }),
  ),
  defineEvent(
    "channel.read",
    {
      ...chat,
      subject: "channel",
      summary: "A member's read position moved (unread counts, inbox)",
      consumers: ["proj.inbox", "rt"],
    },
    z.strictObject({ membership_id: Uuid, message_id: Uuid }),
  ),
  // THREAD-READS: Slack's per-thread read state. The subject is the thread's root message, so
  // the ping reaches the channel's readers only (and carries no one's position).
  defineEvent(
    "thread.read",
    {
      ...chat,
      subject: "message",
      summary: "A member's read position in a reply thread moved (THREAD-READS)",
      consumers: ["rt"],
    },
    z.strictObject({
      membership_id: Uuid,
      thread_id: Uuid,
      last_read_reply_id: Uuid.nullable(),
      last_read_at: Timestamp,
    }),
  ),
  defineEvent(
    "thread.follow_changed",
    {
      ...chat,
      subject: "message",
      summary: "A member followed or unfollowed a reply thread by hand (THREAD-READS)",
      consumers: ["rt"],
    },
    z.strictObject({ membership_id: Uuid, thread_id: Uuid, following: z.boolean() }),
  ),
  defineEvent(
    "thread.agent_left",
    {
      ...chat,
      subject: "message",
      summary:
        "An agent was muted in a reply thread: replies naming nobody no longer wake it (CHAT-THREAD-AGENTS)",
      consumers: ["rt"],
    },
    z.strictObject({
      thread_id: Uuid,
      agent_membership_id: Uuid,
      by_membership_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "agent_room.taken_over",
    {
      ...chat,
      subject: ["channel", "topic"],
      summary: "A person took over an agent room; agent runs targeting it park",
      consumers: ["rt (banner)", "run parking", "audit"],
    },
    z.strictObject({ by_membership_id: Uuid }),
  ),
  defineEvent(
    "agent_room.released",
    {
      ...chat,
      subject: ["channel", "topic"],
      summary: "The take-over ended; parked runs may resume",
      consumers: ["rt (banner)", "run parking", "audit"],
    },
    z.strictObject({ by_membership_id: Uuid, taken_over_at: Timestamp }),
  ),
  // M4-05: rooms, their participants and turns (the `agents` add-on), parked runs, status asks.
  defineEvent(
    "agent_room.created",
    {
      ...agents,
      subject: "agent_room",
      summary: "A channel or topic became an agent room",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      channel_id: Uuid,
      topic_id: Uuid.nullable(),
      window_size: z.int().min(1).max(200),
      max_agent_turns: z.int().min(1).max(50),
    }),
  ),
  defineEvent(
    "agent_room.updated",
    {
      ...agents,
      subject: "agent_room",
      summary: "The room's turn window or agent-turn limit changed",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      changes: changesOf({
        window_size: z.int().min(1).max(200),
        max_agent_turns: z.int().min(1).max(50),
      }),
    }),
  ),
  defineEvent(
    "agent_room.participant_changed",
    {
      ...agents,
      subject: "agent_room",
      summary: "An agent or a person was added to, joined, left or was removed from a room",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      membership_id: Uuid,
      kind: z.enum(["agent", "person"]),
      op: z.enum(["added", "removed"]),
    }),
  ),
  // ACTIONS: a room closed by its managers. Its channel and messages stay: a room channel of its
  // own is archived, a channel or topic flagged a room is a plain one again.
  defineEvent(
    "agent_room.removed",
    {
      ...agents,
      subject: "agent_room",
      summary: "An agent room was closed (its messages kept)",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      channel_id: Uuid,
      topic_id: Uuid.nullable(),
      channel_archived: z.boolean(),
    }),
  ),
  defineEvent(
    "agent_room.turn_changed",
    {
      ...agents,
      subject: "agent_room",
      summary: "The turn moved to another agent, or the room stalled (loop prevention)",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      membership_id: Uuid.nullable(),
      seq: z.int().min(0),
      reason: z.enum([
        "started",
        "posted",
        "passed",
        "human",
        "released",
        "stalled",
        "participant_changed",
      ]),
    }),
  ),
  defineEvent(
    "run.parked",
    {
      ...agents,
      subject: "run",
      summary: "A take-over parked a run targeting the room (it waits until the release)",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      channel_id: Uuid,
      topic_id: Uuid.nullable(),
      prior_state: RunState,
      by_membership_id: Uuid,
    }),
  ),
  defineEvent(
    "run.unparked",
    {
      ...agents,
      subject: "run",
      summary: "The release restored a parked run's state",
      consumers: ["rt", "audit"],
    },
    z.strictObject({ channel_id: Uuid, topic_id: Uuid.nullable(), state: RunState }),
  ),
  defineEvent(
    "status_ask.created",
    {
      ...agents,
      subject: "status_ask",
      summary: "An agent asked another agent in a room for a task's status",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      room_id: Uuid,
      asker_membership_id: Uuid,
      target_membership_id: Uuid,
      subject_kind: StatusAskSubjectKind,
      subject_id: Uuid,
      rule: SharingRule,
      delivered: z.boolean(),
    }),
  ),
  defineEvent(
    "status_ask.answered",
    {
      ...agents,
      subject: "status_ask",
      summary: "A status ask was answered (by its target agent or the human it escalated to)",
      consumers: ["rt", "audit"],
    },
    z.strictObject({ by_membership_id: Uuid, by_kind: z.enum(["agent", "person"]) }),
  ),
  defineEvent(
    "status_ask.escalated",
    {
      ...agents,
      subject: "status_ask",
      summary:
        "A status ask went to the target's human: the sharing policy refused it, or it was stuck",
      consumers: ["rt", "inbox (the human)", "audit"],
    },
    z.strictObject({ to_membership_id: Uuid, reason: StatusAskEscalation }),
  ),
  defineEvent(
    "status_ask.declined",
    {
      ...agents,
      subject: "status_ask",
      summary: "The human a status ask escalated to declined it",
      consumers: ["rt", "audit"],
    },
    z.strictObject({ by_membership_id: Uuid, reason: z.string().max(500).nullable() }),
  ),
  defineEvent(
    "topic.created",
    {
      ...chat,
      subject: "topic",
      summary: "Topic created (first message or explicit)",
      consumers: ["rt", "inbox (followers)", "search"],
    },
    z.strictObject({ channel_id: Uuid, name: z.string(), thread_id: Uuid }),
  ),
  defineEvent(
    "topic.renamed",
    { ...chat, subject: "topic", summary: "Topic renamed", consumers: ["rt", "search"] },
    z.strictObject({ channel_id: Uuid, from: z.string(), to: z.string() }),
  ),
  defineEvent(
    "topic.resolved",
    {
      ...chat,
      subject: "topic",
      summary: "Topic resolved (✓) or reopened",
      consumers: ["rt", "inbox (followers)"],
    },
    z.strictObject({ channel_id: Uuid, resolved: z.boolean() }),
  ),
  defineEvent(
    "topic.pinned",
    {
      ...chat,
      subject: "topic",
      summary: "Topic pinned to the top of its channel, or unpinned (CHAT-UX)",
      consumers: ["rt"],
    },
    z.strictObject({ channel_id: Uuid, pinned: z.boolean() }),
  ),
  defineEvent(
    "topic.archived",
    {
      ...chat,
      subject: "topic",
      summary: "Topic archived (hidden from the lists) or restored (CHAT-UX)",
      consumers: ["rt", "search"],
    },
    z.strictObject({ channel_id: Uuid, archived: z.boolean() }),
  ),
  defineEvent(
    "topic.deleted",
    {
      ...chat,
      subject: "topic",
      summary: "Topic deleted by a manager: its messages are tombstones (CHAT-UX)",
      consumers: ["rt", "search", "recall (tombstone)"],
    },
    z.strictObject({ channel_id: Uuid, name: z.string(), thread_id: Uuid }),
  ),
  defineEvent(
    "topic.moved",
    {
      ...chat,
      subject: "topic",
      summary: "Topic moved to another channel",
      consumers: ["rt", "search"],
    },
    z.strictObject({ from_channel_id: Uuid, to_channel_id: Uuid }),
  ),
  defineEvent(
    "topic.split",
    {
      ...chat,
      subject: "topic",
      summary: "Selected messages moved into a new topic",
      consumers: ["rt", "search"],
    },
    z.strictObject({ new_topic_id: Uuid, message_ids: z.array(Uuid).min(1) }),
  ),
  defineEvent(
    "topic.member_changed",
    {
      ...chat,
      subject: "topic",
      summary:
        "Someone was added to a topic or left it (CHAT-TOPICS-SEPARATE): who is notified of it, who sees a private one",
      consumers: ["rt"],
    },
    z.strictObject({
      channel_id: Uuid,
      membership_id: Uuid,
      op: z.enum(["added", "removed"]),
      via: TopicMemberVia.nullable(),
    }),
  ),
  defineEvent(
    "topic.privacy_changed",
    {
      ...chat,
      subject: "topic",
      summary: "A topic became private (members only) or open (CHAT-TOPICS-SEPARATE)",
      consumers: ["rt", "search"],
    },
    z.strictObject({ channel_id: Uuid, is_private: z.boolean() }),
  ),
  defineEvent(
    "message.created",
    {
      ...chat,
      subject: "message",
      summary: "Message posted (idempotent on client_id)",
      consumers: ["rt", "proj.inbox", "search", "recall", "digest"],
    },
    z.strictObject({
      thread_id: Uuid,
      thread_kind: ThreadKind,
      channel_id: Uuid.nullable(),
      topic_id: Uuid.nullable(),
      author_membership_id: Uuid.nullable(),
      body_text: z.string(),
      mentions: z.array(z.string()),
      card_refs: z.array(z.string()),
      parent_message_id: Uuid.nullable(),
      created_at: Timestamp,
      /**
       * CHAT-SWITCH-A: `@channel` (every person in the channel) or `@here` (those of them active
       * when it was posted), with the people it reached, decided when posted so a rebuild of the
       * inbox gives the same items. Absent: no broadcast.
       */
      broadcast: z
        .strictObject({ kind: z.enum(["channel", "here"]), recipients: z.array(Uuid) })
        .optional(),
    }),
  ),
  defineEvent(
    "message.edited",
    {
      ...chat,
      subject: "message",
      summary: "Message edited inside the edit window",
      consumers: ["rt", "search", "recall"],
    },
    z.strictObject({
      thread_id: Uuid,
      created_at: Timestamp,
      body_text: z.string(),
      mentions: z.array(z.string()),
      edited_at: Timestamp,
    }),
  ),
  defineEvent(
    "message.deleted",
    {
      ...chat,
      subject: "message",
      summary: "Message deleted (tombstone)",
      consumers: ["rt", "search", "recall (tombstone)"],
    },
    z.strictObject({ thread_id: Uuid, created_at: Timestamp, deleted_at: Timestamp }),
  ),
  defineEvent(
    "message.pin_changed",
    { ...chat, subject: "message", summary: "Message pinned or unpinned", consumers: ["rt"] },
    z.strictObject({ thread_id: Uuid, created_at: Timestamp, pinned: z.boolean() }),
  ),
  // M2-06: the caller's channel settings and a channel's agent rules (saved messages: M2-02's
  // `message.bookmark_changed`, below).
  defineEvent(
    "channel.prefs_changed",
    {
      ...chat,
      subject: "channel",
      summary: "A member joined, left, muted or re-levelled a channel for themselves",
      consumers: ["proj.inbox", "rt"],
    },
    z.strictObject({
      membership_id: Uuid,
      joined: z.boolean(),
      muted: z.boolean(),
      notify: z.enum(["all", "mentions", "none"]),
    }),
  ),
  // CHAT-PRESENCE: a member closed a DM in their own list (by hand, or "Clean up" of quiet ones),
  // or opened a closed one again. A new message opens it with no event (the list reads it).
  defineEvent(
    "channel.dm_closed",
    {
      ...chat,
      subject: "channel",
      summary: "A member closed a DM in their own list (its history is kept)",
      consumers: ["rt"],
    },
    z.strictObject({ membership_id: Uuid, quiet_days: z.int().min(1).nullable() }),
  ),
  defineEvent(
    "channel.dm_reopened",
    {
      ...chat,
      subject: "channel",
      summary: "A member opened a DM they had closed",
      consumers: ["rt"],
    },
    z.strictObject({ membership_id: Uuid }),
  ),
  // CHAT-PRESENCE: someone saw a stuck DE's failure ("Got it"); its ring stops showing it.
  defineEvent(
    "run.failure_acknowledged",
    {
      ...chat,
      subject: "run",
      summary: "A person acknowledged a DE's failed run (the DE's ring stops showing it stuck)",
      consumers: ["rt", "audit"],
    },
    z.strictObject({ membership_id: Uuid, de_membership_id: Uuid }),
  ),
  defineEvent(
    "channel.agent_rules_changed",
    {
      ...chat,
      subject: "channel",
      summary: "A channel's agent wake rule or standing instructions changed",
      consumers: ["wakeup", "rt"],
    },
    z.strictObject({
      wake: z.enum(["mentions", "topic", "silent"]),
      instructions_changed: z.boolean(),
    }),
  ),
  defineEvent(
    "message.bookmark_changed",
    {
      ...chat,
      subject: "message",
      summary: "A member bookmarked a message or removed the bookmark (private to that member)",
      consumers: ["rt"],
    },
    z.strictObject({ thread_id: Uuid, membership_id: Uuid, bookmarked: z.boolean() }),
  ),
  defineEvent(
    "reaction.added",
    { ...chat, subject: "message", summary: "Reaction added", consumers: ["rt"] },
    z.strictObject({ thread_id: Uuid, membership_id: Uuid, emoji: z.string() }),
  ),
  defineEvent(
    "reaction.removed",
    { ...chat, subject: "message", summary: "Reaction removed", consumers: ["rt"] },
    z.strictObject({ thread_id: Uuid, membership_id: Uuid, emoji: z.string() }),
  ),
  defineEvent(
    "attachment.uploaded",
    {
      ...work,
      subject: "attachment",
      summary: "Upload registered (pending until scanned)",
      consumers: ["virus-scan worker", "search (text extraction)"],
    },
    z.strictObject({
      object_key: z.string(),
      mime: z.string(),
      bytes: z.int().min(1),
      subject_kind: AttachmentSubjectKind,
      subject_id: Uuid,
    }),
  ),
  // CHAT-FILES: a chat file's life after its upload (the audit trail of who sent, removed and
  // saved what; reads go through the authenticated proxy and an agent's are run steps).
  defineEvent(
    "attachment.attached",
    {
      ...chat,
      subject: "message",
      summary: "Staged chat files were sent with a message (CHAT-FILES)",
      consumers: ["rt"],
    },
    z.strictObject({
      thread_id: Uuid,
      attachment_ids: z.array(Uuid).min(1),
      file_names: z.array(z.string()),
    }),
  ),
  defineEvent(
    "attachment.removed",
    {
      ...chat,
      subject: "attachment",
      summary:
        "A chat file was removed: discarded or expired before sending, or trashed with its deleted message, topic or channel (CHAT-FILES)",
      consumers: ["rt"],
    },
    z.strictObject({
      /** Null: a file staged for a topic not made yet, discarded. */
      thread_id: Uuid.nullable(),
      /** The message it was on; null for a staged file discarded before sending. */
      message_id: Uuid.nullable(),
      file_name: z.string(),
      /** expired: staged and never sent; source_deleted: its topic or channel was deleted. */
      reason: z.enum(["discarded", "message_deleted", "expired", "source_deleted"]),
    }),
  ),
  defineEvent(
    "attachment.saved",
    {
      ...chat,
      subject: "attachment",
      summary:
        "A copy of a chat file was saved onto a task, with its provenance (CHAT-FILES; the copy is independent)",
      consumers: ["rt"],
    },
    z.strictObject({
      thread_id: Uuid,
      message_id: Uuid,
      copy_id: Uuid,
      task_id: Uuid,
      file_name: z.string(),
    }),
  ),
  defineEvent(
    "follow.changed",
    {
      ...work,
      subject: "membership",
      summary: "A member followed, re-levelled or unfollowed a subject",
      consumers: ["proj.inbox"],
    },
    z.strictObject({
      subject_kind: FollowSubjectKind,
      subject_id: Uuid,
      level: FollowLevel.nullable(),
      op: z.enum(["followed", "updated", "unfollowed"]),
    }),
  ),
  defineEvent(
    "inbox_item.created",
    {
      ...work,
      subject: "inbox_item",
      summary: "Inbox item projected (or coalesced into a live one)",
      consumers: ["rt", "cp membership_counts", "push.inbox"],
    },
    z.strictObject({
      membership_id: Uuid,
      kind: InboxItemKind,
      subject_kind: InboxSubjectKind,
      subject_id: Uuid,
      source_event_id: Uuid,
      count: z.int().min(1),
    }),
  ),
  // SEARCH-NOTIF: a person's own notification preferences (06 §Notification preferences).
  defineEvent(
    "notification_prefs.changed",
    {
      ...work,
      subject: "membership",
      summary: "A person changed their notification preferences (channels, quiet hours, digest)",
      consumers: ["proj.inbox", "inbox.digest", "rt"],
    },
    z.strictObject({
      /** The kinds whose channels changed. */
      kinds: z.array(z.string()),
      quiet_hours: z.boolean(),
      digest_frequency: DigestFrequency,
      /** INBOX-PREFS: the rows (notification types) whose routing changed. */
      categories: z.array(z.string()).optional(),
      /** INBOX-PREFS: the person's preset (null: the company's default). */
      preset: z.string().nullable().optional(),
      /** INBOX-PREFS: alerts and the digest are paused until then (null: not paused). */
      paused_until: Timestamp.nullable().optional(),
      /** INBOX-PREFS: the per-item overrides that changed (`task:<id>` / `agent:<id>`). */
      overrides: z.array(z.string()).optional(),
    }),
  ),
  // DE-REPLY-STYLE: how long a person wants DEs' chat replies (set by them, or by a DE they told).
  defineEvent(
    "reply_style.changed",
    {
      ...work,
      subject: "membership",
      summary: "A person's DE reply length (short, normal or detailed) or quick replies changed",
      consumers: [],
    },
    z.strictObject({
      length: ReplyLength,
      /** `settings`: the person chose it; `agent`: a DE recorded what the person told it. */
      via: z.enum(["settings", "agent"]),
      /** DE-QUICK-ACK: the instant first reply ("On it — …") was turned on or off (settings). */
      quick_replies: z.boolean().optional(),
      /** CHAT-COST-PILLS: the cost labels in chat were turned on or off (settings). */
      show_costs: z.boolean().optional(),
    }),
  ),
  // INBOX-PREFS: a waiting item's one gentle reminder, then its hand-on to whoever acts next.
  defineEvent(
    "inbox_item.nudged",
    {
      ...work,
      subject: "inbox_item",
      summary:
        "A waiting item (an approval, a question a DE needs) waited past the company's nudge hours: one gentle reminder",
      consumers: ["rt", "push.inbox"],
    },
    z.strictObject({
      membership_id: Uuid,
      kind: InboxItemKind,
      subject_kind: InboxSubjectKind,
      subject_id: Uuid,
      waiting_since: Timestamp,
      alert: z.boolean(),
    }),
  ),
  defineEvent(
    "inbox_item.escalation_due",
    {
      ...work,
      subject: "inbox_item",
      summary:
        "A waiting item is still unanswered after its reminder: the DE waiting on it escalates to whoever it reports to, or marks itself blocked (the hook; no further pings)",
      consumers: [],
    },
    z.strictObject({
      /** The person the item waits on. */
      membership_id: Uuid,
      /** Who is waiting (a DE, a person), when known. */
      waiting_membership_id: Uuid.nullable(),
      kind: InboxItemKind,
      subject_kind: InboxSubjectKind,
      subject_id: Uuid,
      waiting_since: Timestamp,
      nudged_at: Timestamp,
    }),
  ),
  // CHAT-SWITCH-A: a person's custom status (Slack's status) or reported time zone.
  defineEvent(
    "member_status.changed",
    {
      ...chat,
      subject: "membership",
      summary: "A person set or cleared their status (emoji, text, expiry) or their time zone",
      consumers: ["rt"],
    },
    z.strictObject({
      emoji: z.string().nullable(),
      text: z.string().nullable(),
      expires_at: Timestamp.nullable(),
      time_zone: z.string().nullable(),
    }),
  ),
  // CHAT-SWITCH-B: "Ask" on the search page. The payload names the messages the DE was given
  // (ids only, never their text): the audit of what an agent read on whose behalf.
  defineEvent(
    "chat_ask.requested",
    {
      ...chat,
      subject: "chat_ask",
      summary: "A person asked a DE to answer from the chat history they can read",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      asker_membership_id: Uuid,
      de_membership_id: Uuid,
      source_message_ids: z.array(Uuid),
    }),
  ),
  defineEvent(
    "chat_ask.answered",
    {
      ...chat,
      subject: "chat_ask",
      summary: "The DE answered an ask, citing messages of its sources",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      asker_membership_id: Uuid,
      de_membership_id: Uuid,
      cited_message_ids: z.array(Uuid),
    }),
  ),
  // CHAT-SWITCH-B: a channel's incoming webhooks (audit: who made, rotated or revoked a URL).
  defineEvent(
    "chat_webhook.created",
    {
      ...chat,
      subject: "chat_webhook",
      summary:
        "A channel manager (channel target) or an admin (DE target) added an incoming webhook (its URL shown once)",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      channel_id: Uuid.nullable(),
      de_membership_id: Uuid.nullable(),
      name: z.string(),
      token_hint: z.string(),
    }),
  ),
  defineEvent(
    "chat_webhook.rotated",
    {
      ...chat,
      subject: "chat_webhook",
      summary: "An incoming webhook got a new URL (the old one stopped)",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      channel_id: Uuid.nullable(),
      de_membership_id: Uuid.nullable(),
      token_hint: z.string(),
    }),
  ),
  defineEvent(
    "chat_webhook.revoked",
    {
      ...chat,
      subject: "chat_webhook",
      summary: "An incoming webhook was turned off for good",
      consumers: ["rt", "audit"],
    },
    z.strictObject({ channel_id: Uuid.nullable(), de_membership_id: Uuid.nullable() }),
  ),
  // CHAT-SWITCH-B: "Send later" (the author's own; the post itself is `message.created`).
  defineEvent(
    "scheduled_message.created",
    {
      ...chat,
      subject: "scheduled_message",
      summary: "A person scheduled a message to send later",
      consumers: ["rt"],
    },
    z.strictObject({ thread_id: Uuid, channel_id: Uuid, send_at: Timestamp }),
  ),
  defineEvent(
    "scheduled_message.updated",
    {
      ...chat,
      subject: "scheduled_message",
      summary: "A person changed a scheduled message's text or time",
      consumers: ["rt"],
    },
    z.strictObject({ send_at: Timestamp, body_changed: z.boolean() }),
  ),
  defineEvent(
    "scheduled_message.cancelled",
    {
      ...chat,
      subject: "scheduled_message",
      summary: "A person cancelled a scheduled message",
      consumers: ["rt"],
    },
    z.strictObject({ thread_id: Uuid }),
  ),
  defineEvent(
    "scheduled_message.sent",
    {
      ...chat,
      subject: "scheduled_message",
      summary: "A scheduled message was posted at its time (or could not be: why)",
      consumers: ["rt"],
    },
    z.strictObject({
      message_id: Uuid.nullable(),
      failure: z.string().nullable(),
    }),
  ),
  // CHAT-SWITCH-B: the author removed a link card from their message.
  defineEvent(
    "message.preview_hidden",
    {
      ...chat,
      subject: "message",
      summary: "A message's author removed one of its link cards",
      consumers: ["rt"],
    },
    z.strictObject({ thread_id: Uuid, url: z.string() }),
  ),
  // HARDEN-pwa: a person's browsers subscribed to Web Push (the `desktop` channel). The payload
  // never carries the endpoint (a capability URL at the push service) or the browser's keys.
  defineEvent(
    "push_subscription.added",
    {
      ...work,
      subject: "membership",
      summary: "A person subscribed a browser to desktop notifications (Web Push)",
      consumers: ["rt"],
    },
    z.strictObject({ subscription_id: Uuid, push_service: z.string() }),
  ),
  defineEvent(
    "push_subscription.removed",
    {
      ...work,
      subject: "membership",
      summary:
        "A browser's Web Push subscription ended: unsubscribed by the person, gone at the push service, or failing",
      consumers: ["rt"],
    },
    z.strictObject({
      subscription_id: Uuid,
      push_service: z.string(),
      reason: z.enum(["unsubscribed", "gone", "failing"]),
    }),
  ),
  // SCREENS-S1 (docs/design/2026-10-28-screens-stack-contract.md): the company stack, browser
  // logins and their grants, and screen leases. Payloads never carry a URL, a credential, what a
  // person typed, page content or a hand-back note's words (the note stays on the lease row).
  defineEvent(
    "screen_stack.registered",
    {
      ...agents,
      subject: "screen_stack",
      summary: "ClawPanel registered (or re-registered) the company's screens stack",
      consumers: ["rt"],
    },
    z.strictObject({
      stack_id: Uuid,
      name: z.string(),
      placement: z.enum(["shared", "dedicated"]),
      credential_version: z.int().min(1),
    }),
  ),
  defineEvent(
    "screen_stack.health_changed",
    {
      ...agents,
      subject: "screen_stack",
      summary: "The screens stack's health or state changed (live, stale, retired)",
      consumers: ["rt"],
    },
    z.strictObject({
      stack_id: Uuid,
      state: z.enum(["live", "stale", "retired"]),
      health: z.strictObject({ browsers: z.string(), desktops: z.string(), live: z.string() }),
    }),
  ),
  defineEvent(
    "browser_account.created",
    {
      ...agents,
      subject: "browser_account",
      summary: "A person added one of their logins (a browser account) to the company",
      consumers: ["rt"],
    },
    z.strictObject({
      account_id: Uuid,
      handle: z.string(),
      owner_membership_id: Uuid,
      /** PERSISTENT-SCREENS: a browser login or a desktop home (absent: browser). */
      kind: z.enum(["browser", "desktop"]).optional(),
    }),
  ),
  defineEvent(
    "browser_account.revoked",
    {
      ...agents,
      subject: "browser_account",
      summary: "A login was removed: its screens ended and its profile is wiped on the stack",
      consumers: ["rt"],
    },
    z.strictObject({ account_id: Uuid, handle: z.string() }),
  ),
  defineEvent(
    "browser_account.granted",
    {
      ...agents,
      subject: "browser_account",
      summary: "A login was lent to a DE or a seat (use_read, use_act or manage)",
      consumers: ["rt"],
    },
    z.strictObject({
      account_id: Uuid,
      grant_id: Uuid,
      subject_kind: z.enum(["membership", "seat"]),
      subject_id: Uuid,
      level: z.enum(["use_read", "use_act", "manage"]),
    }),
  ),
  defineEvent(
    "browser_account.grant_revoked",
    {
      ...agents,
      subject: "browser_account",
      summary: "A login's grant was taken back",
      consumers: ["rt"],
    },
    z.strictObject({ account_id: Uuid, grant_id: Uuid }),
  ),
  defineEvent(
    "browser_account.updated",
    {
      ...agents,
      subject: "browser_account",
      summary: "A login was renamed or its 'use for' note changed (SAVED-LOGIN-SHARING)",
      consumers: ["rt"],
    },
    z.strictObject({
      account_id: Uuid,
      fields: z.array(z.enum(["label", "use_for"])).min(1),
    }),
  ),
  defineEvent(
    "browser_account.status_changed",
    {
      ...agents,
      subject: "browser_account",
      summary: "A login needs its owner (login wall, 2FA) or is live again after a hand-back",
      consumers: ["rt"],
    },
    z.strictObject({
      account_id: Uuid,
      status: z.enum(["live", "needs_you", "expired", "revoked"]),
      previous: z.enum(["live", "needs_you", "expired", "revoked"]),
    }),
  ),
  defineEvent(
    "screen_lease.queued",
    {
      ...agents,
      subject: "screen_lease",
      summary: "A browser or desktop was asked for and waits in the queue (the stack is full)",
      consumers: ["rt"],
    },
    z.strictObject({
      lease_id: Uuid,
      kind: z.enum(["browser", "desktop"]),
      holder_membership_id: Uuid,
      holder_kind: z.enum(["de", "ag", "person"]),
      account_id: Uuid.nullable(),
      task_id: Uuid.nullable(),
      origin: z.enum(["person", "agent"]),
    }),
  ),
  defineEvent(
    "screen_lease.started",
    {
      ...agents,
      subject: "screen_lease",
      summary: "A browser or desktop went live for a DE, an AG or a person",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      lease_id: Uuid,
      kind: z.enum(["browser", "desktop"]),
      holder_membership_id: Uuid,
      holder_kind: z.enum(["de", "ag", "person"]),
      account_id: Uuid.nullable(),
      task_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "screen_lease.ended",
    {
      ...agents,
      subject: "screen_lease",
      summary:
        "A screen ended: released, its task ended, idle, stopped by a person, or the stack is gone",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      lease_id: Uuid,
      reason: z.enum(["released", "task_ended", "idle", "stopped", "stack_gone", "failed"]),
    }),
  ),
  defineEvent(
    "screen_lease.taken_over",
    {
      ...agents,
      subject: "screen_lease",
      summary: "A person took over a screen; its agent pauses",
      consumers: ["rt", "audit"],
    },
    z.strictObject({ lease_id: Uuid, by_membership_id: Uuid }),
  ),
  defineEvent(
    "screen_lease.handed_back",
    {
      ...agents,
      subject: "screen_lease",
      summary: "A person handed a screen back to its agent (with or without a note)",
      consumers: ["rt", "audit"],
    },
    z.strictObject({ lease_id: Uuid, by_membership_id: Uuid, with_note: z.boolean() }),
  ),
  defineEvent(
    "screen_lease.needs_you",
    {
      ...agents,
      subject: "screen_lease",
      summary: "An agent hit a login wall, 2FA or a CAPTCHA and waits on a person",
      consumers: ["rt"],
    },
    z.strictObject({
      lease_id: Uuid,
      account_id: Uuid.nullable(),
      why: z.enum(["login", "2fa", "captcha", "other"]),
      /** SCREENS-NEEDS-YOU: the ask (its chat card) this request opened. */
      ask_id: Uuid.optional(),
    }),
  ),
  defineEvent(
    "screen_ask.resolved",
    {
      ...agents,
      subject: "screen_ask",
      summary:
        "A needs-you ask closed: done (handed back), not now, expired (nobody acted in time) or ended with its screen",
      consumers: ["rt"],
    },
    z.strictObject({
      ask_id: Uuid,
      lease_id: Uuid,
      state: z.enum(["done", "declined", "expired", "ended"]),
      by_membership_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "screen_ask.extended",
    {
      ...agents,
      subject: "screen_ask",
      summary:
        "A person kept a screen waiting on them open longer (Extend on a needs-you ask): its expiry moved",
      consumers: ["rt"],
    },
    z.strictObject({
      ask_id: Uuid,
      lease_id: Uuid,
      expires_at: Timestamp,
      by_membership_id: Uuid,
    }),
  ),
  defineEvent(
    "screen_lease.bumped",
    {
      ...agents,
      subject: "screen_lease",
      summary: "An admin moved a waiting screen to the front of the queue",
      consumers: ["rt"],
    },
    z.strictObject({ lease_id: Uuid, priority: z.int() }),
  ),
  defineEvent(
    "inbox_item.read",
    {
      ...work,
      subject: "inbox_item",
      summary: "Inbox item read or marked unread",
      consumers: ["rt", "cp membership_counts"],
    },
    z.strictObject({ membership_id: Uuid, read: z.boolean() }),
  ),
  defineEvent(
    "inbox_item.done",
    {
      ...work,
      subject: "inbox_item",
      summary: "Inbox item done or undone",
      consumers: ["rt", "cp membership_counts"],
    },
    z.strictObject({ membership_id: Uuid, done: z.boolean() }),
  ),
  defineEvent(
    "inbox_item.read_all",
    {
      ...work,
      subject: "membership",
      summary: "Every unread item of a member (in a filter) marked read",
      consumers: ["rt", "cp membership_counts"],
    },
    z.strictObject({ filter: z.string().nullable(), count: z.int().min(0), before: Timestamp }),
  ),
  defineEvent(
    "inbox_item.snoozed",
    {
      ...work,
      subject: "inbox_item",
      summary: "Inbox item snoozed until a time, or woken (M2-04)",
      consumers: ["rt", "cp membership_counts"],
    },
    z.strictObject({ membership_id: Uuid, until: Timestamp.nullable() }),
  ),
  defineEvent(
    "board.created",
    {
      ...work,
      subject: "board",
      summary: "Board created with its home channel",
      consumers: ["channel auto-create", "search"],
    },
    z.strictObject({ space_id: Uuid, name: z.string(), home_channel_id: Uuid.nullable() }),
  ),
  defineEvent(
    "board.updated",
    {
      ...work,
      subject: "board",
      summary: "Board renamed, statuses, settings, digest, colour or lead changed",
      consumers: ["rt", "search", "digest"],
    },
    z.strictObject({
      changes: changesOf({
        name: z.string(),
        space_id: Uuid,
        status_set: z.array(StatusDef),
        settings: BoardSettings,
        digest_interval: BoardDigestInterval,
        color: BoardColor,
        lead_seat_id: Uuid,
        /** T6-BOARD-NOTE: the board's note page, set by its first write. */
        page_id: Uuid,
      }),
      version: z.int().min(1),
    }),
  ),
  defineEvent(
    "board.moved",
    {
      ...work,
      subject: "board",
      summary: "Board reordered in its space or moved to another space (BOARD-ORDER)",
      consumers: ["rt", "search"],
    },
    z.strictObject({
      from_space_id: Uuid,
      space_id: Uuid,
      before_board_id: Uuid.nullable(),
      board_ids: z.array(Uuid).describe("The target space's live boards in their new order"),
      version: z.int().min(1),
    }),
  ),
  defineEvent(
    "board.archived",
    {
      ...work,
      subject: "board",
      summary: "Board archived (tasks kept)",
      consumers: ["rt", "search"],
    },
    z.strictObject({ space_id: Uuid }),
  ),
  // ACTIONS: back from the archive (in its old place), with the home channel its archive took.
  defineEvent(
    "board.unarchived",
    {
      ...work,
      subject: "board",
      summary: "Board restored from the archive",
      consumers: ["rt", "search"],
    },
    z.strictObject({ space_id: Uuid, restored_channel_ids: z.array(Uuid) }),
  ),
  defineEvent(
    "view.created",
    { ...work, subject: "view", summary: "Saved view created", consumers: ["rt"] },
    z.strictObject({ board_id: Uuid, kind: ViewKind, name: z.string() }),
  ),
  defineEvent(
    "view.updated",
    { ...work, subject: "view", summary: "Saved view changed", consumers: ["rt"] },
    z.strictObject({
      board_id: Uuid,
      changes: changesOf({
        name: z.string(),
        kind: ViewKind,
        filter: ViewFilter,
        sort: ViewSort,
        group_by: z.string(),
        visible_fields: z.array(z.string()),
        date_field: z.string().nullable(),
        card_fields: z.array(z.string()).nullable(),
        is_default: z.boolean(),
        position: z.int(),
      }),
    }),
  ),
  defineEvent(
    "view.deleted",
    { ...work, subject: "view", summary: "Saved view deleted", consumers: ["rt"] },
    z.strictObject({ board_id: Uuid }),
  ),
  // B4-ITEM-TEMPLATES: a board's item templates (save a task as one; create tasks from it)
  defineEvent(
    "item_template.created",
    {
      ...work,
      subject: "item_template",
      summary: "An item template was saved on a board (from a task, or written outright)",
      consumers: ["rt"],
    },
    z.strictObject({ board_id: Uuid, name: z.string(), source_task_id: Uuid.nullable() }),
  ),
  defineEvent(
    "item_template.updated",
    {
      ...work,
      subject: "item_template",
      summary: "An item template was renamed or its task shape changed",
      consumers: ["rt"],
    },
    z.strictObject({
      board_id: Uuid,
      changes: changesOf({
        name: z.string(),
        description: z.string().nullable(),
        payload: z.record(z.string(), z.unknown()),
        position: z.int(),
      }),
      version: z.int().min(1),
    }),
  ),
  defineEvent(
    "item_template.archived",
    {
      ...work,
      subject: "item_template",
      summary: "An item template was archived (tasks made from it keep their link)",
      consumers: ["rt"],
    },
    z.strictObject({ board_id: Uuid, name: z.string() }),
  ),
  // CLOSED-LOOPS (B3): one commitment per request in any direction (domain/commitments)
  defineEvent(
    "commitment.created",
    {
      ...work,
      subject: "commitment",
      summary:
        "A request was asked (person, DE or helper, either way) or a decision became owed: its owner, due time and how it is verified",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      kind: CommitmentKind,
      request_type: z.string(),
      state: CommitmentState,
      requester_membership_id: Uuid,
      owner_membership_id: Uuid,
      parent_id: Uuid.nullable(),
      due_at: Timestamp,
    }),
  ),
  defineEvent(
    "commitment.changed",
    {
      ...work,
      subject: "commitment",
      summary:
        "A commitment moved: accepted, progressed, handed in, verified, failed with options, decided, withdrawn, expired, or a rung of its escalation ladder was taken",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      action: z.string().max(40),
      from: CommitmentState,
      to: CommitmentState,
      rung: z.int().min(0).nullable(),
    }),
  ),
  // BIZ-PLAYBOOKS: what makes an SOP page a playbook (owner seat, follower seats, run templates)
  defineEvent(
    "playbook.links_changed",
    {
      ...work,
      subject: "page",
      summary:
        "A playbook's owner seat, follower seats, run templates or owner-approval setting were set",
      consumers: ["rt"],
    },
    z.strictObject({
      owner_seat_id: Uuid.nullable(),
      follower_seat_ids: z.array(Uuid),
      item_template_ids: z.array(Uuid),
      /** BIZ-PLAYBOOKS (0161): a DE's change needs the owner's approval. */
      owner_approval: z.boolean().optional(),
    }),
  ),
  // BIZ-CLIENT-HEALTH: a survey about a client sent, and its answer (never the token or link)
  defineEvent(
    "client_survey.sent",
    {
      ...work,
      subject: "client_survey",
      summary: "An NPS or CSAT survey about a client was sent (mailed when it has an email)",
      consumers: ["rt", "mail.client_survey"],
    },
    z.strictObject({
      space_id: Uuid,
      kind: z.enum(["nps", "csat"]),
      mailed: z.boolean(),
      approval_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "client_survey.answered",
    {
      ...work,
      subject: "client_survey",
      summary: "A client answered a survey (a detractor's answer raises a client issue)",
      consumers: ["rt"],
    },
    z.strictObject({
      space_id: Uuid,
      kind: z.enum(["nps", "csat"]),
      score: z.int(),
      detractor: z.boolean(),
      issue_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "field_def.created",
    {
      ...work,
      subject: "field_def",
      summary: "Board field added",
      consumers: ["rt", "search mapping"],
    },
    z.strictObject({ board_id: Uuid, name: z.string(), type: FieldType, audience: FieldAudience }),
  ),
  defineEvent(
    "field_def.updated",
    {
      ...work,
      subject: "field_def",
      summary: "Board field changed",
      consumers: ["rt", "search mapping"],
    },
    z.strictObject({
      board_id: Uuid,
      changes: changesOf({
        name: z.string(),
        options: FieldOptions,
        audience: FieldAudience,
        requires_approval: z.boolean(),
        position: z.int(),
      }),
    }),
  ),
  defineEvent(
    "field_def.deleted",
    {
      ...work,
      subject: "field_def",
      summary: "Board field removed with its values",
      consumers: ["rt", "search mapping"],
    },
    z.strictObject({ board_id: Uuid, name: z.string() }),
  ),
  defineEvent(
    "task.created",
    {
      ...work,
      subject: "task",
      summary: "Task created (API, scheduler, message, agent, template, import)",
      consumers: [
        "rt",
        "proj.inbox",
        "digest",
        "proj.kpi",
        "search",
        "recall",
        "wakeup (DE assignee)",
      ],
    },
    z.strictObject({
      board_id: Uuid,
      number: z.int().min(1),
      title: z.string(),
      status: z.string(),
      priority: z.int().min(0).max(4),
      parent_task_id: Uuid.nullable(),
      origin: TaskOrigin,
      origin_ref: TaskOriginRef.nullable(),
      assignee_seat_ids: z.array(Uuid),
      due_at: Timestamp.nullable(),
      /**
       * The page body given at creation (M2-03 stub, 04 `body` / `body_markdown`): the task's
       * page is created from it when pages land (M7-02). Absent when none was given.
       */
      body: BlockNoteDoc.optional(),
      body_text: z.string().optional(),
    }),
  ),
  defineEvent(
    "task.updated",
    {
      ...work,
      subject: "task",
      summary: "Title, dates, estimate, parent or restriction changed",
      consumers: ["rt", "digest", "search"],
    },
    z.strictObject({
      board_id: Uuid,
      changes: changesOf({
        title: z.string(),
        start_at: Timestamp,
        estimate_minutes: z.int(),
        parent_task_id: Uuid,
        restricted: z.boolean(),
      }),
      version: z.int().min(1),
    }),
  ),
  defineEvent(
    "task.status_changed",
    {
      ...work,
      subject: "task",
      summary: "Task status changed",
      consumers: ["rt", "proj.inbox", "digest", "proj.kpi", "recall (outcome)"],
    },
    z.strictObject({
      board_id: Uuid,
      from: z.string(),
      to: z.string(),
      completed: z.boolean(),
      // AGENT-RECORD: whether the task was done before the move (absent on older events), so a
      // move into done is known from the event itself, whatever the board's statuses become.
      was_completed: z.boolean().optional(),
      // B2-FIXES: a DE's move through its owner's `hold` gate: the owner may pull it back until
      // then, so the usual notifications wait for `task.gate_passed` (only the quiet line now)
      held_until: Timestamp.optional(),
      // BOARD-GATES: which control made the move (absent on older events and plain moves)
      via: z.string().max(32).optional(),
      // BOARD-GATES §2.3: a person's decision on a column only a person moves out of (the
      // Approve · Rework · Reject buttons; a drag out of the column is `decision: null`)
      review: z
        .strictObject({
          decision: z.enum(["approve", "rework", "reject"]).nullable(),
          note: z.string().max(2000).nullable(),
          note_message_id: Uuid.nullable(),
          edited: z.boolean(),
          gate_column: z.string(),
          // DECISION-LEDGER: a rework or reject the reviewer marked as wrong work (a reversal
          // of the agent's delivered work, not only a review); absent on older events
          wrong: z.boolean().optional(),
        })
        .optional(),
    }),
  ),
  defineEvent(
    "task.move_refused",
    {
      ...work,
      subject: "task",
      summary:
        "An agent tried to move a card out of a column only a person moves out of (refused, nothing changed)",
      consumers: ["activity", "stall alert"],
    },
    z.strictObject({
      board_id: Uuid,
      from: z.string(),
      to: z.string(),
      code: z.literal("column_locked"),
    }),
  ),
  defineEvent(
    "task.assigned",
    {
      ...work,
      subject: "task",
      summary: "Seat assigned",
      consumers: ["rt", "proj.inbox", "proj.kpi (load)", "digest", "wakeup (DE seat)"],
    },
    z.strictObject({
      board_id: Uuid,
      seat_id: Uuid,
      // RUN-EFFORT: the effort the assigner asked for (absent: auto); the DE's wake-up keeps it.
      effort: RunEffortRequest.optional(),
    }),
  ),
  defineEvent(
    "task.unassigned",
    {
      ...work,
      subject: "task",
      summary: "Seat unassigned",
      consumers: ["rt", "proj.inbox", "proj.kpi (load)", "digest"],
    },
    z.strictObject({ board_id: Uuid, seat_id: Uuid }),
  ),
  defineEvent(
    "task.handed_off",
    {
      ...work,
      subject: "task",
      summary:
        "HANDOFF-LOG: the task moved from one seat to another (a handoff reassigns); the envelope's actor (and run) is who handed it on",
      consumers: ["rt"],
    },
    z.strictObject({
      board_id: Uuid,
      from_seat_id: Uuid,
      to_seat_ids: z.array(Uuid).min(1),
      reason: z.string().nullable(),
      /**
       * COS-MODES: the route line ("for Valentina · Team · Tell me"; "taken back by Maria"), when
       * an agent directed another person's agent or a head took it back.
       */
      route: z.string().nullable().optional(),
    }),
  ),
  defineEvent(
    "task.agent_directed",
    {
      ...work,
      subject: "task",
      summary:
        "COS-MODES: an agent directed another person's agent in Team mode (the routing verdict); `inform` and `proceeded` tell the head with Take back, `ask` filed a request, `escalated` sent it to the CEO",
      consumers: ["rt", "proj.inbox", "audit"],
    },
    z.strictObject({
      board_id: Uuid,
      action: z.enum(["assign", "priority", "urgent"]),
      outcome: z.enum(["inform", "ask", "proceeded", "escalated"]),
      level: z.enum(["hands_off", "inform", "ask"]),
      head_membership_id: Uuid,
      actor_membership_id: Uuid.nullable(),
      /** For whom the actor acted (its principal: the CEO for the Chief of Staff). */
      principal_membership_id: Uuid.nullable(),
      from_seat_id: Uuid.nullable(),
      to_seat_ids: z.array(Uuid),
      /** A priority or deadline change: the values before and after (Take back restores them). */
      changes: z
        .strictObject({
          priority: z.strictObject({ from: z.int(), to: z.int() }).optional(),
          due_at: z
            .strictObject({ from: Timestamp.nullable(), to: Timestamp.nullable() })
            .optional(),
        })
        .nullable(),
      reason: z.string(),
      urgent_reason: z.string().nullable(),
      approval_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "task.taken_back",
    {
      ...work,
      subject: "task",
      summary:
        "COS-MODES: a head took back what an agent directed onto their agent: the agent's run stopped and the task reassigned to the head, or the field restored",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      board_id: Uuid,
      directed_event_id: Uuid,
      by_membership_id: Uuid,
      from_seat_ids: z.array(Uuid),
      to_seat_id: Uuid.nullable(),
      restored: z.array(z.enum(["priority", "due_at"])),
    }),
  ),
  defineEvent(
    "task.handoff_parked",
    {
      ...work,
      subject: "task",
      summary:
        "COS-SLICE1: agents handed the task on three times in 24 h; it waits on a person, and no agent may hand it on until a person touches it",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      board_id: Uuid,
      handoffs: z.int().min(1),
      supervisor_seat_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "task.field_changed",
    {
      ...work,
      subject: "task",
      summary: "A field value set or cleared",
      consumers: ["rt", "digest", "proj.kpi (field metrics)"],
    },
    z.strictObject({ board_id: Uuid, field_def_id: Uuid, from: z.unknown(), to: z.unknown() }),
  ),
  defineEvent(
    "task.due_changed",
    {
      ...work,
      subject: "task",
      summary: "Due date changed",
      consumers: ["rt", "digest", "reminders"],
    },
    z.strictObject({ board_id: Uuid, from: Timestamp.nullable(), to: Timestamp.nullable() }),
  ),
  // B2-GATES: a DE's move through a `hold` gate, and its owner pulling it back.
  defineEvent(
    "task.gate_held",
    {
      ...work,
      subject: "task",
      summary:
        "A DE moved a task through a hold gate: its owner may pull it back until `until` (a quiet Inbox line)",
      consumers: ["inbox (owner)", "rt"],
    },
    z.strictObject({
      board_id: Uuid,
      hold_id: Uuid,
      from: z.string(),
      to: z.string(),
      owner_seat_id: Uuid,
      held_by_membership_id: Uuid,
      until: Timestamp,
    }),
  ),
  defineEvent(
    "task.gate_pulled",
    {
      ...work,
      subject: "task",
      summary:
        "The owner pulled a held move back (the task returned; the DE is woken with the note)",
      consumers: ["rt", "board attention"],
    },
    z.strictObject({
      board_id: Uuid,
      hold_id: Uuid,
      from: z.string(),
      to: z.string(),
      held_by_membership_id: Uuid,
      note: z.string().nullable(),
    }),
  ),
  defineEvent(
    "task.gate_passed",
    {
      ...work,
      subject: "task",
      summary:
        "A held move's window ended with the move standing: the usual status-change notifications go now",
      consumers: ["proj.inbox", "automations (the held move's triggers)", "digest", "rt"],
    },
    z.strictObject({
      board_id: Uuid,
      hold_id: Uuid,
      from_board_id: Uuid.nullable(),
      from: z.string(),
      to: z.string(),
      // whether the task is done now (the held move into a done status stands)
      completed: z.boolean(),
      held_by_membership_id: Uuid,
    }),
  ),
  defineEvent(
    "board.gates_changed",
    {
      ...work,
      subject: "board",
      summary:
        "A gate on a board transition set or removed (a person's or the board default), or the admin ceiling changed",
      consumers: ["rt"],
    },
    z.strictObject({
      /** The person's seat; null: the board default (or the ceiling). */
      seat_id: Uuid.nullable(),
      from_status: z.string().nullable(),
      to_status: z.string().nullable(),
      /** null: the gate was removed (or only the ceiling changed). */
      mode: GateMode.nullable(),
      no_trust: z.boolean().optional(),
    }),
  ),
  defineEvent(
    "task.priority_changed",
    { ...work, subject: "task", summary: "Priority changed", consumers: ["rt", "digest"] },
    z.strictObject({ board_id: Uuid, from: z.int(), to: z.int() }),
  ),
  defineEvent(
    "task.checklist_changed",
    {
      ...work,
      subject: "task",
      summary:
        "Checklist item added, edited, checked, removed or reordered; a checklist added, renamed or removed",
      consumers: ["rt", "digest"],
      version: 2,
      previous: [
        {
          version: 1,
          payload: TaskChecklistChangedV1,
          upgrade: (p) => ({
            ...(p as z.infer<typeof TaskChecklistChangedV1>),
            checklist_id: null,
          }),
        },
      ],
    },
    z.strictObject({
      board_id: Uuid,
      /** v2 (DOCS-KB): the checklist concerned (null only in upgraded v1 events). */
      checklist_id: Uuid.nullable(),
      /** The item; null for an op on the checklist itself. */
      item_id: Uuid.nullable(),
      op: z.enum([
        "added",
        "updated",
        "removed",
        "reordered",
        "list_added",
        "list_renamed",
        "list_removed",
      ]),
      done: z.boolean(),
    }),
  ),
  defineEvent(
    "task.file_changed",
    {
      ...work,
      subject: "task",
      summary:
        "A file of the task added (to the task, one of its comments or a promoted thread), renamed, moved, made or unmade a cover, trashed or restored (TASK-FILES; Work OS task.attachment_*)",
      consumers: ["rt", "digest", "automations"],
    },
    z.strictObject({
      board_id: Uuid,
      attachment_id: Uuid,
      op: z.enum([
        "added",
        "renamed",
        "moved",
        "cover_set",
        "cover_cleared",
        "trashed",
        "restored",
      ]),
      file_name: z.string(),
      /** The name before a rename; null otherwise. */
      from_name: z.string().nullable(),
      /** The folder it is in (after a move); null = the task root. */
      folder_id: Uuid.nullable(),
      mime: z.string(),
      bytes: z.int().min(1),
      /** The comment (message) the file is attached to; null for a task file. */
      message_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "task.file_folder_changed",
    {
      ...work,
      subject: "task",
      summary:
        "A folder of the task's files added, renamed, moved or removed (its files to the trash) (TASK-FILES)",
      consumers: ["rt"],
    },
    z.strictObject({
      board_id: Uuid,
      folder_id: Uuid,
      op: z.enum(["added", "renamed", "moved", "removed"]),
      name: z.string(),
      parent_id: Uuid.nullable(),
      /** Live files the removal sent to the trash (0 otherwise). */
      file_count: z.int().min(0),
    }),
  ),
  defineEvent(
    "task.trash_emptied",
    {
      ...work,
      subject: "task",
      summary:
        "The task's trashed files purged: by a person (Empty trash) or by the retention job (TASK-FILES)",
      consumers: ["rt"],
    },
    z.strictObject({
      board_id: Uuid,
      count: z.int().min(1),
      /** The purged files' names (at most 50). */
      file_names: z.array(z.string()).max(50),
    }),
  ),
  defineEvent(
    "task.dependency_changed",
    {
      ...work,
      subject: "task",
      summary: "Dependency added or removed",
      consumers: ["rt", "scheduler (hard locks)", "automations"],
    },
    z.strictObject({
      board_id: Uuid,
      depends_on_task_id: Uuid,
      kind: TaskDependencyKind,
      op: z.enum(["added", "removed"]),
    }),
  ),
  // WOS-BUNDLE: tags, links, the task timer and duplicates (Work OS `task.tagged`, `task.linked`,
  // `task.unlinked`, `task.time_logged`; `duplicateTask` / `duplicateContainerTree`). Tag and
  // link events ping the task's viewers; a link is announced on both tasks.
  defineEvent(
    "task.tagged",
    {
      ...work,
      subject: "task",
      summary: "A tag was put on the task (WOS-BUNDLE)",
      consumers: ["rt", "search"],
    },
    z.strictObject({ board_id: Uuid, tag_id: Uuid, name: z.string() }),
  ),
  defineEvent(
    "task.untagged",
    {
      ...work,
      subject: "task",
      summary: "A tag was taken off the task (WOS-BUNDLE)",
      consumers: ["rt", "search"],
    },
    z.strictObject({ board_id: Uuid, tag_id: Uuid, name: z.string() }),
  ),
  defineEvent(
    "task.linked",
    {
      ...work,
      subject: "task",
      summary: "A link to another task was added; written on both tasks (WOS-BUNDLE)",
      consumers: ["rt", "automations"],
    },
    z.strictObject({ board_id: Uuid, linked_task_id: Uuid, relation: TaskLinkRelation }),
  ),
  defineEvent(
    "task.unlinked",
    {
      ...work,
      subject: "task",
      summary: "A link to another task was removed; written on both tasks (WOS-BUNDLE)",
      consumers: ["rt"],
    },
    z.strictObject({ board_id: Uuid, linked_task_id: Uuid, relation: TaskLinkRelation }),
  ),
  defineEvent(
    "task.duplicated",
    {
      ...work,
      subject: "task",
      summary:
        "The task was copied (the copy's own `task.created` carries origin `duplicate`; WOS-BUNDLE)",
      consumers: ["rt"],
    },
    z.strictObject({
      board_id: Uuid,
      copy_task_id: Uuid,
      subtasks: z.int().min(0).describe("Subtasks copied with it"),
    }),
  ),
  defineEvent(
    "task.timer_started",
    {
      ...work,
      subject: "task",
      summary: "A person started the timer on the task (one running timer per person; WOS-BUNDLE)",
      consumers: ["rt"],
    },
    z.strictObject({ board_id: Uuid, membership_id: Uuid, started_at: Timestamp }),
  ),
  defineEvent(
    "task.timer_stopped",
    {
      ...work,
      subject: "task",
      summary:
        "A person's timer on the task stopped; the time is booked as a `time_entry` (WOS-BUNDLE)",
      consumers: ["rt"],
    },
    z.strictObject({
      board_id: Uuid,
      membership_id: Uuid,
      started_at: Timestamp,
      duration_seconds: z.int().positive(),
      time_entry_id: Uuid,
    }),
  ),
  defineEvent(
    "board.duplicated",
    {
      ...work,
      subject: "board",
      summary:
        "A board was copied (statuses, fields, views; tasks when asked); the copy's `board.created` follows (WOS-BUNDLE)",
      consumers: ["rt", "search"],
    },
    z.strictObject({ copy_board_id: Uuid, space_id: Uuid, tasks: z.int().min(0) }),
  ),
  defineEvent(
    "space.duplicated",
    {
      ...work,
      subject: "space",
      summary:
        "A space was copied with its boards (tasks when asked); the copy's `space.created` follows (WOS-BUNDLE)",
      consumers: ["rt", "search"],
    },
    z.strictObject({ copy_space_id: Uuid, boards: z.int().min(0), tasks: z.int().min(0) }),
  ),
  // WOS-BUNDLE: company tags (Settings → Tags renames, recolours, merges and deletes them).
  defineEvent(
    "tag.created",
    {
      ...work,
      subject: "tag",
      summary: "A company tag was made (explicitly or by tagging a task with a new name)",
      consumers: ["rt"],
    },
    z.strictObject({ name: z.string(), color: TagColor }),
  ),
  defineEvent(
    "tag.updated",
    {
      ...work,
      subject: "tag",
      summary: "A company tag was renamed or recoloured",
      consumers: ["rt", "search"],
    },
    z.strictObject({ changes: changesOf({ name: z.string(), color: TagColor }) }),
  ),
  defineEvent(
    "tag.merged",
    {
      ...work,
      subject: "tag",
      summary:
        "A tag was merged into another: its tasks carry the other tag and it is gone (subject: the removed tag)",
      consumers: ["rt", "search"],
    },
    z.strictObject({
      name: z.string(),
      into_tag_id: Uuid,
      into_name: z.string(),
      tasks: z.int().min(0),
    }),
  ),
  defineEvent(
    "tag.deleted",
    {
      ...work,
      subject: "tag",
      summary: "A company tag was deleted (taken off every task)",
      consumers: ["rt", "search"],
    },
    z.strictObject({ name: z.string(), tasks: z.int().min(0) }),
  ),
  // WOS-BUNDLE: a person's own arrangement — favorites, recents, My Tasks sections. The subject
  // is the person's membership, so the ping reaches that person only (live/visibility.ts).
  defineEvent(
    "favorite.added",
    {
      ...work,
      subject: "membership",
      summary: "A person starred a space, board, doc, task or channel",
      consumers: ["rt"],
    },
    z.strictObject({ kind: FavoriteKind, object_id: Uuid, position: z.int() }),
  ),
  defineEvent(
    "favorite.removed",
    {
      ...work,
      subject: "membership",
      summary: "A person unstarred something",
      consumers: ["rt"],
    },
    z.strictObject({ kind: FavoriteKind, object_id: Uuid }),
  ),
  defineEvent(
    "favorite.reordered",
    {
      ...work,
      subject: "membership",
      summary: "A person reordered its Favorites",
      consumers: ["rt"],
    },
    z.strictObject({ count: z.int().min(0) }),
  ),
  defineEvent(
    "recent.viewed",
    {
      ...work,
      subject: "membership",
      summary: "A person opened a task, doc, board or space (its recents moved; ten are kept)",
      consumers: ["rt"],
    },
    z.strictObject({ kind: RecentKind, object_id: Uuid }),
  ),
  defineEvent(
    "my_task_section.created",
    {
      ...work,
      subject: "membership",
      summary: "A person added a My Tasks section",
      consumers: ["rt"],
    },
    z.strictObject({ section_id: Uuid, name: z.string(), position: z.int() }),
  ),
  defineEvent(
    "my_task_section.updated",
    {
      ...work,
      subject: "membership",
      summary: "A person renamed or moved a My Tasks section",
      consumers: ["rt"],
    },
    z.strictObject({ section_id: Uuid, name: z.string(), position: z.int() }),
  ),
  defineEvent(
    "my_task_section.deleted",
    {
      ...work,
      subject: "membership",
      summary: "A person deleted a My Tasks section (its tasks went back to Recently assigned)",
      consumers: ["rt"],
    },
    z.strictObject({ section_id: Uuid, name: z.string(), tasks: z.int().min(0) }),
  ),
  defineEvent(
    "my_task.placed",
    {
      ...work,
      subject: "membership",
      summary: "A person moved a task to a My Tasks section (or within one)",
      consumers: ["rt"],
    },
    z.strictObject({ task_id: Uuid, section: z.string(), position: z.int() }),
  ),
  defineEvent(
    "task.moved",
    {
      ...work,
      subject: "task",
      summary: "Task moved to another board",
      consumers: ["rt", "search", "digest"],
    },
    z.strictObject({
      from_board_id: Uuid,
      to_board_id: Uuid,
      status: z.string(),
      // AGENT-RECORD: whether the task is done after the move and was before it (absent on older
      // events): a move into a done status is a completion, as a status change into done is.
      completed: z.boolean().optional(),
      was_completed: z.boolean().optional(),
      // B2-FIXES: a held move (see `task.status_changed`)
      held_until: Timestamp.optional(),
    }),
  ),
  defineEvent(
    "task.deleted",
    {
      ...work,
      subject: "task",
      summary: "Task deleted",
      consumers: ["rt", "search", "proj.inbox (cleanup)"],
    },
    z.strictObject({ board_id: Uuid, number: z.int().min(1), title: z.string() }),
  ),
  // CARD-ARCHIVE (flag `card_archive`): a soft archive. The card keeps its rows; it leaves board
  // views, agent board reads and wake dispatch. Archiving stopped the agent runs working on it,
  // dropped its queued wake-ups and pending wake timers, and closed its open loop incidents.
  defineEvent(
    "task.archived",
    {
      ...work,
      subject: "task",
      summary:
        "Task archived (soft): left out of board views, agent reads and wakes; its runs stopped",
      consumers: ["rt"],
    },
    z.strictObject({
      board_id: Uuid,
      number: z.int().min(1),
      runs_stopped: z.int().min(0).describe("Agent runs on the card that were ended"),
      wakes_dropped: z.int().min(0).describe("Queued wake-ups on the card that were dropped"),
      timers_cancelled: z.int().min(0).describe("Pending wake timers on the card, cancelled"),
      incidents_closed: z.int().min(0).describe("Open loop incidents on the card, closed"),
    }),
  ),
  defineEvent(
    "task.unarchived",
    {
      ...work,
      subject: "task",
      summary: "Task restored from the archive (nothing is woken by it)",
      consumers: ["rt"],
    },
    z.strictObject({ board_id: Uuid, number: z.int().min(1) }),
  ),

  // Knowledge pages (M7-02; 03 §Knowledge: `space_id, kind, version` and what the consumers
  // read: Recall ingest takes `team_id`, `restricted`, `title` and `body_text` of `page.edited`).
  defineEvent(
    "page.created",
    {
      ...work,
      subject: "page",
      summary: "Page created in a space (or as a task's body)",
      consumers: ["search", "rt", "inbox (followers)"],
    },
    z.strictObject({
      space_id: Uuid,
      team_id: Uuid.nullable(),
      kind: PageKind,
      version: z.int().min(1),
      title: z.string(),
      parent_page_id: Uuid.nullable(),
      restricted: z.boolean(),
      /** The task whose body this page is (`task_body`). */
      task_id: Uuid.nullable(),
      template: z.enum(["sop", "policy", "kb", "meeting"]).nullable(),
    }),
  ),
  defineEvent(
    "page.edited",
    {
      ...work,
      subject: "page",
      summary: "Page title, body, icon, kind or restriction changed (or a version restored)",
      consumers: ["search", "recall (ingest)", "rt", "inbox (followers)"],
    },
    z.strictObject({
      space_id: Uuid,
      team_id: Uuid.nullable(),
      kind: PageKind,
      version: z.int().min(1),
      title: z.string(),
      // KB-UPLOAD-2: + read_only (a doc made read only for everyone, or opened again).
      changed: z.array(z.enum(["title", "body", "icon", "kind", "restricted", "read_only"])).min(1),
      /** The body as plain text, at most 20,000 characters (Recall ingest's source). */
      body_text: z.string().max(20_000),
      restricted: z.boolean(),
      restored_from_version: z.int().min(1).nullable(),
    }),
  ),
  defineEvent(
    "page.moved",
    {
      ...work,
      subject: "page",
      summary: "Page moved under another parent, to another space or among its siblings",
      consumers: ["search", "rt"],
    },
    z.strictObject({
      space_id: Uuid,
      from_space_id: Uuid,
      parent_page_id: Uuid.nullable(),
      from_parent_page_id: Uuid.nullable(),
      position: z.int(),
      kind: PageKind,
      version: z.int().min(1),
      restricted: z.boolean(),
      /** Pages below it that moved with it (a move to another space carries the subtree). */
      descendants: z.int().min(0),
    }),
  ),
  // MULTI-SPACE-ACCESS: the spaces a page is shared into (beside its home) changed. `from` / `to`
  // are the share lists (space and level) before and after; the envelope's actor changed them.
  defineEvent(
    "page.shares_changed",
    {
      ...work,
      subject: "page",
      summary: "Page shared into more spaces, or a share's level changed or was removed",
      consumers: ["audit"],
    },
    z.strictObject({
      space_id: Uuid,
      from: z.array(z.strictObject({ space_id: Uuid, level: SpaceShareLevel })),
      to: z.array(z.strictObject({ space_id: Uuid, level: SpaceShareLevel })),
    }),
  ),
  // KB-UPLOAD-2: the people and seats (DEs) a page is shared with directly changed. `from` / `to`
  // are the lists before and after (principal and level); the envelope's actor changed them.
  defineEvent(
    "page.members_changed",
    {
      ...work,
      subject: "page",
      summary: "Page shared with people or DEs directly, or a level changed or was removed",
      consumers: ["audit"],
    },
    z.strictObject({
      space_id: Uuid,
      from: z.array(
        z.strictObject({
          principal_kind: PageMemberPrincipalKind,
          principal_id: Uuid,
          level: PageMemberLevel,
        }),
      ),
      to: z.array(
        z.strictObject({
          principal_kind: PageMemberPrincipalKind,
          principal_id: Uuid,
          level: PageMemberLevel,
        }),
      ),
    }),
  ),
  // UPLOAD-PRIVATE-FIRST (founder 2026-10-30): an uploaded document waits for its text to be read.
  // The upload (or its Retry) stores the original and returns; the worker's `upload.extract`
  // reads the text, writes the doc's body and clears the page's "Processing" state.
  defineEvent(
    "page.upload_queued",
    {
      ...work,
      subject: "page",
      summary: "An uploaded document's text is queued to be read (upload or retry)",
      consumers: ["upload.extract"],
    },
    z.strictObject({
      space_id: Uuid,
      file_name: z.string().max(255),
      mime: z.string().max(200),
      bytes: z.int().min(1),
      retry: z.boolean(),
      /**
       * UPLOAD-ORIGINAL: `true` (Replace file, Re-extract): the text read replaces the doc's as a
       * new version even when people wrote in it; otherwise only an empty doc is filled.
       */
      replace: z.boolean().optional(),
    }),
  ),
  // UPLOAD-ORIGINAL (founder 2026-10-30): an uploaded doc's original file was replaced (the old one
  // kept as a previous version), removed (the text stays), or the doc made a plain doc (detached).
  defineEvent(
    "page.upload_changed",
    {
      ...work,
      subject: "page",
      summary: "An uploaded doc's original file was replaced or removed, or the doc made plain",
      consumers: ["audit"],
    },
    z.strictObject({
      space_id: Uuid,
      change: z.enum(["replaced", "original_removed", "detached"]),
      file_name: z.string().max(255),
      revision: z.int().min(1),
    }),
  ),
  defineEvent(
    "page.archived",
    {
      ...work,
      subject: "page",
      summary: "Page archived (its child pages move up to its parent)",
      consumers: ["search", "rt"],
    },
    z.strictObject({
      space_id: Uuid,
      kind: PageKind,
      version: z.int().min(1),
      children_moved: z.int().min(0),
    }),
  ),
  // DOCS-KB: back from the archive, soft delete, and an agent's knowledge base.
  defineEvent(
    "page.unarchived",
    {
      ...work,
      subject: "page",
      summary: "Page restored from the archive (under its old parent when live, else at the top)",
      consumers: ["search", "rt"],
    },
    z.strictObject({
      space_id: Uuid,
      kind: PageKind,
      version: z.int().min(1),
      parent_page_id: Uuid.nullable(),
    }),
  ),
  // ACTIONS: a deleted page back (`POST pages/{id}/undelete`); `archived` when it was archived
  // before its delete and stays so.
  defineEvent(
    "page.undeleted",
    {
      ...work,
      subject: "page",
      summary: "Deleted page brought back (live in its old place, or archived as it was)",
      consumers: ["search", "rt"],
    },
    z.strictObject({
      space_id: Uuid,
      kind: PageKind,
      version: z.int().min(1),
      parent_page_id: Uuid.nullable(),
      archived: z.boolean(),
    }),
  ),
  defineEvent(
    "page.deleted",
    {
      ...work,
      subject: "page",
      summary: "Page deleted by an editor (it leaves every list; its versions stay)",
      consumers: ["search", "rt", "recall (forget)"],
    },
    z.strictObject({
      space_id: Uuid,
      kind: PageKind,
      title: z.string(),
      version: z.int().min(1),
      children_moved: z.int().min(0),
      /** Agents whose knowledge base named the page (their source rows are removed). */
      readers_removed: z.int().min(0),
    }),
  ),
  defineEvent(
    "agent.knowledge_changed",
    {
      ...agents,
      subject: "membership",
      summary: "A page, space or page kind added to or removed from an agent's knowledge base",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      /** The DE or personal-agent membership (the subject). */
      agent_kind: z.enum(["digital_employee", "personal_agent"]),
      op: z.enum(["added", "removed"]),
      source_id: Uuid,
      source_kind: KnowledgeSourceKind,
      page_id: Uuid.nullable(),
      space_id: Uuid.nullable(),
      page_kind: PageKind.nullable(),
    }),
  ),

  // Measurement
  defineEvent(
    "metric_def.created",
    {
      ...kpi,
      subject: "metric_def",
      summary: "Metric defined",
      consumers: ["proj.kpi (backfill)"],
    },
    z.strictObject({
      key: z.string(),
      name: z.string(),
      unit: MetricUnit,
      direction: MetricDirection,
      aggregation: MetricAggregation,
      cadence: MetricCadence.nullable(),
      source: MetricSource,
      event_query: EventQuery.nullable(),
      formula: z.string().nullable(),
      /** T3-TASK-ROLLUPS: a `task` metric's board and what it counts there. */
      task_query: TaskMetricQuery.nullable().optional(),
      visibility: Visibility,
    }),
  ),
  defineEvent(
    "metric_def.updated",
    {
      ...kpi,
      subject: "metric_def",
      summary: "Metric changed",
      consumers: ["proj.kpi (backfill)"],
    },
    z.strictObject({
      changes: changesOf({
        name: z.string(),
        description: z.string(),
        unit: MetricUnit,
        direction: MetricDirection,
        aggregation: MetricAggregation,
        normalization: MetricNormalization,
        cadence: MetricCadence,
        source: MetricSource,
        event_query: EventQuery,
        formula: z.string(),
        task_query: TaskMetricQuery,
        visibility: Visibility,
        gap_policy: GapPolicy,
        /** AGENT-REPORTING: the per-metric review flag (13 §Reporting duties). */
        needs_review: z.boolean(),
        /** TEAM-KPI: the metric's own warning bands (null: back to the company's). */
        bands: MetricBands,
        /** PLATFORM-OPERATOR: the gate spec (null: no longer a gate). */
        gate: MetricGate,
      }),
    }),
  ),
  defineEvent(
    "metric_def.archived",
    { ...kpi, subject: "metric_def", summary: "Metric archived", consumers: ["proj.kpi"] },
    z.strictObject({ key: z.string() }),
  ),
  defineEvent(
    "metric_def.unarchived",
    { ...kpi, subject: "metric_def", summary: "Metric restored", consumers: ["proj.kpi"] },
    z.strictObject({ key: z.string() }),
  ),
  // SEARCH-NOTIF: the `kpi.due` job (domain/inbox/kpi-due.ts): a seat's number for the period
  // just closed is not in — `due` while the entry window's grace runs, `late` after it. One per
  // (seat, metric, period, state); the inbox items of the seat's holders and its supervisors'.
  defineEvent(
    "kpi.entry_due",
    {
      ...kpi,
      subject: "seat",
      summary: "A seat's scorecard number for the last period is due, or late",
      consumers: ["inbox (seat and supervisor holders)", "rt"],
    },
    z.strictObject({
      metric_def_id: Uuid,
      metric_name: z.string(),
      period_key: z.string(),
      state: z.enum(["due", "late"]),
      due_at: Timestamp,
      recipients: z.int().min(0),
    }),
  ),
  // AGENT-REPORTING (13 §Reporting duties, founder-approved 2026-10-21)
  defineEvent(
    "reporting_duty.changed",
    {
      ...kpi,
      subject: "reporting_duty",
      summary: "A seat measurable's reporting duty set or removed",
      consumers: ["audit"],
    },
    z.strictObject({
      op: z.enum(["set", "removed"]),
      seat_id: Uuid,
      metric_def_id: Uuid,
      method: ReportingMethod,
      due_rule: DueRule,
      lead_minutes: z.int(),
      evidence_required: z.boolean(),
      remind: z.array(ReportingRemind),
    }),
  ),
  defineEvent(
    "reporting_duty.due",
    {
      ...kpi,
      subject: "reporting_duty",
      summary: "A reporting duty is about to fall due: its reminders went out",
      consumers: ["proj.inbox (reminder: the holder and/or the supervisor)", "audit"],
    },
    z.strictObject({
      seat_id: Uuid,
      metric_def_id: Uuid,
      metric_name: z.string(),
      seat_title: z.string(),
      due_at: Timestamp,
      period_key: PeriodKey,
      /** The people reminded in their inbox. */
      remind_seat_ids: z.array(Uuid),
      /** The DE woken to report (its `reporting` wake-up), if any. */
      woke_membership_id: Uuid.nullable(),
      wakeup_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "reporting_duty.late",
    {
      ...kpi,
      subject: "reporting_duty",
      summary: "A reporting duty's period was not reported by its due time",
      consumers: ["proj.inbox (the seat's manager)", "audit"],
    },
    z.strictObject({
      seat_id: Uuid,
      metric_def_id: Uuid,
      metric_name: z.string(),
      seat_title: z.string(),
      due_at: Timestamp,
      period_key: PeriodKey,
      /** The seat's supervisor seat, whose people are told (null: none, nobody is). */
      manager_seat_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "target_rule.changed",
    {
      ...kpi,
      subject: "target_rule",
      summary: "Target rule created, changed or deleted",
      consumers: ["proj.kpi"],
    },
    z.strictObject({
      op: z.enum(["created", "updated", "deleted"]),
      metric_def_id: Uuid,
      subject_kind: SubjectKind,
      subject_id: Uuid,
      period_type: PeriodType,
      value: Decimal,
      value_max: Decimal.nullable(),
      effective_from: IsoDate,
      effective_to: IsoDate.nullable(),
    }),
  ),
  defineEvent(
    "observation.recorded",
    {
      ...kpi,
      subject: "observation",
      summary: "Manual/import value, correction or tombstone (recordObservation)",
      consumers: ["scorecard reads", "friday report", "proj.inbox"],
    },
    z.strictObject({
      ...ObservationRef,
      value: Decimal.nullable(),
      status: ObservationStatus,
      source: ObservationSource,
      source_event_id: z.string(),
      supersedes_id: Uuid.nullable(),
      /**
       * MEMBER-ENTRY (audit of a correction): the value of the row this one supersedes (null:
       * it superseded nothing; on a tombstone, the value removed); with `value`, the actor and `note` (the
       * reason a holder must give) the correction's whole audit line. Absent on older events.
       */
      previous_value: Decimal.nullable().optional(),
      tombstone: z.boolean(),
      note: z.string().nullable(),
      /** A scorecard of a period containing this one is published: the value diverges from it. */
      report_stale: z.boolean(),
      /** AGENT-KPI-TOOL: an agent value (`source = agent`): the run, the DE and its evidence. */
      provenance: ObservationProvenance.optional(),
    }),
  ),
  defineEvent(
    "observation.recomputed",
    {
      ...kpi,
      subject: "observation",
      summary: "proj.kpi recomputed an event-sourced value",
      consumers: ["scorecard reads", "friday report"],
    },
    z.strictObject({
      ...ObservationRef,
      from: Decimal.nullable(),
      to: Decimal.nullable(),
      reason: z.string(),
      supersedes_id: Uuid.nullable(),
      source_event_ids: z.array(Uuid),
      /** A scorecard of a period containing this one is published: the value diverges from it. */
      report_stale: z.boolean(),
    }),
  ),
  defineEvent(
    "scorecard.published",
    {
      domain: "reports",
      feature: "kpi",
      subject: "scorecard_publication",
      summary: "Publication revision frozen (revision > 1 carries a reason)",
      consumers: ["friday report", "audit"],
    },
    z.strictObject({
      subject_kind: SubjectKind,
      subject_id: Uuid,
      period_type: PeriodType,
      period_key: PeriodKey,
      revision: z.int().min(1),
      reason: z.string().nullable(),
      kpi_score: z.number().nullable(),
      band: Band,
    }),
  ),
  defineEvent(
    "friday_report.generated",
    {
      domain: "reports",
      feature: "kpi",
      subject: "friday_report",
      summary: "Report version generated (and its publications written)",
      consumers: ["audit"],
    },
    z.strictObject({ week_key: WeekKey, version: z.int().min(1), publication_ids: z.array(Uuid) }),
  ),
  defineEvent(
    "friday_report.updated",
    {
      domain: "reports",
      feature: "kpi",
      subject: "friday_report",
      summary: "Supervisor note edited",
      consumers: ["audit"],
    },
    z.strictObject({ changes: changesOf({ supervisor_note: z.string() }) }),
  ),
  defineEvent(
    "friday_report.sent",
    {
      domain: "reports",
      feature: "kpi",
      subject: "friday_report",
      summary: "Report emailed (idempotent per report id and version)",
      consumers: ["audit"],
    },
    z.strictObject({
      week_key: WeekKey,
      version: z.int().min(1),
      recipient_count: z.int().min(0),
      forced: z.boolean(),
    }),
  ),
  defineEvent(
    "friday_report.send_failed",
    {
      domain: "reports",
      feature: "kpi",
      subject: "friday_report",
      summary: "Send failed after retries; state = failed",
      consumers: ["ops digest"],
    },
    z.strictObject({
      week_key: WeekKey,
      version: z.int().min(1),
      attempts: z.int().min(1),
      error: z.string(),
    }),
  ),
  defineEvent(
    "friday_report.delivery_reconciled",
    {
      domain: "reports",
      feature: "kpi",
      subject: "friday_report",
      summary: "An agency operator settled an unconfirmed delivery against the provider's logs",
      consumers: ["audit"],
    },
    z.strictObject({
      week_key: WeekKey,
      version: z.int().min(1),
      operation: z.string(),
      /** The recipient as sha256 of the normalized address, 32 hex (no address in the log). */
      recipient_ref: z.string().regex(/^[0-9a-f]{32}$/),
      previous_state: z.enum(["in_flight", "submitting", "uncertain"]),
      outcome: z.enum(["delivered", "not_delivered"]),
      /** Where the operator established the outcome (exactly-once email is impossible). */
      evidence: z.enum(["provider_log", "message_id_lookup"]),
      note: z.string(),
    }),
  ),

  // Cost and time
  defineEvent(
    "cost.posted",
    {
      ...ledger,
      subject: "cost_event",
      summary: "Ledger row posted (ingest, manual, import, subscription, human time, reversal)",
      consumers: ["proj.kpi", "friday report"],
    },
    z.strictObject({
      kind: CostEventKind,
      amount_minor: MinorAmount,
      amount_micros: MicrosAmount.nullable(),
      currency: Currency,
      seat_id: Uuid.nullable(),
      holder_membership_id: Uuid.nullable(),
      meter_identity: z.string(),
      meter: CostMeter.nullable(),
      source_ref: SourceRef.nullable(),
      reverses_cost_event_id: Uuid.nullable(),
      visibility: Visibility,
      occurred_at: Timestamp,
      period_key: MonthKey,
      /** Ingest `cost` items only: the item minus `item_key`, `kind` and `cost`. */
      item: z.strictObject(IngestedItem).optional(),
    }),
  ),
  // VOICE-CHOICES: the voice service opened a session it hosts for a person (its segments are
  // booked against it, and must fit its timeline).
  defineEvent(
    "voice_session.opened",
    {
      ...ledger,
      subject: "membership",
      summary: "The voice service opened a voice session for a person",
      consumers: ["audit"],
    },
    z.strictObject({
      session_id: Uuid,
      started_at: Timestamp,
    }),
  ),
  defineEvent(
    "cost.meter_settled",
    {
      ...ledger,
      subject: "cost_event",
      summary:
        "An operator settled a stuck meter identity (D-15): re-posted the source's amount or reconciled it",
      consumers: ["audit"],
    },
    z.strictObject({
      meter_identity: z.string(),
      resolution: MeterSettlementResolution,
      original_cost_event_id: Uuid,
      /** The row a re-send of the settled content is a duplicate of. */
      cost_event_id: Uuid,
      reversal_cost_event_id: Uuid.nullable(),
      amount_minor: MinorAmount,
      reason: z.string(),
    }),
  ),
  defineEvent(
    "de_subscription.created",
    {
      ...ledger,
      subject: "de_subscription",
      summary: "DE subscription created",
      consumers: ["subscription job"],
    },
    z.strictObject({
      seat_id: Uuid,
      membership_id: Uuid,
      amount_minor: MinorAmount,
      currency: Currency,
      cadence: SubscriptionCadence,
      start_month: MonthKey,
      end_month: MonthKey.nullable(),
      scope: Visibility,
    }),
  ),
  defineEvent(
    "de_subscription.changed",
    {
      ...ledger,
      subject: "de_subscription",
      summary: "DE subscription changed",
      consumers: ["subscription job"],
    },
    z.strictObject({
      changes: changesOf({ amount_minor: MinorAmount, end_month: MonthKey, scope: Visibility }),
    }),
  ),
  defineEvent(
    "time_entry.created",
    {
      ...ledger,
      subject: "time_entry",
      summary: "Time entry revision 1 (manual, import, ingest)",
      consumers: ["proj.kpi", "cost (human time)"],
    },
    z.strictObject({
      ...TimeEntryRef,
      duration_seconds: z.int().positive(),
      minutes: z.int(),
      /** A scorecard of the period is already published (13 §Ingest contract rule 8). */
      report_stale: z.boolean(),
    }),
  ),
  defineEvent(
    "time_entry.updated",
    {
      ...ledger,
      subject: "time_entry",
      summary: "New revision superseding the live row; consumers post the delta",
      consumers: ["proj.kpi", "cost (human time)"],
    },
    z.strictObject({
      ...TimeEntryRef,
      supersedes_id: Uuid,
      old_seconds: z.int().positive(),
      new_seconds: z.int().positive(),
      /** The period's scorecard was already published (13 §Ingest contract rule 8). */
      report_stale: z.boolean(),
    }),
  ),
  defineEvent(
    "time_entry.deleted",
    {
      ...ledger,
      subject: "time_entry",
      summary: "Live row voided (never a DELETE)",
      consumers: ["proj.kpi", "cost (human time)"],
    },
    z.strictObject({
      ...TimeEntryRef,
      duration_seconds: z.int().positive(),
      /** A scorecard of the period is already published: published time disappears. */
      report_stale: z.boolean(),
    }),
  ),
  defineEvent(
    "time_entry.approval_changed",
    {
      ...ledger,
      subject: "time_entry",
      summary: "Approval decided; only approved time counts",
      consumers: ["proj.kpi", "cost (human time)"],
    },
    z.strictObject({
      ...TimeEntryRef,
      from: ApprovalStatus,
      duration_seconds: z.int().positive(),
      /** The deciding person member; null when the status came from the source system (ingest). */
      decided_by_membership_id: Uuid.nullable(),
      rejection_reason: z.string().nullable(),
      /** A scorecard of the period is already published: counted hours change (review #13). */
      report_stale: z.boolean(),
    }),
  ),

  // DE configuration (DC-01): catalog
  defineEvent(
    "agent_template.created",
    {
      ...deConfig,
      subject: "agent_template",
      summary: "A template author created a template head (agency console)",
      consumers: ["audit", "agency console"],
      // v2 (PLATFORM-OPERATOR, migration 0162): the done-for-you class is `managed`. Stored v1
      // events are append-only and may say `htva`; they read back as `managed`.
      version: 2,
      previous: [
        {
          version: 1,
          payload: AgentTemplateCreatedV1,
          upgrade: (p) => ({
            ...(p as z.infer<typeof AgentTemplateCreatedV1>),
            template_class: normaliseTemplateClass(
              (p as z.infer<typeof AgentTemplateCreatedV1>).template_class,
            ),
          }),
        },
      ],
    },
    z.strictObject({
      scope: AgentTemplateScope,
      agency_id: Uuid.nullable(),
      key: z.string(),
      name: z.string(),
      template_class: TemplateClass,
    }),
  ),
  defineEvent(
    "agent_template_version.drafted",
    {
      ...deConfig,
      subject: "agent_template_version",
      summary: "A draft version was cut (by an author, or by an accepted learning-loop edit)",
      consumers: ["audit", "agency console"],
    },
    z.strictObject({
      ...VersionRef,
      source: TemplateVersionSource,
      supersedes_version_id: Uuid.nullable(),
      proposal_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "agent_template_version.draft_updated",
    {
      ...deConfig,
      subject: "agent_template_version",
      summary: "A draft's content, evals or rules changed (validated versions are immutable)",
      consumers: ["audit", "agency console"],
    },
    z.strictObject({
      ...VersionRef,
      changed: z
        .array(
          z.enum([
            "skill_index",
            "verification_skill",
            "role_model_map",
            "standing_orders",
            "tool_grants",
            "runtime_config",
            "evals",
            "rules",
            "changelog",
            // TEMPLATES-T2: a role template's defaults
            "role_defaults",
          ]),
        )
        .min(1),
    }),
  ),
  defineEvent(
    "agent_template_version.validated",
    {
      ...deConfig,
      subject: "agent_template_version",
      summary: "Validation passed: token cap, verification skill, evals, rule table",
      consumers: ["audit", "agency console"],
    },
    z.strictObject(ValidationOutcome),
  ),
  defineEvent(
    "agent_template_version.validation_failed",
    {
      ...deConfig,
      subject: "agent_template_version",
      summary: "Validation failed; the version stays a draft (reasons[] say why)",
      consumers: ["audit", "agency console"],
    },
    z.strictObject(ValidationOutcome),
  ),
  defineEvent(
    "agent_template_version.published",
    {
      ...deConfig,
      subject: "agent_template_version",
      summary: "A validated version was published and became the template's current version",
      consumers: ["audit", "run admission (pin)", "agency console"],
    },
    z.strictObject({
      ...VersionRef,
      supersedes_version_id: Uuid.nullable(),
      source: TemplateVersionSource,
      changelog: z.string().nullable(),
    }),
  ),
  defineEvent(
    "agent_template_version.deprecated",
    {
      ...deConfig,
      subject: "agent_template_version",
      summary: "A published version was deprecated (re-deploy tasks on affected supervisor seats)",
      consumers: ["audit", "drift audit scheduler (re-deploy task per affected DE)"],
    },
    z.strictObject({
      ...VersionRef,
      replaced_by_version_id: Uuid.nullable(),
      reason: z.string().nullable(),
    }),
  ),
  defineEvent(
    "template_edit.proposed",
    {
      ...deConfig,
      subject: "template_edit_proposal",
      summary: "A learning-loop edit was proposed with its mandatory eval",
      consumers: ["audit", "inbox (template owner)"],
    },
    z.strictObject({
      template_id: Uuid,
      based_on_version_id: Uuid,
      source: TemplateEditSource,
      eval_id: Uuid,
      run_refs_count: z.int().min(0),
      diff_ref: z.string(),
    }),
  ),
  defineEvent(
    "template_edit.accepted",
    {
      ...deConfig,
      subject: "template_edit_proposal",
      summary:
        "An edit was accepted: a new draft version follows (`agent_template_version.drafted`)",
      consumers: ["audit", "agency console"],
    },
    z.strictObject({
      template_id: Uuid,
      based_on_version_id: Uuid,
      eval_id: Uuid,
      accepted_version_id: Uuid,
    }),
  ),
  defineEvent(
    "template_edit.rejected",
    {
      ...deConfig,
      subject: "template_edit_proposal",
      summary: "An edit was rejected with a reason",
      consumers: ["audit"],
    },
    z.strictObject({
      template_id: Uuid,
      based_on_version_id: Uuid,
      eval_id: Uuid,
      reason: z.string(),
    }),
  ),

  // DE configuration: deploys (ADR-0018 §5)
  defineEvent(
    "deploy.staged",
    {
      ...deConfig,
      subject: "deploy",
      summary: "A bundle was built and a deploy staged (first deploys wait for a person)",
      consumers: [
        "inbox (approver, when needs_approval)",
        "hand-off worker (no approval needed)",
        "audit",
      ],
    },
    z.strictObject({
      ...DeployTarget,
      de_membership_id: Uuid,
      template_id: Uuid,
      template_version_id: Uuid,
      version: z.int().min(1),
      /** Null when the version has no verification skill: the override builds the bundle (DC-05). */
      bundle_id: Uuid.nullable(),
      bundle_sha: Sha256.nullable(),
      kind: DeployKind,
      verification_state: VerificationState,
      needs_approval: z.boolean(),
      /** DC-05: the deploy waits for an operator's verification override before anything else. */
      awaits_verification_override: z.boolean().optional(),
    }),
  ),
  defineEvent(
    "deploy.approved",
    {
      ...deConfig,
      subject: "deploy",
      summary: "A person approved the first deploy of this version into the company",
      consumers: ["hand-off worker", "audit"],
    },
    z.strictObject({ ...DeployTarget, approved_by: DeployApprover }),
  ),
  defineEvent(
    "deploy.rejected",
    {
      ...deConfig,
      subject: "deploy",
      summary: "A person rejected a staged deploy with a reason",
      consumers: ["inbox (stager's supervisor)", "audit"],
    },
    z.strictObject({ ...DeployTarget, rejected_by: DeployApprover, reason: z.string() }),
  ),
  defineEvent(
    "deploy.handed_off",
    {
      ...deConfig,
      subject: "deploy",
      summary: "The platform handed the bundle to AiGentPanel (intake 202)",
      consumers: ["confirm poller", "audit"],
    },
    z.strictObject({ ...DeployTarget, bundle_sha: Sha256, panel_ref: PanelRef }),
  ),
  defineEvent(
    "deploy.confirmed",
    {
      ...deConfig,
      subject: "deploy",
      summary: "AiGentPanel confirmed and the echoed bundle_sha matched",
      consumers: [
        "cell: digital_employee.deployed (consumer:deploy.mirror)",
        "deploys.recall_keys",
        "audit",
      ],
    },
    z.strictObject({ ...DeployTarget, bundle_sha: Sha256, panel_ref: PanelRef }),
  ),
  defineEvent(
    "deploy.failed",
    {
      ...deConfig,
      subject: "deploy",
      summary: "Panel error, sha mismatch, refused intake or hand-off timeout",
      consumers: [
        "cell: digital_employee.deploy_failed (consumer:deploy.mirror)",
        "inbox (operator; supervisor when the DE was running)",
        "audit",
      ],
    },
    z.strictObject({
      ...DeployTarget,
      /** As received from the panel; null when nothing came back. */
      bundle_sha: Sha256.nullable(),
      panel_ref: PanelRef.nullable(),
      error: DeployError,
    }),
  ),
  defineEvent(
    "verification.overridden",
    {
      ...deConfig,
      subject: "deploy",
      summary: "An operator overrode verification for a pilot (the DE shows unverified)",
      consumers: ["audit", "inbox (supervisor, company admins)"],
    },
    z.strictObject({
      override_id: Uuid,
      bundle_id: Uuid,
      target_company_id: Uuid,
      reason: z.string(),
      pilot_until: Timestamp,
    }),
  ),
  defineEvent(
    "digital_employee.deployed",
    {
      ...deConfig,
      subject: "digital_employee",
      summary: "The DE now runs a confirmed bundle; the only writer of the mirror columns",
      consumers: [
        "DE profile (version, unverified badge)",
        "agent key issue (first deploy)",
        "drift audit scheduler",
        "inbox (supervisor)",
        "audit",
      ],
    },
    z.strictObject({
      deploy_ref: Uuid,
      template_version_id: Uuid,
      bundle_sha: Sha256,
      verification_state: VerificationState,
      previous_version_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "digital_employee.deploy_failed",
    {
      ...deConfig,
      subject: "digital_employee",
      summary: "A deploy of this DE failed; it stays on its previous bundle",
      consumers: ["DE profile", "inbox (supervisor)", "audit"],
    },
    z.strictObject({
      deploy_ref: Uuid,
      template_version_id: Uuid,
      error: DeployError,
    }),
  ),
  defineEvent(
    "digital_employee.panel_drift",
    {
      ...deConfig,
      subject: "digital_employee",
      summary:
        "AiGentPanel reported a late result for a deploy CompanyOS closed: the DE's routing evidence is invalid, a probe is required",
      consumers: ["DE profile (probe required)", "routing evidence (Phase 2 increment 6)", "audit"],
    },
    z.strictObject({
      deploy_ref: Uuid,
      /** What the panel reported late: `confirmed`, `failed`, or `not_received` (never had it). */
      result: z.enum(["confirmed", "failed", "not_received"]),
      panel_ref: PanelRef.nullable(),
      message: z.string(),
    }),
  ),
  // PROVISION-IN-PRODUCT: a DE's set-up asked again by a manager (the `de.provision` consumer
  // runs it, as it does for every new DE); the satellite took the DE's keys; the satellite
  // registered or removed the DE's bridge (the wake intake). Never a key or secret: prefixes and
  // the bridge URL only.
  defineEvent(
    "digital_employee.provision_requested",
    {
      ...deConfig,
      subject: "digital_employee",
      summary: "A manager asked for the DE's set-up to run again (only what is missing is done)",
      consumers: ["de.provision", "audit"],
    },
    z.strictObject({}),
  ),
  // DE-CONNECT: a manager (or the backfill, as a manager) asked for the DE to be connected to its
  // agent container from CompanyOS's records; the worker's `de.connect` consumer runs the steps.
  defineEvent(
    "digital_employee.connect_requested",
    {
      ...deConfig,
      subject: "digital_employee",
      summary:
        "A manager asked for the DE to be connected to its agent container (seat, spec, key, endpoint, container, tools, bridge)",
      consumers: ["de.connect", "audit"],
    },
    z.strictObject({
      satellite_id: Uuid,
      instance_id: z.string().min(1).max(128),
      profile_name: z.string().min(1).max(128),
      /** An already-running DE: nothing is minted or replaced. */
      adopt: z.boolean(),
    }),
  ),
  defineEvent(
    "digital_employee.credentials_claimed",
    {
      ...deConfig,
      subject: "digital_employee",
      summary:
        "The satellite took the DE's MCP agent key (no longer held here) or its Recall key again",
      consumers: ["DE profile (set-up)", "de.connect", "audit"],
    },
    z.strictObject({
      /** The agent key handed over now (its prefix), null when it was taken before. */
      agent_key_prefix: z.string().nullable(),
      recall_key: z.boolean(),
    }),
  ),
  defineEvent(
    "digital_employee.bridge_registered",
    {
      ...deConfig,
      subject: "digital_employee",
      summary: "The satellite registered where the DE's bridge listens for wake pushes",
      consumers: ["bridge.wake", "DE profile (set-up)", "audit"],
    },
    z.strictObject({
      url: z.string(),
      container: z.string().nullable(),
      profile: z.string().nullable(),
    }),
  ),
  defineEvent(
    "digital_employee.bridge_removed",
    {
      ...deConfig,
      subject: "digital_employee",
      summary: "The satellite removed the DE's bridge (it polls again)",
      consumers: ["bridge.wake", "audit"],
    },
    z.strictObject({ url: z.string() }),
  ),
  defineEvent(
    "de_template_override.changed",
    {
      ...deConfig,
      subject: "de_template_override",
      summary: "A company changed its standing orders or role-to-model map (a new deploy follows)",
      consumers: ["Deployment engineer (re-stage)", "audit"],
    },
    z.strictObject({
      de_membership_id: Uuid,
      /** DE-WIZARD: null = the DE's own, before any template is deployed or staged. */
      template_id: Uuid.nullable(),
      changed: z.array(z.enum(["standing_orders", "role_model_map"])).min(1),
      version: z.int().min(1),
    }),
  ),
  // AGENT-SPEC (consolidate-then-demo A3): a new signed version of a DE's agent spec.
  defineEvent(
    "agent_spec.versioned",
    {
      ...deConfig,
      subject: "digital_employee",
      summary:
        "A new signed version of the DE's agent spec (SOUL, AGENTS, skills, tools, model, Recall, MCP, key references)",
      consumers: ["Spec applier (refresh notice)", "audit"],
    },
    z.strictObject({
      version: z.int().min(1),
      spec_sha256: Sha256,
      source: AgentSpecSource,
      /** The parts this version changed (every part the first time). */
      changed: z.array(AgentSpecPart).min(1),
    }),
  ),

  // TEMPLATES-T3/T4 (docs/design/2026-10-28-agent-templates.md §1–§3)
  defineEvent(
    "template_overlay.changed",
    {
      ...deConfig,
      subject: "template_overlay",
      summary:
        "A client overlay or an applied vertical pack was created, edited or archived (DEs pick it up at their next deploy)",
      consumers: ["audit", "Deployment engineer (re-stage)"],
    },
    z.strictObject({
      op: z.enum(["created", "updated", "archived"]),
      scope: TemplateOverlayScope,
      template_key: z.string().nullable(),
      vertical_pack_id: Uuid.nullable(),
      version: z.int().min(1),
      changed: z.array(z.string().max(64)).max(16),
    }),
  ),
  defineEvent(
    "digital_employee.role_applied",
    {
      ...deConfig,
      subject: "digital_employee",
      summary:
        "A DE was set up from a role template: overlay, KPIs, access defaults, seat policy, routines and probation",
      consumers: ["audit", "Deployment engineer (stage)"],
    },
    z.strictObject({
      template_id: Uuid,
      template_version_id: Uuid,
      template_key: z.string(),
      /** The company's own overlays that applied (every role's, then this role's). */
      overlay_ids: z.array(Uuid).max(2),
      vertical_overlay_id: Uuid.nullable(),
      access: z.array(z.strictObject({ family: AccessFamily, level: AccessLevel })).max(16),
      kpis: z.int().min(0),
      routines: z.int().min(0),
      connect_later: z.array(AccessFamily).max(16),
      probation_until: Timestamp,
      probation_tasks_required: z.int().min(0),
    }),
  ),
  defineEvent(
    "de_access_default.changed",
    {
      ...deConfig,
      subject: "digital_employee",
      summary:
        "A DE's access on a connection family changed; a widening above the role default names who confirmed it",
      consumers: ["audit"],
    },
    z.strictObject({
      changes: z
        .array(
          z.strictObject({
            family: AccessFamily,
            from: AccessLevel.nullable(),
            to: AccessLevel,
            widened: z.boolean(),
            confirmed_by_membership_id: Uuid.nullable(),
          }),
        )
        .min(1)
        .max(16),
    }),
  ),
  defineEvent(
    "digital_employee.probation_ended",
    {
      ...deConfig,
      subject: "digital_employee",
      summary:
        "The supervisor ended a DE's probation with its record attached; its access defaults now apply in full",
      consumers: ["audit"],
    },
    z.strictObject({
      started_at: Timestamp,
      until: Timestamp,
      tasks_required: z.int().min(0),
      tasks_done: z.int().min(0),
      record: z.strictObject({
        approved_as_is: z.int().min(0),
        edited: z.int().min(0),
        rejected: z.int().min(0),
      }),
    }),
  ),

  // DE configuration: model credentials (ADR-0020 widened 2026-10-20)
  defineEvent(
    "model_credential.added",
    {
      ...deConfig,
      subject: "model_credential",
      summary: "A person or a company added an API key or linked a CLI subscription",
      consumers: ["audit", "run admission", "deploys.model_keys"],
    },
    z.strictObject({
      scope: ModelCredentialScope,
      kind: ModelCredentialKind,
      provider: z.string(),
      label: z.string(),
      plan: z.string().nullable(),
      owner_user_id: Uuid.nullable(),
      login_location: CliLoginLocation.nullable(),
    }),
  ),
  defineEvent(
    "model_credential.revoked",
    {
      ...deConfig,
      subject: "model_credential",
      summary: "A credential was revoked (a sealed key is destroyed in the same transaction)",
      consumers: ["audit", "run admission", "deploys.model_keys"],
    },
    z.strictObject({
      scope: ModelCredentialScope,
      kind: ModelCredentialKind,
      provider: z.string(),
      reason: z.string().nullable(),
    }),
  ),
  // ACTIONS: the non-secret fields of a credential changed (never a key: a new key is a new
  // credential, the old one revoked).
  defineEvent(
    "model_credential.updated",
    {
      ...deConfig,
      subject: "model_credential",
      summary: "A credential's label or plan-login details changed (never its key)",
      consumers: ["audit"],
    },
    z.strictObject({
      fields: z.array(z.enum(["label", "login_location", "runtime_ref", "fallback_to_gateway"])),
    }),
  ),
  defineEvent(
    "subscription_attestation.recorded",
    {
      ...deConfig,
      subject: "subscription_attestation",
      summary: "Who attested which provider terms for a credential (re-attest on plan change)",
      consumers: ["audit", "run admission (refused without a live attestation)"],
    },
    z.strictObject({
      credential_id: Uuid,
      provider: z.string(),
      plan: z.string().nullable(),
      terms_ref: z.string(),
      terms_version: z.string().nullable(),
      plan_is_own: z.boolean(),
      own_work_only: z.boolean(),
      allows_paid_client_work: z.boolean(),
      attested_by_user_id: Uuid,
      supersedes_attestation_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "subscription_attestation.revoked",
    {
      ...deConfig,
      subject: "subscription_attestation",
      summary: "An attestation was revoked; new runs on its credential are refused",
      consumers: ["audit", "run admission"],
    },
    z.strictObject({ credential_id: Uuid, reason: z.string() }),
  ),

  // BYO-01 tool connections ("My subscriptions"; Work OS `connect`, program row M4-04)
  defineEvent(
    "tool_connection.added",
    {
      ...deConfig,
      subject: "tool_connection",
      summary: "A person or a company connected a tool (an API key sealed, or an OAuth account)",
      consumers: ["audit"],
    },
    z.strictObject({
      scope: ToolConnectionScope,
      provider: ToolConnectionProvider,
      label: z.string(),
      owner_user_id: Uuid.nullable(),
      host: z.string().nullable(),
    }),
  ),
  defineEvent(
    "tool_connection.checked",
    {
      ...deConfig,
      subject: "tool_connection",
      summary: "A connection was checked with its read-only probe (never the vendor's text)",
      consumers: ["audit"],
    },
    z.strictObject({
      ok: z.boolean(),
      state: ToolConnectionState,
      error_class: z.string().nullable(),
    }),
  ),
  defineEvent(
    "tool_connection.revoked",
    {
      ...deConfig,
      subject: "tool_connection",
      summary:
        "A connection was disconnected (its sealed secret destroyed in the same transaction)",
      consumers: ["audit"],
    },
    z.strictObject({
      scope: ToolConnectionScope,
      provider: ToolConnectionProvider,
      reason: z.string().nullable(),
    }),
  ),
  // M4-04: refresh, reconnect, mail and calendar for crews.
  defineEvent(
    "tool_connection.refreshed",
    {
      ...deConfig,
      subject: "tool_connection",
      summary:
        "An OAuth connection's access token was refreshed, or the vendor refused the refresh (never a token)",
      consumers: ["audit"],
    },
    z.strictObject({
      outcome: z.enum(["refreshed", "failed"]),
      state: ToolConnectionState,
      error_class: z.string().nullable(),
      /** Why it failed (`refresh_refused`, `rate_limited`, `not_set_up`, `unavailable`, …). */
      cause: z.string().nullable(),
      token_expires_at: Timestamp.nullable(),
    }),
  ),
  defineEvent(
    "tool_connection.reconnected",
    {
      ...deConfig,
      subject: "tool_connection",
      summary: "An OAuth connection was signed in again as the same account (its tokens re-sealed)",
      consumers: ["audit"],
    },
    z.strictObject({
      scope: ToolConnectionScope,
      provider: ToolConnectionProvider,
      tiers: z.array(z.string()),
    }),
  ),
  defineEvent(
    "crew_connection_grant.changed",
    {
      ...agents,
      subject: "personal_crew",
      summary: "What a crew may read through its owner's own connections changed (mail, calendar)",
      consumers: ["audit"],
    },
    z.strictObject({
      crew_membership_id: Uuid,
      owner_membership_id: Uuid,
      mail: Uuid.nullable(),
      calendar: Uuid.nullable(),
      granted: z.int().min(0),
      revoked: z.int().min(0),
    }),
  ),

  // DE configuration: AiGentPanel launch (ADR-0018 §2)
  defineEvent(
    "launch_token.minted",
    {
      ...deConfig,
      subject: "launch_token",
      summary: "A super-admin was given a one-time, 60 s link into AiGentPanel",
      consumers: ["audit"],
    },
    z.strictObject({
      audience: z.string(),
      target_company_id: Uuid,
      de_membership_id: Uuid.nullable(),
      expires_at: Timestamp,
    }),
  ),
  defineEvent(
    "launch_token.used",
    {
      ...deConfig,
      subject: "launch_token",
      summary: "AiGentPanel reported the launch token redeemed (signed callback)",
      consumers: ["audit"],
    },
    z.strictObject({ used_at: Timestamp }),
  ),

  // DE configuration: verdicts, decision log, drift audits (ADR-0019)
  defineEvent(
    "run.verdict_recorded",
    {
      ...deRuns,
      subject: "run",
      summary: "The verification skill's verdict on the run's output (one per run, depth 0)",
      consumers: [
        "approval precondition",
        "inbox (supervisor on not_verified / inconclusive)",
        "proj.kpi (de.verified_rate)",
        "recall (outcome)",
      ],
    },
    z.strictObject({
      verdict: Verdict,
      output_ref: PointerRef,
      evidence_ref: PointerRef,
      note: z.string().max(280).nullable(),
    }),
  ),
  defineEvent(
    "run.decision_recorded",
    {
      ...deRuns,
      subject: "run",
      summary: "One append-only decision-log row (evidence is a pointer, never prose)",
      consumers: ["audit", "learning loop", "drift audit", "recall (lesson on failure)"],
    },
    z.strictObject({
      decision_id: Uuid,
      seq: z.int().min(1),
      decision: z.string().max(200),
      why: z.string().max(500),
      evidence_ref: PointerRef.nullable(),
      result: RunDecisionResult,
      escalation_kind: RunEscalationKind.nullable(),
    }),
  ),
  defineEvent(
    "run.premise_escalated",
    {
      ...deRuns,
      subject: "run",
      summary: "Two failed fixes shared an assumption: the run stops and escalates it",
      consumers: ["inbox (supervisor)", "run → waiting_input", "recall (lesson)"],
    },
    z.strictObject({
      assumption: z.string().max(500),
      failed_fix_refs: z.array(Uuid).length(2),
    }),
  ),
  defineEvent(
    "run.resumed",
    {
      ...deRuns,
      subject: "run",
      summary: "The supervisor resumed a refused run; its verdict was cleared into the log",
      consumers: ["run state", "audit"],
    },
    z.strictObject({
      by_membership_id: Uuid,
      note: z.string().max(LIMITS.text).nullable(),
      verdict_cleared: z.boolean(),
    }),
  ),
  defineEvent(
    "drift_audit.scheduled",
    {
      ...deRuns,
      subject: "drift_audit",
      summary: "A drift audit was scheduled (monthly, target-system change, or manual)",
      consumers: ["drift audit runner"],
    },
    z.strictObject({ ...DriftAuditRef, scheduled_for: Timestamp }),
  ),
  defineEvent(
    "drift_audit.started",
    {
      ...deRuns,
      subject: "drift_audit",
      summary: "The audit started re-running the deployed version's evals",
      consumers: ["DE profile"],
    },
    z.strictObject(DriftAuditRef),
  ),
  defineEvent(
    "drift_audit.passed",
    {
      ...deRuns,
      subject: "drift_audit",
      summary: "Every eval passed",
      consumers: ["DE profile (last_drift_audit_at)", "cost ledger"],
    },
    z.strictObject({
      ...DriftAuditRef,
      results: z.record(z.string(), z.enum(["pass", "fail"])),
      cost_micros: MicrosAmount,
    }),
  ),
  defineEvent(
    "drift_audit.failed",
    {
      ...deRuns,
      subject: "drift_audit",
      summary: "An eval failed: a task opens on the supervisor seat; fail counts feed the loop",
      consumers: [
        "inbox (supervisor)",
        "DE profile (last_drift_audit_at)",
        "cost ledger",
        "agency console (fail counts → learning loop)",
      ],
    },
    z.strictObject({
      ...DriftAuditRef,
      results: z.record(z.string(), z.enum(["pass", "fail"])),
      task_id: Uuid,
      cost_micros: MicrosAmount,
    }),
  ),
  defineEvent(
    "drift_audit.errored",
    {
      ...deRuns,
      subject: "drift_audit",
      summary: "The audit could not run (target unreachable, runner error); not a verdict",
      consumers: ["inbox (operator)", "cost ledger"],
    },
    z.strictObject({ ...DriftAuditRef, reason: z.string(), cost_micros: MicrosAmount }),
  ),

  // Ingest
  defineEvent(
    "activity.recorded",
    {
      domain: "ingest",
      feature: "core",
      subject: "digital_employee",
      summary: "DE activity item (ingest key; item minus item_key)",
      consumers: ["proj.kpi", "DE timeline"],
    },
    z.strictObject({ kind: z.literal("activity"), ...IngestedItem }),
  ),
  defineEvent(
    "outcome.recorded",
    {
      domain: "ingest",
      feature: "core",
      subject: "digital_employee",
      summary: "DE outcome item (ingest key; item minus item_key)",
      consumers: ["proj.kpi", "DE timeline", "friday report highlights"],
    },
    z.strictObject({ kind: z.literal("outcome"), ...IngestedItem }),
  ),

  // Operations
  defineEvent(
    "import.committed",
    {
      domain: "operations",
      feature: "core",
      subject: "import_batch",
      summary: "CSV batch committed (rows carry correlation_id = batch id)",
      consumers: ["import batch list"],
    },
    z.strictObject({
      resource: ImportResource,
      rows: z.int().min(0),
      counts: z.record(z.string(), z.int().min(0)),
    }),
  ),
  defineEvent(
    "import.reversed",
    {
      domain: "operations",
      feature: "core",
      subject: "import_batch",
      summary: "Operator reversed a batch (voids, tombstones, cost reversals)",
      consumers: ["import batch list", "audit"],
    },
    z.strictObject({
      resource: ImportResource,
      voided_time_entries: z.int().min(0),
      tombstoned_observations: z.int().min(0),
      reversed_cost_events: z.int().min(0),
      closed_external_identities: z.int().min(0),
      /** M2-08: tasks a `tasks` batch created that still existed and were deleted. */
      deleted_tasks: z.int().min(0).optional(),
    }),
  ),
  defineEvent(
    "consumer.record_retried",
    {
      domain: "operations",
      feature: "core",
      subject: "event",
      summary: "Operator retried a failed processing record",
      consumers: ["audit"],
    },
    z.strictObject({ consumer_name: z.string(), attempts: z.int().min(0) }),
  ),
  defineEvent(
    "consumer.record_skipped",
    {
      domain: "operations",
      feature: "core",
      subject: "event",
      summary: "Operator marked a failed record skipped_by_operator",
      consumers: ["audit"],
    },
    z.strictObject({ consumer_name: z.string(), reason: z.string().min(1) }),
  ),
  // CONNECTIONS: a platform admin set or reset one of the platform's service endpoints (Operator
  // console › Connections). A control-plane fact recorded in the platform company's log, like
  // the launch tokens, beside its `cp.platform_audit` row. Values are URLs or address lists,
  // never a secret (credentials in a URL are refused before anything is stored).
  defineEvent(
    "service_endpoint.changed",
    {
      domain: "operations",
      feature: "core",
      subject: "service_endpoint",
      summary:
        "A platform admin set or reset a service endpoint (old → new, host change confirmed)",
      consumers: ["audit"],
    },
    z.strictObject({
      key: ServiceEndpointKey,
      action: z.enum(["set", "reset"]),
      from: z.strictObject({
        value: z.string().nullable(),
        source: z.enum(["stored", "env", "unset"]),
      }),
      to: z.strictObject({
        value: z.string().nullable(),
        source: z.enum(["stored", "env", "unset"]),
      }),
      host_changed: z.boolean(),
      /** The host typed back to confirm a credentialed service's move; null when none was needed. */
      confirmed_host: z.string().nullable(),
      /** Companies with a Recall tenant when Recall's host changed; null otherwise. */
      tenants: z.int().min(0).nullable(),
    }),
  ),
  // RUN-EFFORT: a platform admin stored a new version of the effort tier defaults (Operator
  // console › Effort defaults), in the platform company's log beside its `cp.effort_default` row.
  defineEvent(
    "effort_defaults.changed",
    {
      domain: "operations",
      feature: "core",
      subject: "effort_default",
      summary: "A platform admin stored a new version of the effort tier defaults (old → new)",
      consumers: ["audit"],
    },
    z.strictObject({
      version: z.int().min(1),
      from: z.record(z.string(), RunEffortTierSpec),
      to: z.record(z.string(), RunEffortTierSpec),
    }),
  ),
  // LOOP-BREAKER: a platform admin changed the default loop thresholds (the platform company's log).
  defineEvent(
    "loop_breaker.default_changed",
    {
      domain: "operations",
      feature: "core",
      subject: "company",
      summary: "A platform admin changed the platform's default loop thresholds",
      consumers: ["audit"],
    },
    z.strictObject({ from: LoopThresholdsJson, to: LoopThresholdsJson }),
  ),
  // WF-CONTROL: a person's Claude Code connection to Workforce Control, in the log of the company
  // it was made from (and revoked from). Never the token, the code or a hash of either.
  defineEvent(
    "control_token.created",
    {
      domain: "operations",
      feature: "core",
      subject: "control_token",
      summary: "A person made a pairing code for a Claude Code connection (Workforce Control)",
      consumers: ["audit"],
    },
    z.strictObject({
      user_id: Uuid,
      label: z.string(),
      pair_expires_at: Timestamp,
      expires_at: Timestamp,
      mfa: z.boolean(),
    }),
  ),
  defineEvent(
    "control_token.paired",
    {
      domain: "operations",
      feature: "core",
      subject: "control_token",
      summary: "A pairing code was exchanged for its connection's token (Workforce Control)",
      consumers: ["audit"],
    },
    z.strictObject({ user_id: Uuid, key_prefix: z.string(), expires_at: Timestamp }),
  ),
  defineEvent(
    "control_token.revoked",
    {
      domain: "operations",
      feature: "core",
      subject: "control_token",
      summary: "A person revoked one of their Claude Code connections (Workforce Control)",
      consumers: ["audit"],
    },
    z.strictObject({ user_id: Uuid, label: z.string() }),
  ),
  // IMPORT (HARDEN): guided imports of a Zulip or ClickUp export (services/api imports/runs).
  // The job's own writes (spaces, channels, topics, boards, tasks, invitations, identities) emit
  // their usual events with `correlation_id` = the run id; imported messages emit none (history
  // must not wake inboxes, digests or agents; as tools/zulip-import.ts).
  defineEvent(
    "import_run.created",
    {
      ...importOps,
      summary: "An owner uploaded an export to import (stored and previewed)",
      consumers: ["audit"],
    },
    z.strictObject({
      source: ImportRunSource,
      file_name: z.string(),
      file_bytes: z.int().min(0),
    }),
  ),
  defineEvent(
    "import_run.mapping_updated",
    { ...importOps, summary: "An owner changed an import's mapping", consumers: ["audit"] },
    z.strictObject({ source: ImportRunSource }),
  ),
  defineEvent(
    "import_run.queued",
    {
      ...importOps,
      summary: "An owner started an import (the background job picks it up)",
      consumers: ["audit"],
    },
    z.strictObject({ source: ImportRunSource }),
  ),
  defineEvent(
    "import_run.started",
    {
      ...importOps,
      summary: "The import job claimed a queued import (`resumed`: one whose worker died)",
      consumers: [],
    },
    z.strictObject({ source: ImportRunSource, resumed: z.boolean() }),
  ),
  defineEvent(
    "import_run.progressed",
    {
      ...importOps,
      summary: "The import job finished a phase or a batch (the progress bar's change)",
      consumers: [],
    },
    z.strictObject({ phase: z.string(), done: z.int().min(0), total: z.int().min(0) }),
  ),
  defineEvent(
    "import_run.cancel_requested",
    {
      ...importOps,
      summary: "An owner asked a running import to stop (it stops at its next batch)",
      consumers: ["audit"],
    },
    z.strictObject({ phase: z.string().nullable() }),
  ),
  defineEvent(
    "import_run.cancelled",
    {
      ...importOps,
      summary: "An import was cancelled (before it started, or at a batch boundary)",
      consumers: ["audit"],
    },
    z.strictObject({ phase: z.string().nullable() }),
  ),
  defineEvent(
    "import_run.succeeded",
    { ...importOps, summary: "An import finished; its report is on the run", consumers: ["audit"] },
    z.strictObject({
      created: z.record(z.string(), z.int().min(0)),
      updated: z.record(z.string(), z.int().min(0)),
      skipped: z.record(z.string(), z.int().min(0)),
      warnings: z.int().min(0),
    }),
  ),
  defineEvent(
    "import_run.failed",
    {
      ...importOps,
      summary: "An import stopped on an error; what was written stays (a re-run completes it)",
      consumers: ["audit"],
    },
    z.strictObject({ phase: z.string().nullable(), error: z.string() }),
  ),
  // Memory and Recall (M7-03; 07 §Tenancy, §Webhooks) --------------------------------------
  defineEvent(
    "recall.notice_received",
    {
      ...memory,
      subject: "recall_notice",
      summary:
        "A signed Recall webhook delivery recorded once per Recall event id (subject: its derived id)",
      consumers: ["recall.callbacks"],
    },
    z.strictObject({
      recall_event_id: z.string().min(1).max(96),
      kind: z.string().min(1).max(64),
      recall_bucket_id: z.string().min(1).max(96).nullable(),
      at: z.string().max(64).nullable(),
      /** Recall's allow-listed fields of the kind (`WEBHOOK_FIELDS`), as delivered. */
      data: boundedRecord(z.unknown()),
    }),
  ),
  defineEvent(
    "recall.tenant_provisioned",
    {
      ...memory,
      subject: "company",
      summary:
        "The company's Recall tenant and install token are held (created, recovered, rotated)",
      consumers: ["audit"],
    },
    z.strictObject({
      recall_tenant_id: z.string().min(1).max(96),
      slug: z.string().min(1).max(96),
      via: z.enum(["created", "recovered", "rotated"]),
    }),
  ),
  defineEvent(
    "recall.reconciled",
    {
      ...memory,
      subject: "company",
      summary: "Recall members, buckets, groups and grants brought back to the CompanyOS org",
      consumers: ["audit"],
    },
    z.strictObject({
      members_ensured: z.int().min(0),
      members_revoked: z.int().min(0),
      buckets_ensured: z.int().min(0),
      seats_ensured: z.int().min(0),
      seats_revoked: z.int().min(0),
      agent_ensured: z.boolean(),
    }),
  ),
  // M7-05: DE and crew Recall agents (07 §Agent keys per DE) and the hire-as-DE copy.
  defineEvent(
    "recall.agent_key_issued",
    {
      ...memory,
      // AG-MEMORY: a helper's (`workforce_agent`) key too
      subject: ["membership", "workforce_agent"],
      summary:
        "A DE's, crew's or helper's Recall agent key is held sealed (created, recovered, or rotated with its CompanyOS key)",
      consumers: ["audit", "recall.sync", "deploys.recall_keys"],
    },
    z.strictObject({
      agent_kind: z.enum(["digital_employee", "personal_crew", "workforce_agent"]),
      recall_agent_id: z.string().min(1).max(96),
      via: z.enum(["created", "recovered", "rotated"]),
      /** The CompanyOS agent key the Recall key follows (a DE's); null for a crew. */
      for_agent_key_id: Uuid.nullable(),
      grants: z.int().min(0),
    }),
  ),
  defineEvent(
    "recall.agent_revoked",
    {
      ...memory,
      subject: ["membership", "workforce_agent"],
      summary:
        "A DE's, crew's or helper's Recall agent ended (the DE or helper retired or removed, the crew deactivated)",
      consumers: ["audit", "deploys.recall_keys"],
    },
    z.strictObject({
      agent_kind: z.enum(["digital_employee", "personal_crew", "workforce_agent"]),
      recall_agent_id: z.string().min(1).max(96).nullable(),
      reason: z.enum(["retired", "crew_ended", "purged"]),
    }),
  ),
  // MEMORY-HYGIENE: a reviewer settled many memory proposals at once.
  defineEvent(
    "memory_proposals.settled",
    {
      ...memory,
      subject: "company",
      summary:
        "A reviewer approved every exact merge, or dismissed every proposal from a retired agent, in one step",
      consumers: ["audit"],
    },
    z.strictObject({
      action: z.enum(["approve_exact_merges", "dismiss_retired_agents"]),
      count: z.int().min(0),
    }),
  ),
  // RECALL-RULES: a standing rule stated, proposed, adopted, dismissed or lifted.
  defineEvent(
    "memory_rule.changed",
    {
      ...memory,
      subject: "memory_rule",
      summary:
        "A standing rule was stated, proposed (by a person or a DE), adopted, dismissed or lifted",
      consumers: ["audit"],
    },
    z.strictObject({
      action: z.enum(["stated", "proposed", "adopted", "dismissed", "lifted"]),
      scope: z.enum(["company", "de"]),
      de_membership_id: Uuid.nullable(),
      title: z.string(),
      source: z.enum(["setup", "de_page", "chat", "de_suggestion", "recall_proposal"]),
      approval_id: Uuid.nullable(),
    }),
  ),
  // RECALL-PROCEDURES: a DE recorded a run of a procedure (failures too).
  defineEvent(
    "procedure.run_recorded",
    {
      ...memory,
      subject: "procedure_run",
      summary: "A DE recorded how one run of a procedure went (succeeded, failed or abandoned)",
      consumers: ["audit"],
    },
    z.strictObject({
      procedure_title: z.string(),
      page_id: Uuid.nullable(),
      result: z.enum(["succeeded", "failed", "abandoned"]),
      run_id: Uuid.nullable(),
      recall_state: z.enum(["pending", "synced", "refused", "not_offered"]),
    }),
  ),
  // RECALL-CONTRADICTIONS: a person settled one memory contradiction (never an agent).
  defineEvent(
    "memory_contradiction.settled",
    {
      ...memory,
      subject: "company",
      summary: "A person kept one side of a memory contradiction, merged both, or dismissed it",
      consumers: ["audit"],
    },
    z.strictObject({
      contradiction_id: Uuid,
      action: z.enum(["keep_a", "keep_b", "merge", "dismiss"]),
      scope_kind: z.enum(["company", "space", "team", "seat", "agency", "agent"]),
      scope_id: Uuid,
    }),
  ),
  // RECALL-INBOX-LOOP: a person answered a Memory lane card (the audit trail of the lane). No
  // record words: the kind, the card's row, the answer and why it was in their lane.
  defineEvent(
    "memory_lane.decided",
    {
      ...memory,
      subject: "company",
      summary:
        "A person answered a Memory lane card (approve, edit, reject, or a decision's check); " +
        "an answer from outside the card's own department is flagged",
      consumers: ["audit"],
    },
    z.strictObject({
      kind: z.enum([
        "lesson",
        "candidate",
        "dream",
        "rule",
        "settle",
        "contradiction",
        "outcome",
        "archive",
      ]),
      ref_id: Uuid,
      action: z.enum(["approve", "reject", "edit_approve", "worked", "did_not", "too_early"]),
      result: z.enum(["done", "waiting"]),
      why_here: z.enum(["yours", "served", "escalated", "owner_all"]),
      /** Answered by someone who is not the card's own department/served person. */
      cross_department: z.boolean(),
      /** Bulk: one of many approved in a single click. */
      bulk: z.boolean(),
    }),
  ),
  // RECALL-INBOX-LOOP: one click approved many cards (each is also audited as memory_lane.decided).
  defineEvent(
    "memory_lane.bulk_approved",
    {
      ...memory,
      subject: "company",
      summary: "A person approved every eligible Memory lane card in one click",
      consumers: ["audit"],
    },
    z.strictObject({
      approved: z.int().min(0),
      waiting: z.int().min(0),
      skipped: z.int().min(0),
    }),
  ),
  // RECALL-INBOX-LOOP: the owner/CEO "See all agents' memory" switch changed (audited).
  defineEvent(
    "memory_lane.see_all_changed",
    {
      ...memory,
      subject: "company",
      summary: "An owner or the CEO switched 'See all agents' memory' on or off",
      consumers: ["audit"],
    },
    z.strictObject({ membership_id: Uuid, on: z.boolean() }),
  ),
  // RECALL-ADMIN-MEMORY: an admin's act in Setup › Memory (audited; a card waiting on the memory
  // service is not an act and says nothing).
  defineEvent(
    "memory_admin.acted",
    {
      ...memory,
      subject: "company",
      summary:
        "An admin changed memory in Setup: flagged, archived or restored a record, set a charter or agent access, or tended a space's rules",
      consumers: ["audit"],
    },
    z.strictObject({
      op: z.enum([
        "record_flag",
        "record_archive",
        "record_restore",
        "charter",
        "agent_access",
        "always_on_add",
        "always_on_remove",
        "always_on_move",
        "retire",
        "lift",
        "veto",
        "unveto",
      ]),
      space_key: z.string(),
      ref: z.string().nullable(),
      result: z.enum(["done", "refused"]),
      cross_department: z.boolean(),
    }),
  ),
  // RECALL-ADMIN-MEMORY: an admin filed, or the operator closed, a job for what Recall keeps
  // signed-in only.
  defineEvent(
    "memory_admin.job_changed",
    {
      ...memory,
      subject: "company",
      summary:
        "A batch settle or clearing of injection flags was filed for the operator, or the operator closed it",
      consumers: ["audit"],
    },
    z.strictObject({
      job_id: Uuid,
      kind: z.enum(["batch_settle", "clear_injection_flags"]),
      space_key: z.string(),
      state: z.enum(["open", "done", "failed"]),
    }),
  ),
  // MEMORY-QUALITY-WATCH: the facts check filed a bulk "archive these" suggestion for a space's
  // owner (the records are named in the suggestion row, never in the event).
  defineEvent(
    "memory_quality.archive_suggested",
    {
      ...memory,
      subject: "company",
      summary:
        "Records that the facts of record show are out of date were put in the space owner's Memory lane as one archive suggestion",
      consumers: ["audit"],
    },
    z.strictObject({
      suggestion_id: Uuid,
      space_key: z.string(),
      records: z.int().min(1),
      source: z.enum(["facts_check", "sweep"]),
    }),
  ),
  // MEMORY-QUALITY-WATCH: an approved archive suggestion was carried out through the install door.
  defineEvent(
    "memory_quality.archive_applied",
    {
      ...memory,
      subject: "company",
      summary: "An approved archive suggestion archived its records in the memory service",
      consumers: ["audit"],
    },
    z.strictObject({
      suggestion_id: Uuid,
      space_key: z.string(),
      archived: z.int().min(0),
      refused: z.int().min(0),
      result: z.enum(["applied", "failed"]),
    }),
  ),
  // RECALL-INBOX-LOOP: a decider answered a decision's "did it work" check.
  defineEvent(
    "decision.outcome_answered",
    {
      ...memory,
      subject: "decision",
      summary: "A decision's 'did it work' check was answered: worked, did not, or too early",
      consumers: ["audit"],
    },
    z.strictObject({
      decision_id: Uuid,
      outcome: z.enum(["worked", "did_not", "too_early"]),
      source: z.enum(["meeting", "issue", "approval", "manual", "recall"]),
    }),
  ),
  // RECALL-KB: a manager switched a space's Sync to Recall, its agent access, or asked a sync now.
  defineEvent(
    "recall.kb_configured",
    {
      ...memory,
      subject: "space",
      summary:
        "A space's Sync to Recall switch or agent access changed, or a sync was asked for now",
      consumers: ["audit"],
    },
    z.strictObject({
      sync_enabled: z.boolean(),
      agent_access: z.enum(["none", "read", "read_write"]),
      change: z.enum(["configured", "sync_requested"]),
    }),
  ),
  // RECALL-RETENTION-HOLD-FORGET: one event for every change to what the company's memory keeps,
  // holds or forgets. Ids and words only; never the text of a record and never a confirm token.
  defineEvent(
    "memory.governance_changed",
    {
      ...memory,
      subject: "company",
      summary:
        "A pin against cooling, a forget (previewed, confirmed or undone), a legal hold, a release or legal-contact request for the operator, or the retention policy changed",
      consumers: ["audit"],
    },
    z.strictObject({
      kind: z.enum(["keep", "forget", "hold", "operator_job", "retention", "person"]),
      action: z.string().min(1).max(40),
      /** The row it is about (keep, forget request, hold, job); null for the company policy. */
      ref_id: Uuid.nullable(),
      /** `forget`/`hard`, or the hold's scope, or the job's kind. */
      detail: z.string().max(80).nullable(),
      /** The memory service did not offer the door: nothing was sent, the request stands here. */
      waiting: z.boolean(),
    }),
  ),
  defineEvent(
    "recall.memory_copied",
    {
      ...memory,
      subject: "recall_memory_copy",
      summary:
        "A hire-as-DE copy of the crew's chosen buckets into the new DE's seat bucket finished (or failed for good)",
      consumers: ["audit"],
    },
    z.strictObject({
      de_membership_id: Uuid,
      crew_membership_id: Uuid,
      state: z.enum(["done", "failed"]),
      sources: z.int().min(0),
      created: z.int().min(0),
      reinforced: z.int().min(0),
      proposed: z.int().min(0),
    }),
  ),
  defineEvent(
    "promotion.proposed",
    {
      ...memory,
      subject: "promotion",
      summary: "A Recall page candidate waits for its scope's reviewer (07 `proposal.waiting`)",
      consumers: ["inbox (reviewer)", "rt"],
    },
    z.strictObject({
      memory_ref: z.string().min(1).max(256),
      suggested_title: z.string(),
      body: z.string(),
      scope_kind: MemoryScopeKind,
      scope_id: Uuid,
      reviewer_seat_id: Uuid.nullable(),
      approval_id: Uuid.nullable(),
    }),
  ),
  // M7-04: the reviewer's decision (`POST promotions/{id}/accept | reject`).
  defineEvent(
    "promotion.accepted",
    {
      ...memory,
      subject: "promotion",
      summary: "A Recall page candidate accepted: it became a Knowledge page",
      consumers: ["rt", "recall (mark promoted, once Recall has references: gap 4)"],
    },
    z.strictObject({
      memory_ref: z.string().min(1).max(256),
      page_id: Uuid,
      space_id: Uuid,
      decided_by_membership_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "promotion.rejected",
    {
      ...memory,
      subject: "promotion",
      summary: "A Recall page candidate rejected: no page",
      consumers: ["rt", "recall (mark rejected, once Recall has references: gap 4)"],
    },
    z.strictObject({
      memory_ref: z.string().min(1).max(256),
      reason: z.string().nullable(),
      decided_by_membership_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "decision.recorded",
    {
      ...memory,
      subject: "decision",
      summary: "A decision appended to the decision log (07 `decision.outcome`: source recall)",
      consumers: ["inbox (decider)", "portfolio projection (shared)", "recall"],
    },
    z.strictObject({
      source: DecisionSource,
      source_ref: z.string().nullable(),
      title: z.string(),
      decided_by_seat_id: Uuid.nullable(),
      review_at: Timestamp.nullable(),
      share_to_portfolio: z.boolean(),
    }),
  ),
  // RECALL-BRIEFS: a person closed their "While you were away" card.
  defineEvent(
    "memory.catch_up_closed",
    {
      ...memory,
      subject: "membership",
      summary: "A person closed their catch-up card for one window (it is not shown again)",
      consumers: ["audit"],
    },
    z.strictObject({ window_key: z.string() }),
  ),
  // RECALL-ROLES-ONBOARDING: a person accepted (maybe edited) or discarded the profile drafted
  // from their seat.
  defineEvent(
    "memory.profile_decided",
    {
      ...memory,
      subject: "membership",
      summary: "A person accepted or discarded the profile drafted from their seat",
      consumers: ["audit"],
    },
    z.strictObject({
      draft_id: Uuid,
      decision: z.enum(["accepted", "discarded"]),
      edited: z.boolean(),
    }),
  ),
  // RECALL-ROLES-ONBOARDING: the new holder answered "take over this seat's memory".
  defineEvent(
    "memory.succession_answered",
    {
      ...memory,
      subject: "seat",
      summary: "The new holder of a seat accepted or declined taking over the seat's memory",
      consumers: ["audit"],
    },
    z.strictObject({
      succession_id: Uuid,
      answer: z.enum(["accept", "decline"]),
      by_membership_id: Uuid,
    }),
  ),
  // RECALL-ROLES-ONBOARDING: an admin cancelled or applied a seat's memory handover.
  defineEvent(
    "memory.succession_settled",
    {
      ...memory,
      subject: "seat",
      summary: "An admin cancelled a seat's memory handover, or applied it to a vacant seat",
      consumers: ["audit"],
    },
    z.strictObject({ succession_id: Uuid, how: z.enum(["cancel", "apply"]) }),
  ),
  // RECALL-ROLES-ONBOARDING: an acting holder's cover over a seat's memory ended.
  defineEvent(
    "memory.acting_ended",
    {
      ...memory,
      subject: "seat",
      summary: "An acting holder's cover over a seat's memory ended",
      consumers: ["audit"],
    },
    z.strictObject({ membership_id: Uuid, why: z.enum(["asked", "handed_over"]) }),
  ),
  // RECALL-ROLES-ONBOARDING: a person's onboarding with the Chief of Staff DE began, ended or was
  // put aside.
  defineEvent(
    "memory.onboarding_changed",
    {
      ...memory,
      subject: "membership",
      summary:
        "A person's onboarding with the Chief of Staff DE started, finished or was dismissed",
      consumers: ["audit"],
    },
    z.strictObject({
      depth: z.enum(["deep", "medium", "quick"]),
      state: z.enum(["offered", "asking", "done", "dismissed"]),
    }),
  ),
  // RECALL-ROLES-ONBOARDING: a charter was written for the company space or one space.
  defineEvent(
    "memory.charter_set",
    {
      ...memory,
      subject: ["company", "space"],
      summary: "The charter of the company space or of one space was written",
      consumers: ["audit"],
    },
    z.strictObject({
      source: z.enum(["creation", "vision", "manual"]),
      edited_by_membership_id: Uuid.nullable(),
    }),
  ),
  // RECALL-KPIS-MIRROR: a person agreed with or objected to a logged decision.
  defineEvent(
    "decision.stance_recorded",
    {
      ...memory,
      subject: "decision",
      summary:
        "A person ratified or dissented from a logged decision (sent to Recall as that person when the decision is in memory)",
      consumers: ["audit"],
    },
    z.strictObject({
      membership_id: Uuid,
      stance: z.enum(["ratify", "dissent"]),
      recall_state: z.enum(["sent", "waiting", "refused", "local"]),
    }),
  ),
  defineEvent(
    "reminder.created",
    {
      ...memory,
      subject: "reminder",
      summary:
        "A reminder for a seat (07: a Recall commitment with no board to hold a task) or, v2, for one person (a chat message's 'Remind me', `/remind`)",
      consumers: ["inbox", "rt", "reminder scheduler (M9-01)"],
      version: 2,
      previous: [
        {
          version: 1,
          payload: ReminderCreatedV1,
          upgrade: (p) => ({ ...(p as object), membership_id: null }),
        },
      ],
    },
    z.strictObject({
      /** v2 (CHAT-SWITCH-A): null for a person's own reminder. */
      seat_id: Uuid.nullable(),
      /** v2 (CHAT-SWITCH-A): the person a personal reminder is for (null for a seat's). */
      membership_id: Uuid.nullable(),
      at: Timestamp,
      origin: ReminderOrigin,
      origin_ref: z.string().nullable(),
      subject_kind: z.string().nullable(),
      subject_id: Uuid.nullable(),
    }),
  ),
  // M4-03: reminders fire into the seat holders' inboxes (job `reminders.fire`; core: a reminder
  // is the person's, only the crew's `remind` proposal needs `agents`).
  defineEvent(
    "reminder.fired",
    {
      ...memory,
      feature: "core",
      subject: "reminder",
      summary:
        "A reminder came due: one inbox item for each person holding its seat (v2: or for the one person it is for)",
      consumers: ["rt", "audit"],
      version: 2,
      previous: [
        {
          version: 1,
          payload: ReminderFiredV1,
          upgrade: (p) => ({ ...(p as object), membership_id: null }),
        },
      ],
    },
    z.strictObject({
      seat_id: Uuid.nullable(),
      membership_id: Uuid.nullable(),
      at: Timestamp,
      recipients: z.int().min(0),
    }),
  ),
  defineEvent(
    "reminder.dismissed",
    {
      ...memory,
      feature: "core",
      subject: "reminder",
      summary: "A holder of the reminder's seat (v2: or its person) dismissed it",
      consumers: ["rt", "audit"],
      version: 2,
      previous: [
        {
          version: 1,
          payload: ReminderDismissedV1,
          upgrade: (p) => ({ ...(p as object), membership_id: null }),
        },
      ],
    },
    z.strictObject({
      seat_id: Uuid.nullable(),
      membership_id: Uuid.nullable(),
      dismissed_by_membership_id: Uuid,
    }),
  ),
  // ACTIONS: a holder of the seat changed an open reminder's text or time.
  defineEvent(
    "reminder.updated",
    {
      ...memory,
      feature: "core",
      subject: "reminder",
      summary: "A holder of the reminder's seat changed its text or time",
      consumers: ["rt", "audit"],
    },
    z.strictObject({ seat_id: Uuid, at: Timestamp, fields: z.array(z.enum(["text", "at"])) }),
  ),
  // M5-05: break-glass (05 "Break-glass"; 03 §Access and audit). The session is a control-plane
  // row; each transition is also this company's event, so its admins see the trail in their own
  // audit log next to the platform's (`cp.platform_audit`).
  defineEvent(
    "access.break_glass_requested",
    {
      ...breakGlass,
      summary: "An agency user asked for time-boxed elevated access (waits for a second admin)",
      consumers: ["audit", "inbox (company owners)"],
    },
    z.strictObject({ ...BreakGlassRef, reason: z.string(), duration_minutes: z.int() }),
  ),
  defineEvent(
    "access.break_glass_started",
    {
      ...breakGlass,
      summary: "A break-glass session was approved and is live until expires_at",
      consumers: ["audit", "inbox (company admins)", "rt banner"],
    },
    z.strictObject({
      ...BreakGlassRef,
      reason: z.string(),
      approver: BreakGlassPerson.extend({ kind: z.enum(["agency_admin", "company_owner"]) }),
      starts_at: Timestamp,
      expires_at: Timestamp,
    }),
  ),
  defineEvent(
    "access.break_glass_rejected",
    {
      ...breakGlass,
      summary: "A break-glass request was refused",
      consumers: ["audit"],
    },
    z.strictObject({
      ...BreakGlassRef,
      decider: BreakGlassPerson.extend({ kind: z.enum(["agency_admin", "company_owner"]) }),
      reason: z.string(),
    }),
  ),
  defineEvent(
    "access.break_glass_ended",
    {
      ...breakGlass,
      summary:
        "A break-glass session ended (by a person), expired, or its request lapsed undecided",
      consumers: ["audit", "inbox (company admins)", "rt banner"],
    },
    z.strictObject({
      ...BreakGlassRef,
      outcome: z.enum(["ended", "expired", "request_expired"]),
      /** Who ended it; null when the clock did. */
      ended_by: BreakGlassPerson.nullable(),
      reason: z.string().nullable(),
      started_at: Timestamp.nullable(),
      ended_at: Timestamp,
    }),
  ),
  // BREAK-GLASS: a resource read only because a break-glass session is live (05 "every read and
  // write under break-glass is logged … and emitted"). One per resource per minute, with the row
  // of `cell.break_glass_read` and the `cp.platform_audit` row (`break_glass.used`).
  defineEvent(
    "access.break_glass_read",
    {
      ...breakGlass,
      summary:
        "An agency user read a resource under a live break-glass session (one per resource per minute)",
      consumers: ["audit", "Setup › Break-glass log"],
    },
    z.strictObject({
      agency_id: Uuid,
      read_by: BreakGlassPerson,
      read_id: Uuid,
      resource_kind: BreakGlassReadSubjectKind,
      resource_id: Uuid,
      route: z.string(),
      read_at: Timestamp,
    }),
  ),
  // M5-05: the portfolio push (12 §Events; 02 "cells push frozen per-company roll-ups").
  defineEvent(
    "portfolio_projection.published",
    {
      domain: "founder_os",
      feature: "core",
      subject: "company",
      summary: "A per-company roll-up pushed to the control plane (new version of one kind)",
      consumers: [
        "control plane store",
        "portfolio_view_cache (M10-02)",
        "focus layer",
        "alert worker",
        "founder brief",
      ],
    },
    z.strictObject({
      company_id: Uuid,
      period_key: z.string(),
      kind: PortfolioProjectionKind,
      version: z.int().min(1),
      snapshot: z.record(z.string(), z.unknown()),
    }),
  ),
  // FOUNDER-OS (12 §Events): the account and its portfolio company, the companies it holds and
  // their linked head seats, the cascade of portfolio priorities and the founder brief. Each is
  // written in the PORTFOLIO company's log (subject: the portfolio company, its goal or its
  // brief); nothing is written into an operating company by a portfolio route.
  defineEvent(
    "account.created",
    {
      domain: "founder_os",
      feature: "core",
      subject: "company",
      summary: "An account and its Founder OS portfolio company were created",
      consumers: ["portfolio company (founder seat and membership)", "founder crew (later)"],
    },
    z.strictObject({ account_id: Uuid, name: z.string(), owner_user_id: Uuid }),
  ),
  defineEvent(
    "portfolio.company_added",
    {
      domain: "founder_os",
      feature: "core",
      subject: "company",
      summary: "A company joined the account's portfolio (owned, or a client through an agency)",
      consumers: ["focus layer", "switcher", "projection subscription"],
    },
    z.strictObject({ account_id: Uuid, company_id: Uuid, relation: AccountCompanyRelation }),
  ),
  defineEvent(
    "portfolio.company_removed",
    {
      domain: "founder_os",
      feature: "core",
      subject: "company",
      summary: "A company left the account's portfolio (its links ended)",
      consumers: ["focus layer", "switcher"],
    },
    z.strictObject({ account_id: Uuid, company_id: Uuid }),
  ),
  defineEvent(
    "linked_seat.created",
    {
      domain: "founder_os",
      feature: "core",
      subject: "company",
      summary: "A company's head seat was linked under a portfolio seat",
      consumers: ["portfolio org chart"],
    },
    z.strictObject({
      account_id: Uuid,
      linked_seat_id: Uuid,
      portfolio_seat_id: Uuid,
      company_id: Uuid,
      company_seat_id: Uuid,
    }),
  ),
  defineEvent(
    "linked_seat.removed",
    {
      domain: "founder_os",
      feature: "core",
      subject: "company",
      summary: "A linked head seat left the portfolio org chart",
      consumers: ["portfolio org chart"],
    },
    z.strictObject({ account_id: Uuid, linked_seat_id: Uuid, company_id: Uuid }),
  ),
  // FOUNDER-OS phase 1: the founder re-parents a company's card (e.g. under a co-founder seat).
  defineEvent(
    "linked_seat.moved",
    {
      domain: "founder_os",
      feature: "core",
      subject: "company",
      summary: "A linked head seat moved under another seat of the founder chart",
      consumers: ["portfolio org chart"],
    },
    z.strictObject({
      account_id: Uuid,
      linked_seat_id: Uuid,
      company_id: Uuid,
      from_seat_id: Uuid,
      portfolio_seat_id: Uuid,
    }),
  ),
  defineEvent(
    "goal.cascaded",
    {
      domain: "founder_os",
      feature: "core",
      subject: "goal",
      summary: "A portfolio priority was linked down to a company priority (goal_link)",
      consumers: ["portfolio goals roll-up", "focus layer"],
    },
    z.strictObject({
      link_id: Uuid,
      account_id: Uuid,
      company_id: Uuid,
      company_goal_id: Uuid,
    }),
  ),
  defineEvent(
    "goal.uncascaded",
    {
      domain: "founder_os",
      feature: "core",
      subject: "goal",
      summary: "A cascade link of a portfolio priority was removed",
      consumers: ["portfolio goals roll-up"],
    },
    z.strictObject({
      link_id: Uuid,
      account_id: Uuid,
      company_id: Uuid,
      company_goal_id: Uuid,
    }),
  ),
  defineEvent(
    "founder_brief.generated",
    {
      domain: "founder_os",
      feature: "core",
      subject: "founder_brief",
      summary: "The founder brief was generated from the projections (system or founder crew)",
      consumers: ["#brief (later)", "inbox (later)"],
    },
    z.strictObject({
      cadence: FounderBriefCadence,
      period_key: z.string(),
      generated_by: FounderBriefSource,
      lines: z.int().min(0),
    }),
  ),
  // ── Parity P1 (task detail, layout, prefs, undo) ──
  defineEvent(
    "task.undone",
    {
      ...work,
      subject: "task",
      summary: "A write was undone from its undo record (the compensation's own events follow it)",
      consumers: ["rt", "activity (Undo line)"],
    },
    z.strictObject({
      undo_id: Uuid,
      kind: z.string().min(1).max(64).describe("The compensation's kind (`task.status`, …)"),
      undone_event_id: Uuid.nullable().describe("The event of the write that was undone"),
      board_id: Uuid,
      // DECISION-LEDGER: whose write was undone (absent on older events), so an undo of an
      // agent's write is its reversal; how the undoer may undo it (`supervisor`: they hold the
      // seat the agent's seat reports to); and why, when they said. Shown on the trail.
      undone_actor_kind: z.string().max(32).optional(),
      undone_membership_id: Uuid.nullable().optional(),
      undone_seat_id: Uuid.nullable().optional(),
      undone_run_id: Uuid.nullable().optional(),
      as_supervisor: z.boolean().optional(),
      reason: z.string().max(500).nullable().optional(),
    }),
  ),
  // CANVAS-VOICE: a person (the voice front, as them) placed an op on their own Live session
  // (`live/canvas/ops`). The op itself is the `cell.canvas_op` row, announced on `cos_live`;
  // the event is its audit line (what kind of op, which session, how many blocks).
  defineEvent(
    "canvas.op_written",
    {
      ...work,
      subject: "company",
      summary:
        "A person placed a show, arrange, highlight, clear or focus_view op on their Live session",
      consumers: ["audit"],
    },
    z.strictObject({
      op_id: Uuid,
      session_id: Uuid,
      // LIVE-WORKSPACE: `focus_view` added (CANVAS-BLOCKS); the four earlier verbs unchanged.
      // LIVE-DESKTOP: `tidy` and `place` added.
      verb: z.enum(["show", "arrange", "highlight", "clear", "focus_view", "tidy", "place"]),
      blocks: z.int().min(0).max(16).describe("Blocks a show kept (the caller's own reach)"),
    }),
  ),
  // P1 wave 1: a person's preference saved (`me/prefs`; the value stays out of the event).
  defineEvent(
    "pref.changed",
    {
      ...work,
      // an agency operator has no membership: its prefs are the company's subject, by user id
      subject: ["membership", "company"],
      summary: "A person saved one of their preferences (drawer rail, folded sections, …)",
      consumers: ["rt (the person's other tabs)"],
    },
    z.strictObject({ key: z.string().min(1).max(64) }),
  ),
  // P1 wave 1 (design §3.3): a board's task layout or a space's default saved as a new version
  // (`reset`: a board back to its space default, a space back to the built-in one).
  defineEvent(
    "board.task_layout_changed",
    {
      ...work,
      subject: "board",
      summary: "A board's task layout was saved as a new version",
      consumers: ["rt"],
    },
    z.strictObject({
      version: z.int().min(1),
      reset: z.boolean(),
      restored_from: z.int().min(1).nullable(),
    }),
  ),
  defineEvent(
    "space.default_task_layout_changed",
    {
      ...work,
      subject: "space",
      summary: "A space's default task layout was saved as a new version",
      consumers: ["rt"],
    },
    z.strictObject({
      version: z.int().min(1),
      reset: z.boolean(),
      restored_from: z.int().min(1).nullable(),
      boards_affected: z.int().min(0).describe("Boards of the space that use the default"),
    }),
  ),
  defineEvent(
    "field.required_changed",
    {
      ...work,
      subject: "field_def",
      summary: "A field's requirement stage or always-show rule changed",
      consumers: ["rt"],
    },
    z.strictObject({
      board_id: Uuid,
      required_on: z.enum(["none", "create", "complete", "both", "enter"]),
      // BOARD-GATES: an `enter` rule's statuses (absent on other stages and older events)
      required_statuses: z.array(z.string()).optional(),
      always_show: z.boolean(),
      /** T4-VIEWS-MEDIA: absent in events written before the flag existed. */
      show_on_card: z.boolean().optional(),
    }),
  ),
  defineEvent(
    "task.observation_recorded",
    {
      ...work,
      subject: "task",
      summary: "A metric observation was recorded on a task (or a correction of one)",
      consumers: ["rt", "activity"],
    },
    z.strictObject({
      board_id: Uuid,
      observation_id: Uuid,
      metric: z.string(),
      value: z.number(),
      unit: z.string().nullable(),
      observed_at: Timestamp,
      supersedes_id: Uuid.nullable(),
    }),
  ),

  // ── Parity P2 (conversations, inbox, memory) ──
  // (wave 1 additions go here)

  // ── Parity P3a (views, order) ──
  // (wave 1 additions go here)

  // ── Parity P3b (board settings, fields, dashboard, create) ──
  // (wave 1 additions go here)

  // ── Parity P4 (lifecycle, access, structure) ──
  defineEvent(
    "access.changed",
    {
      domain: "access",
      feature: "core",
      subject: ["space", "board", "page"],
      summary:
        "A Share grant was set or removed, or a board/page restricted or lifted; subject is what it covers",
      consumers: ["access.invalidate (live ping to drop the subject, follow pruning)", "audit"],
    },
    z.strictObject({
      change: AccessChange,
      subject_kind: AccessSubjectKind,
      subject_id: Uuid,
      /** The grant's principal (null for a restriction). */
      principal_kind: AccessPrincipalKind.nullable(),
      principal_id: Uuid.nullable(),
      /** The level after the change (null: revoked, or a restriction). */
      level: AccessGrantLevel.nullable(),
      /** The level before (null: a new grant, or a restriction). */
      previous_level: AccessGrantLevel.nullable(),
      /** `access_version.version` after the change (fix #25). */
      access_version: z.int().min(1),
    }),
  ),

  // ── Parity P5 (process engine) ──
  // Automations (design §6.1): a rule's lifecycle, each firing and each refusal. A firing's own
  // effects emit their usual events with the actor `system` acting for the rule's owner and
  // `correlation_id` = the firing (`automation_run.id`), which is how a later firing reads its
  // loop depth.
  defineEvent(
    "automation.created",
    {
      ...work,
      subject: "automation",
      summary: "An automation rule was created (by a person, or an agent's approved proposal)",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      name: z.string(),
      scope: AutomationScope,
      board_id: Uuid.nullable(),
      trigger_type: z.string(),
      owner_membership_id: Uuid,
      source: AutomationSource,
      approval_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "automation.updated",
    {
      ...work,
      subject: "automation",
      summary: "A rule's definition, order, budget or switch changed",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      /** The fields that changed (`trigger`, `conditions`, `actions`, `enabled`, …). */
      fields: z.array(z.string()).min(1),
      enabled: z.boolean(),
      version: z.int().min(1),
    }),
  ),
  defineEvent(
    "automation.deleted",
    { ...work, subject: "automation", summary: "A rule was deleted", consumers: ["rt", "audit"] },
    z.strictObject({ name: z.string(), board_id: Uuid.nullable() }),
  ),
  defineEvent(
    "automation.fired",
    {
      ...work,
      subject: "automation",
      summary: "A rule fired: its actions applied (all, or some with the rest refused)",
      consumers: ["rt (History)", "audit"],
    },
    z.strictObject({
      run_id: Uuid,
      outcome: AutomationRunOutcome,
      depth: z.int().min(0),
      effect_count: z.int().min(0),
      trigger_event_id: Uuid.nullable(),
      subject_kind: z.string().nullable(),
      subject_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "automation.refused",
    {
      ...work,
      subject: "automation",
      summary: "A rule matched but did not run (loop depth, effect budget, owner's access)",
      consumers: ["rt (History)", "audit"],
    },
    z.strictObject({
      run_id: Uuid,
      reason: z.string(),
      depth: z.int().min(0),
      trigger_event_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "automation.paused",
    {
      ...work,
      subject: "automation",
      summary: "A rule paused itself: its owner lost access, or it spent its effect budget",
      consumers: ["rt", "proj.inbox (admin notice)", "audit"],
    },
    z.strictObject({ reason: AutomationPauseReason, owner_membership_id: Uuid }),
  ),
  // Due reminders (design §6.1): once per task, kind and day; triggers of automations too.
  defineEvent(
    "task.due_soon",
    {
      ...work,
      subject: "task",
      summary: "A task is due within 24 hours (reminder sent to its assignees)",
      consumers: ["automations", "push"],
    },
    z.strictObject({ board_id: Uuid, due_at: Timestamp, recipients: z.int().min(0) }),
  ),
  defineEvent(
    "task.overdue",
    {
      ...work,
      subject: "task",
      summary: "A task is past its due date and not done (reminder sent to its assignees)",
      consumers: ["automations", "push"],
    },
    z.strictObject({ board_id: Uuid, due_at: Timestamp, recipients: z.int().min(0) }),
  ),
  // BOARD-RESULTS: a published card's +24 h / +72 h readings are due (once per window).
  defineEvent(
    "task.results_due",
    {
      ...work,
      subject: "task",
      summary:
        "A published card's 24 h or 72 h results are due and missing (a quiet Inbox line to its owner)",
      consumers: ["proj.inbox (the job writes the line)"],
    },
    z.strictObject({
      board_id: Uuid,
      window: z.enum(["24h", "72h"]),
      posted_at: Timestamp,
      recipients: z.int().min(0),
    }),
  ),
  // Recurring work controls (design §6.2): a person's pause, resume, skip and take-over.
  defineEvent(
    "recurring.paused",
    {
      ...org,
      subject: "recurring_work",
      summary: "Recurring work paused from its controls",
      consumers: ["scheduler", "audit"],
    },
    z.strictObject({ responsibility_id: Uuid }),
  ),
  defineEvent(
    "recurring.resumed",
    {
      ...org,
      subject: "recurring_work",
      summary: "Recurring work resumed from the next occurrence after now",
      consumers: ["scheduler", "audit"],
    },
    z.strictObject({ responsibility_id: Uuid, next_run_at: Timestamp.nullable() }),
  ),
  defineEvent(
    "recurring.skipped",
    {
      ...org,
      subject: "recurring_work",
      summary: "The next occurrence was skipped (no task); the schedule moved past it",
      consumers: ["scheduler", "audit"],
    },
    z.strictObject({
      responsibility_id: Uuid,
      skipped: Timestamp,
      next_run_at: Timestamp.nullable(),
    }),
  ),
  defineEvent(
    "recurring.taken_over",
    {
      ...org,
      subject: "recurring_work",
      summary: "A person took the work over: this occurrence, or all future ones",
      consumers: ["audit"],
    },
    z.strictObject({
      responsibility_id: Uuid,
      seat_id: Uuid,
      scope: RecurringTakeoverScope,
      task_id: Uuid.nullable(),
    }),
  ),

  // ── Parity P6a (policy, proposals, approvals) ──
  // D10 scope editor: a manager set a seat's agent policy modes (the Permissions tab). The event
  // is the audit record: who (the envelope's actor), which classes, from and to.
  defineEvent(
    "agent_policy.changed",
    {
      ...agents,
      subject: "seat",
      summary: "A manager changed a seat's agent policy (Permissions tab: Direct / Ask / Never)",
      consumers: ["audit", "agent runs (next decision reads the rows)"],
    },
    z.strictObject({
      scope: z.literal("seat"),
      changes: z
        .array(
          z.strictObject({
            class: AgentActionClass,
            from: AgentPolicyMode,
            to: AgentPolicyMode,
          }),
        )
        .min(1),
      /** The policy version after the change (the next `If-Match`). */
      version: z.string(),
    }),
  ),
  // MANAGED-POLICY: policy enforcement at dispatch. An agent's tool call (the MCP server, before
  // the handler runs) or a run's admission (the trigger's board) the policy denies: the audit
  // record of the refusal, with the class, the level the verdict came from and why.
  defineEvent(
    "agent_policy.dispatch_refused",
    {
      ...agents,
      subject: "membership",
      summary: "The agent policy refused an agent's tool call or run at dispatch",
      consumers: ["audit"],
    },
    z.strictObject({
      /**
       * `tool_call`: an MCP tool before it ran; `run_admission`: a run before it was queued;
       * `rest_call` (CORE-KEYS): an agent's own REST write (a self-hosted DE's key or a run
       * token), `tool` then naming the route as `rest:{route id}`.
       */
      stage: z.enum(["tool_call", "run_admission", "rest_call"]),
      seat_id: Uuid.nullable(),
      run_id: Uuid.nullable(),
      /** The MCP tool (`tool_call`), or `rest:{route id}` (`rest_call`). */
      tool: z.string().max(64).nullable(),
      class: AgentActionClass,
      /** Where the verdict came from: `company`, `seat`, `board`, `fence`, `never`, `default`. */
      level: z.string().max(32),
      board_ids: z.array(Uuid).max(20),
      code: ApprovalRefusalCode,
      reason: z.string().max(500),
    }),
  ),
  // DECISION-LEDGER (roadmap Phase 1 item 1): one verdict of the agent policy on an agent's
  // write, with its `cell.agent_decision` row — `allow_in_run` in the write's own transaction
  // (the write committed with it), `deny` / `propose` beside a dispatch refusal. Consumer
  // `agent_record.feed` counts the allowed ones as the DE's `decision.decided`.
  defineEvent(
    "agent_policy.decided",
    {
      ...agents,
      subject: "membership",
      summary: "The agent policy decided one of an agent's writes (the decision ledger's row)",
      consumers: ["agent_record.feed (decided)", "audit"],
    },
    z.strictObject({
      decision_id: Uuid,
      /**
       * `in_run`: a write route's or tool write's gate; `tool_call` / `run_admission`: dispatch;
       * `rest_call` (rel/wave33): a self-hosted DE's REST write refused at its own request.
       */
      stage: z.enum(["in_run", "tool_call", "run_admission", "rest_call"]),
      seat_id: Uuid.nullable(),
      run_id: Uuid.nullable(),
      tool: z.string().max(64).nullable(),
      class: z.string().max(64),
      verdict: z.enum(["allow_in_run", "propose", "deny"]),
      /** Where the verdict came from: `company`, `seat`, `board`, `fence`, `never`, `default`. */
      level: z.string().max(32),
      /**
       * Core plan item 3: the rulings the verdict followed or cited (CORE-PRECEDENTS `cell.ruling`
       * ids, the same ids an Ask filed from that verdict carries as `approval.precedent_ids`).
       */
      precedent_ids: z.array(z.string().min(1).max(128)).max(10).optional(),
    }),
  ),
  // AGENT-GUARDRAILS: an agent's write the guardrails refused (a destructive route, or past its
  // hourly cap — then it is paused and `recipients` get an Inbox notice).
  defineEvent(
    "agent.guardrail_tripped",
    {
      ...agents,
      subject: "membership",
      summary: "An agent's write was refused by the guardrails (a destructive route or its cap)",
      consumers: ["audit", "inbox (when paused: the people it serves, company admins)"],
    },
    z.strictObject({
      /** `never_route` / `ask_route`: a destructive route; `*_per_hour`: the hourly cap. */
      rule: z.enum(["never_route", "ask_route", "writes_per_hour", "task_changes_per_hour"]),
      /** The contract route refused (null when the path matches none). */
      route: z.string().max(64).nullable(),
      run_id: Uuid.nullable(),
      count: z.number().int().nonnegative().nullable(),
      cap: z.number().int().nonnegative().nullable(),
      paused: z.boolean(),
      recipients: z.array(Uuid).max(50),
      summary: z.string().max(300),
    }),
  ),
  // CORE-FREEZE (core plan item 7): an incident opened — a person froze the company or one DE.
  // The subject is what is frozen (the company, or the DE); counts only, never content.
  defineEvent(
    "incident.opened",
    {
      ...agents,
      subject: ["company", "digital_employee"],
      summary:
        "An incident opened: a freeze of the company or one DE (writes refused, runs and sends held)",
      consumers: ["audit", "rt"],
    },
    z.strictObject({
      incident_id: Uuid,
      kind: z.enum(["freeze", "loop", "budget", "guardrail", "reversal"]),
      scope: z.enum(["company", "digital_employee"]),
      membership_id: Uuid.nullable(),
      outbox_mode: z.enum(["hold", "cancel"]).nullable(),
      /** The blast radius when it opened, as counts (the ids are on the incident). */
      runs_held: z.int().nonnegative(),
      outbox_rows: z.int().nonnegative(),
      undoable_writes: z.int().nonnegative(),
    }),
  ),
  // CORE-FREEZE: an incident resolved; a freeze is lifted (its held runs released).
  defineEvent(
    "incident.resolved",
    {
      ...agents,
      subject: ["company", "digital_employee"],
      summary: "An incident was resolved; a freeze was lifted and its held runs and sends released",
      consumers: ["audit", "rt"],
    },
    z.strictObject({
      incident_id: Uuid,
      kind: z.enum(["freeze", "loop", "budget", "guardrail", "reversal"]),
      scope: z.enum(["company", "digital_employee"]),
      membership_id: Uuid.nullable(),
      runs_released: z.int().nonnegative(),
    }),
  ),
  // CORE-AUTONOMY (core plan item 6; flag `earned_autonomy`): an agent's autonomy level changed —
  // up by a person's confirmed `autonomy_change`, or down one in an incident's own transaction
  // (a loop stop, a budget stop, a guardrail trip, a reversal marked wrong). The level's bundle
  // was rendered into the agent's seat rows; a demotion also tells the seat's reviewers.
  defineEvent(
    "agent.autonomy_changed",
    {
      ...agents,
      subject: "membership",
      summary: "An agent's autonomy level went up (confirmed) or down (an incident)",
      consumers: ["audit", "inbox (a demotion: the people who review the seat)"],
    },
    z.strictObject({
      seat_id: Uuid,
      from_level: z.number().int().min(0).max(3),
      to_level: z.number().int().min(0).max(3),
      cause: z.enum(["approval", "loop", "budget", "guardrail", "reversal"]),
      approval_id: Uuid.nullable(),
      /** The incident's reference (its row or event id); null for a promotion. */
      ref: z.string().max(200).nullable(),
      /** Seat rows the new level's bundle changed. */
      rows_changed: z.number().int().nonnegative(),
      summary: z.string().max(300),
    }),
  ),
  // CORE-REVERT (core plan item 7): a person reverted an incident or one agent run in one action.
  // Every item it reached, with its outcome and the agent it belongs to: the decision ledger
  // counts each as a reversal of that agent (keys `record:reversed:undo:{undo_id}` for a write —
  // the same as its `task.undone`, so it is counted once — and `record:reversed:outbox:{id}` for
  // a send). Ids only; the reason is the person's, as on `task.undone`.
  defineEvent(
    "incident.reverted",
    {
      ...agents,
      subject: ["company", "digital_employee"],
      summary:
        "An incident or agent run was reverted: sends cancelled, writes undone, delivered sends marked irreversible",
      consumers: ["audit", "rt", "agent_record.feed (reversed; DECISION-LEDGER)"],
    },
    z.strictObject({
      incident_id: Uuid,
      kind: z.enum(["freeze", "loop", "budget", "guardrail", "reversal"]),
      scope: z.enum(["company", "digital_employee"]),
      membership_id: Uuid.nullable(),
      /** `run`: one agent run's revert (its `reversal` incident); `incident`: an incident's. */
      target: z.enum(["incident", "run"]),
      run_id: Uuid.nullable(),
      items: z
        .array(
          z.strictObject({
            kind: z.enum(["write", "send"]),
            id: Uuid,
            outcome: z.enum(["undone", "cancelled", "irreversible", "expired", "failed"]),
            membership_id: Uuid.nullable(),
            seat_id: Uuid.nullable(),
            run_id: Uuid.nullable(),
          }),
        )
        .max(1000),
      follow_up_task_id: Uuid.nullable(),
      reason: z.string().max(500).nullable(),
    }),
  ),

  // ── MANAGED-POLICY: managed mode (requests for agents) ──
  defineEvent(
    "company.agent_mode_changed",
    {
      ...org,
      subject: "company",
      summary: "An agency admin switched who adds agents (managed or self-serve)",
      consumers: ["audit"],
    },
    z.strictObject({ from: CompanyAgentMode, to: CompanyAgentMode }),
  ),
  defineEvent(
    "agent_request.created",
    {
      ...org,
      subject: "agent_request",
      summary: "Someone in a managed company asked its agency for a DE or an agent",
      consumers: ["inbox (the agency's operators)", "audit"],
    },
    z.strictObject({
      kind: AgentRequestKind,
      role: z.string().max(200),
      template_key: z.string().max(64).nullable(),
      requested_by_membership_id: Uuid,
      /** HELPERS: the DE a helper request is for. */
      de_membership_id: Uuid.nullable().optional(),
    }),
  ),
  defineEvent(
    "agent_request.stage_changed",
    {
      ...org,
      subject: "agent_request",
      summary:
        "A paid helper request moved on (quoted, approved, paid, building, added or declined)",
      consumers: [
        "inbox (the requester: quoted, added, declined)",
        "billing (wp/SKILLSMITH-OPPORTUNITIES)",
        "audit",
      ],
    },
    z.strictObject({
      de_membership_id: Uuid,
      from: HelperRequestStage,
      to: HelperRequestStage,
      requested_by_membership_id: Uuid,
      quote: HelperQuote.nullable(),
    }),
  ),
  defineEvent(
    "agent_request.resolved",
    {
      ...org,
      subject: "agent_request",
      summary: "The agency fulfilled or declined a request for an agent",
      consumers: ["inbox (the requester)", "audit"],
    },
    z.strictObject({
      kind: AgentRequestKind,
      role: z.string().max(200),
      outcome: z.enum(["fulfilled", "declined"]),
      requested_by_membership_id: Uuid,
      fulfilled_ref_id: Uuid.nullable(),
    }),
  ),

  // REACH-S1 (agent-reach §2, §3 "Audited"): an agent's Reach changed — an edit, or a granted
  // access request. The event is the audit record: who (the envelope's actor), before, after, why.
  defineEvent(
    "agent.reach_changed",
    {
      ...agents,
      subject: "membership",
      summary: "An agent's Reach changed (serves, hears from, talks to, extra spaces, who sees)",
      consumers: ["audit", "chat wakes and DMs (the next ask reads the row)"],
    },
    z.strictObject({
      agent_membership_id: Uuid,
      /** The Reach in force before (its defaults while `stored` was false) and after. */
      before: ReachSnapshot,
      after: ReachSnapshot,
      /** The fields that differ. */
      fields: z.array(ReachField).min(1),
      reason: z.string().max(500).nullable(),
      /** The row's version after the change (the next `If-Match`). */
      version: z.int().min(1),
      /** The access request whose grant made the change, if any. */
      request_id: Uuid.nullable(),
    }),
  ),
  // REACH-S1: a person asked for access to an agent outside its Hears from ("Ask <serving person>
  // for access"); the people asked get an inbox row with Grant / Decline.
  defineEvent(
    "agent.reach_requested",
    {
      ...agents,
      subject: "agent_reach_request",
      summary: "A person asked to talk to an agent outside its reach",
      consumers: ["inbox (the people the agent serves; else owners and admins)", "audit"],
    },
    z.strictObject({
      agent_membership_id: Uuid,
      requester_membership_id: Uuid,
      /** Who is asked (empty: the company's owners and admins). */
      to_membership_ids: z.array(Uuid).max(50),
      note: z.string().max(500).nullable(),
    }),
  ),
  defineEvent(
    "agent.reach_request_decided",
    {
      ...agents,
      subject: "agent_reach_request",
      summary: "An access request to an agent was granted or declined",
      consumers: ["audit"],
    },
    z.strictObject({
      agent_membership_id: Uuid,
      requester_membership_id: Uuid,
      state: z.enum(["granted", "declined"]),
    }),
  ),

  // SHARED-DES (docs/design/2026-10-30-shared-des-and-fulfillment.md §2.2, §3.2): a shared seat's
  // serves links changed. The write's own event (the envelope's actor is who changed it); in the
  // same transaction it becomes one STRUCTURE-CHANGED `structure.changed` (kind serves, keyed by
  // this event): the seat's DEs are told what they serve now, the leaders get a notice. `from` /
  // `to` are the link lists before and after.
  defineEvent(
    "seat.serves_changed",
    {
      ...org,
      subject: "seat",
      summary: "A shared seat's serves links changed",
      consumers: ["audit", "structure.changed (kind serves)"],
    },
    z.strictObject({
      from: z.unknown(),
      to: z.unknown(),
      reason: z.string().max(500).nullable(),
    }),
  ),

  // DE-BRIEFS: a level of the brief rhythm was set or cleared (the company's defaults, a person's
  // own, or a DE's with its recipients). The event is the audit record of who changed what.
  defineEvent(
    "brief_settings.updated",
    {
      ...agents,
      subject: "company",
      summary: "A brief rhythm was set or cleared (company, person or DE)",
      consumers: ["audit"],
    },
    z.strictObject({
      scope: z.enum(["company", "person", "de"]),
      /** The person or DE the level belongs to; null for the company's. */
      subject_membership_id: Uuid.nullable(),
      /** The level now inherits from the one above (every field null). */
      cleared: z.boolean(),
      enabled: z.boolean().nullable(),
      frequency: z.enum(["daily", "weekly", "custom"]).nullable(),
      days: z.array(z.int().min(1).max(7)).max(7).nullable(),
      time: z.string().max(5).nullable(),
      recipients: z.array(Uuid).max(20).nullable(),
      cos_membership_id: Uuid.nullable(),
    }),
  ),
  // COS-BRIEF-RULES: a brief rule was set or removed (or the CoS stopped asking about the owner),
  // by the CEO / an admin in the settings or by the CoS on the CEO's instruction (its message).
  defineEvent(
    "brief_rule.updated",
    {
      ...agents,
      subject: "company",
      summary: "A brief rule was set or removed (who gets the CoS report, what, how often, how)",
      consumers: ["audit"],
    },
    z.strictObject({
      action: z.enum(["set", "removed", "stopped_asking"]),
      rule_id: Uuid.nullable(),
      recipient_kind: z.enum(["owner", "person"]),
      recipient_membership_id: Uuid.nullable(),
      sections: z
        .array(z.enum(["work", "kpis", "stuck", "decisions"]))
        .max(4)
        .nullable(),
      enabled: z.boolean().nullable(),
      frequency: z.enum(["daily", "weekly", "custom"]).nullable(),
      days: z.array(z.int().min(1).max(7)).max(7).nullable(),
      time: z.string().max(5).nullable(),
      channel: z.enum(["inbox", "dm", "email"]).nullable(),
      instruction_message_id: Uuid.nullable(),
    }),
  ),

  // ── Parity P6b (connections, tools, runtime) ──
  // (wave 1 additions go here)

  // ── STEP1-OUTBOX (build order increment 1 B): the outward-action seam ──
  defineEvent(
    "outbox.queued",
    {
      ...agents,
      subject: "action_outbox",
      summary:
        "An outward act entered the outbox (an approved external action; it goes out at send_at)",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      action: OutboxActionKind,
      approval_id: Uuid.nullable(),
      connection_id: Uuid.nullable(),
      provider: z.string(),
      send_at: Timestamp.nullable(),
    }),
  ),
  defineEvent(
    "outbox.sent",
    {
      ...agents,
      subject: "action_outbox",
      summary:
        "An outward act went out (or, a dry run, was logged only): the provider and its id, never the content",
      consumers: ["rt", "audit", "approval.receipt_slack (the receipt in the card's Slack thread)"],
    },
    z.strictObject({
      action: OutboxActionKind,
      approval_id: Uuid.nullable(),
      provider: z.string(),
      vendor_id: z.string().nullable(),
      dry_run: z.boolean(),
      attempts: z.int().min(1),
    }),
  ),
  defineEvent(
    "outbox.failed",
    {
      ...agents,
      subject: "action_outbox",
      summary: "An outward act failed and is retried (backoff)",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      action: OutboxActionKind,
      approval_id: Uuid.nullable(),
      attempts: z.int().min(1),
      error: z.string(),
      next_attempt_at: Timestamp,
    }),
  ),
  defineEvent(
    "outbox.dead",
    {
      ...agents,
      subject: "action_outbox",
      summary:
        "An outward act became a dead letter (refused, or out of attempts): a person retries or cancels it",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      action: OutboxActionKind,
      approval_id: Uuid.nullable(),
      attempts: z.int().min(0),
      reason: z.string(),
    }),
  ),
  defineEvent(
    "outbox.retried",
    {
      ...agents,
      subject: "action_outbox",
      summary: "A person sent a dead letter or a failed outward act back to the queue",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      action: OutboxActionKind,
      approval_id: Uuid.nullable(),
      from: z.enum(["failed", "dead"]),
    }),
  ),
  defineEvent(
    "outbox.cancelled",
    {
      ...agents,
      subject: "action_outbox",
      summary: "An outward act was cancelled before it went out",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      action: OutboxActionKind,
      approval_id: Uuid.nullable(),
      reason: z.string().nullable(),
    }),
  ),
  defineEvent(
    "connection.grant_changed",
    {
      ...deConfig,
      subject: "tool_connection",
      summary:
        "Who may use a connection changed: a grant to a person, DE, seat, team or crew made, replaced or revoked",
      consumers: ["audit"],
    },
    z.strictObject({
      grant_id: Uuid,
      change: z.enum(["granted", "revoked"]),
      grantee_kind: OutboxGranteeKind,
      grantee_id: Uuid,
      level: OutboxGrantLevel,
      families: z.array(OutboxConnectionFamily),
      daily_acts: z.int().nullable(),
      recipient_domains: z.array(z.string()).nullable(),
    }),
  ),

  // ── INC2-INTAKE (build order increment 2 A, §3 "One intake"): notices, events, jobs ──
  defineEvent(
    "inbound_event.received",
    {
      ...org,
      subject: "inbound_event",
      summary:
        "Something came in (a vendor notice, a booking, a form, a mail): logged with its source, preset and screen verdict, never its payload; it becomes a task for its handler DE when a rule names one",
      consumers: ["inbound task", "audit"],
    },
    z.strictObject({
      source_kind: IntakeSourceKind,
      verification: IntakeVerification,
      notice: z.boolean(),
      subscription_id: Uuid.nullable(),
      screen: z.strictObject({
        verdict: z.enum(["clean", "flagged", "off"]),
        quarantined: z.boolean(),
      }),
    }),
  ),
  defineEvent(
    "inbound_rule.changed",
    {
      ...org,
      subject: "inbound_rule",
      summary:
        "A source's handler rule was made, replaced or removed (who handles what comes in, and the owner's instructions)",
      consumers: ["audit"],
    },
    z.strictObject({
      change: z.enum(["put", "deleted"]),
      kind: IntakeRuleKind,
      source_key: z.string(),
      handler_membership_id: Uuid.nullable(),
      enabled: z.boolean(),
    }),
  ),
  defineEvent(
    "connection.job_dead",
    {
      ...deConfig,
      subject: "integration_job",
      summary:
        "An integration job became a dead letter (refused, or out of attempts): a person retries or discards it",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      provider: z.string(),
      kind: IntakeJobKind,
      connection_id: Uuid.nullable(),
      attempts: z.int().min(0),
      error_class: IntakeErrorClass.nullable(),
      reason: z.string(),
    }),
  ),
  defineEvent(
    "integration_job.resolved",
    {
      ...deConfig,
      subject: "integration_job",
      summary: "A person sent a dead letter back to the queue, or discarded it",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      action: z.enum(["retried", "discarded"]),
      provider: z.string(),
      kind: IntakeJobKind,
    }),
  ),
  // ── GMAIL-P1 (build order increment 3 A): a mailbox's sync and a company's own OAuth app.
  // Payloads carry ids and switches, never an address, a client id or a secret.
  defineEvent(
    "tool_connection.mail_sync_changed",
    {
      ...deConfig,
      subject: "tool_connection",
      summary:
        "A mailbox's sync was turned on (watched and pulled into the 30-day header index) or off (paused, its header rows deleted)",
      consumers: ["audit"],
    },
    z.strictObject({
      change: z.enum(["on", "off"]),
      subscription_id: Uuid,
      push: z.boolean(),
    }),
  ),
  defineEvent(
    "oauth_client.changed",
    {
      ...deConfig,
      subject: "company",
      summary:
        "The company's own OAuth app for a provider was set, replaced or removed (its connections authorize and refresh through it)",
      consumers: ["audit"],
    },
    z.strictObject({
      provider: z.string(),
      change: z.enum(["set", "removed"]),
      push_token_set: z.boolean(),
    }),
  ),
  // ── INC2-CONTACT (build order increment 2 C): the thin contact core. Payloads carry kinds and
  // ids, never an email, a phone or a name (the row holds them; the stream is not a copy).
  defineEvent(
    "contact.upserted",
    {
      ...org,
      subject: "contact",
      summary:
        "A contact was created or matched by its identities (new, existing, or kept with a duplicate candidate)",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      outcome: z.enum(["new", "existing", "candidate"]),
      source: z.string(),
      matched_by: z.enum(["external", "email", "phone"]).nullable(),
      candidate_ids: z.array(Uuid),
      identities_added: z.array(z.enum(["email", "phone", "domain", "external"])),
    }),
  ),
  defineEvent(
    "contact.identity_linked",
    {
      ...org,
      subject: "contact",
      summary: "An outside system's id (GHL, HubSpot) was linked to a contact",
      consumers: ["audit"],
    },
    z.strictObject({
      identity_id: Uuid,
      provider: z.string(),
      external_object: z.string(),
      connection_id: Uuid.nullable(),
    }),
  ),
  defineEvent(
    "contact.consent_recorded",
    {
      ...org,
      subject: "contact",
      summary: "A contact's consent for a channel was recorded (the latest per channel wins)",
      consumers: ["rt", "audit"],
    },
    z.strictObject({
      consent_id: Uuid,
      channel: z.enum(["email", "sms", "call", "whatsapp", "mail"]),
      status: z.enum(["opted_in", "opted_out", "unknown"]),
      basis: z.enum(["explicit", "form", "booking", "imported", "inferred"]),
      dnd: z.boolean(),
    }),
  ),

  // ── Parity P7 (guide, import, goals) ──
  // (wave 1 additions go here)
] as const;

export const eventCatalog = createCatalog(eventEntries);

type Entry = (typeof eventEntries)[number];
export type EventType = Entry["type"];
export type EventPayload<T extends EventType> = z.infer<Extract<Entry, { type: T }>["payload"]>;

export const EventType = z.enum(eventEntries.map((e) => e.type) as [EventType, ...EventType[]]);

/** Same as `eventCatalog.parse`, typed by event type. */
export function parseEventPayload<T extends EventType>(
  type: T,
  version: number,
  payload: unknown,
): EventPayload<T> {
  return eventCatalog.parse(type, version, payload) as EventPayload<T>;
}

// Envelope ------------------------------------------------------------------------------------

export const EventSubject = z
  .object({ kind: EventSubjectKind, id: Uuid })
  .meta({ id: "EventSubject" });
export type EventSubject = z.infer<typeof EventSubject>;

/**
 * 03 §Envelope as the API returns it (`GET /events`). Validated as a whole (finding #1): the
 * payload must parse as `type`@`version` with no undeclared field at any depth, and the subject
 * must be of a kind the catalog allows. Used on the way in (`eventToRow`) and out
 * (`eventFromRow`).
 */
export const Event = z
  .object({
    id: Uuid,
    company_id: Uuid,
    seq: z.int().min(1).nullable(),
    type: EventType,
    version: z.int().min(1),
    actor: Actor,
    subject: EventSubject,
    payload: z.record(z.string(), z.unknown()),
    correlation_id: Uuid.nullable(),
    causation_id: Uuid.nullable(),
    occurred_at: Timestamp,
  })
  .superRefine((e, ctx) => {
    const entry = eventCatalog.get(e.type);
    if (!entry) return;
    try {
      eventCatalog.parse(e.type, e.version, e.payload);
    } catch (err) {
      ctx.addIssue({ code: "custom", path: ["payload"], message: (err as Error).message });
    }
    if (!subjectKindsOf(entry).includes(e.subject.kind))
      ctx.addIssue({
        code: "custom",
        path: ["subject", "kind"],
        message: `${e.type} subject must be ${subjectKindsOf(entry).join("|")}`,
      });
  })
  .meta({ id: "Event" });
export type Event = z.infer<typeof Event>;

/** Envelope narrowed to one type at its current version. */
export type TypedEvent<T extends EventType> = Omit<Event, "type" | "payload"> & {
  type: T;
  payload: EventPayload<T>;
};

/** The persisted `event` row (column names), including the full actor column set. */
export const EventRow = z
  .object({
    company_id: Uuid,
    occurred_at: Timestamp,
    id: Uuid,
    seq: z.int().min(1).nullable(),
    type: z.string(),
    version: z.int().min(1),
    ...ActorColumns.shape,
    subject_kind: z.string().nullable(),
    subject_id: Uuid.nullable(),
    payload: z.record(z.string(), z.unknown()),
    correlation_id: Uuid.nullable(),
    causation_id: Uuid.nullable(),
  })
  .meta({ id: "EventRow" });
export type EventRow = z.infer<typeof EventRow>;

export function eventToRow(event: Event): EventRow {
  const e = Event.parse(event);
  return EventRow.parse({
    company_id: e.company_id,
    occurred_at: e.occurred_at,
    id: e.id,
    seq: e.seq,
    type: e.type,
    version: e.version,
    ...actorToColumns(e.actor),
    subject_kind: e.subject.kind,
    subject_id: e.subject.id,
    payload: e.payload,
    correlation_id: e.correlation_id,
    causation_id: e.causation_id,
  });
}

export function eventFromRow(row: EventRow): Event {
  const r = EventRow.parse(row);
  const { actor_kind, actor_membership_id, actor_seat_id, actor_key_id, actor_run_id } = r;
  const actor = actorFromColumns({
    actor_kind,
    actor_membership_id,
    actor_seat_id,
    actor_key_id,
    actor_run_id,
    actor_acting_for_membership_id: r.actor_acting_for_membership_id,
    actor_identity_mode: r.actor_identity_mode,
    actor_crew_agent_id: r.actor_crew_agent_id,
    actor_policy_snapshot_id: r.actor_policy_snapshot_id,
    actor_auth_version: r.actor_auth_version,
    actor_user_id: r.actor_user_id,
  });
  return Event.parse({
    id: r.id,
    company_id: r.company_id,
    seq: r.seq,
    type: r.type,
    version: r.version,
    actor,
    subject:
      r.subject_kind === null || r.subject_id === null
        ? null
        : { kind: r.subject_kind, id: r.subject_id },
    payload: r.payload,
    correlation_id: r.correlation_id,
    causation_id: r.causation_id,
    occurred_at: r.occurred_at,
  });
}

/** What a handler passes to `emit(tx, drafts)`; actor, company and correlation come from context. */
export type EventDraft<T extends EventType = EventType> = {
  type: T;
  subject: EventSubject;
  payload: EventPayload<T>;
  causation_id?: string;
};

/**
 * Typed, validated draft. The payload is parsed against the catalog, so a handler cannot emit
 * a type that is not in the catalog or a payload that does not match it.
 */
export function draftEvent<T extends EventType>(
  type: T,
  subject: EventSubject,
  payload: EventPayload<T>,
  opts: { causation_id?: string } = {},
): EventDraft<T> {
  const entry = eventCatalog.get(type);
  if (!entry) throw new Error(`unknown event type ${type}`);
  if (!subjectKindsOf(entry).includes(subject.kind))
    throw new Error(
      `${type} subject must be ${subjectKindsOf(entry).join("|")}, not ${subject.kind}`,
    );
  const draft: EventDraft<T> = {
    type,
    subject: EventSubject.parse(subject),
    payload: eventCatalog.parse(type, entry.version, payload) as EventPayload<T>,
  };
  if (opts.causation_id !== undefined) draft.causation_id = Uuid.parse(opts.causation_id);
  return draft;
}

/**
 * Producers that are not HTTP routes (jobs, consumers, the relay), with the types they emit. The
 * routes declare theirs in `api/*.ts` (`emits`).
 */
export const backgroundProducers = {
  // SCREENS-S1: the sweep ends idle leases and leases whose run ended, marks a silent stack
  // stale, and starts the next waiting lease.
  "job:screens.sweep": [
    "screen_lease.ended",
    "screen_lease.started",
    "screen_stack.health_changed",
    // SCREENS-NEEDS-YOU: an ask nobody answered in the agent's wait window expires.
    "screen_ask.resolved",
    // SCREEN-HANDOFF: an agent whose run ended is told beside the card and woken.
    "message.created",
    "wakeup.requested",
  ],
  "job:de_subscription.post": ["cost.posted"],
  // SPEND-KPIS: the `ledger` KPIs' values, written as the system from `spend_daily`.
  "job:spend.snapshot": ["observation.recomputed"],
  // T3-TASK-ROLLUPS: the `task` KPIs' values, written as the system from `task_metric_daily`.
  "job:task_metric.snapshot": ["observation.recomputed"],
  "job:friday_report.generate": ["friday_report.generated", "scorecard.published"],
  "job:friday_report.send": ["friday_report.sent", "friday_report.send_failed"],
  "job:litellm.spend_pull": ["cost.posted"],
  // AGENT-PLUMBING: the shared key's tagged rows, booked per company, DE and run.
  "job:litellm.tagged_spend_pull": ["cost.posted"],
  "job:litellm.tagged_spend_catch_up": ["cost.posted"],
  // AGENT-PLUMBING: a due "wake me after" timer becomes an `inbox` wake-up on its subject.
  "job:wake_timer.fire": ["wakeup.requested"],
  // HANDOFF-NO-DROPS: a stuck request: the DE woken once (its safety net, a lost card wake), then
  // the requester told in their thread and the operators' inbox items.
  "job:handoff.watch": ["wakeup.requested", "message.created", "inbox_item.created"],
  // HANDOFF-NO-DROPS: a run's end settles its requests (a re-wake, the progress line, an alert).
  "consumer:handoff.run_end": [
    "wakeup.requested",
    "message.created",
    "message.edited",
    "inbox_item.created",
    // lane B: the run's request delivered for review
    "request.delivered",
  ],
  // lane B (REQUEST-RECOVERY): every stop of a request opens its one recovery — the MCP failure
  // stop, the loop breaker's stop, a parked retry, a stalled chain, an acked stop request — with
  // the accountable person's item and the managers' alert.
  "mcp:failure_stop": ["recovery.opened", "inbox_item.created"],
  "consumer:run.loop_inbox:recovery": ["recovery.opened", "inbox_item.created"],
  "job:run.retry_sweep:recovery": ["recovery.opened", "inbox_item.created"],
  "job:handoff.watch:recovery": ["recovery.opened", "inbox_item.created"],
  // lane B2: an OOO window starting or running out on its own re-routes (approvals, recoveries);
  // a failed commitment and a crashed helper open their request's recovery.
  "job:ooo.reroute_sweep": ["inbox_item.created"],
  "job:commitment.ladder:recovery": ["recovery.opened", "inbox_item.created"],
  // B2-FIXES: a held move whose window ended with the move standing is settled (`passed`) and
  // its usual status-change notifications go now
  "job:gate_hold.settle": ["task.gate_passed"],
  "job:litellm.provision_reconcile": [
    "digital_employee.litellm_key_created",
    "digital_employee.provisioning_failed",
    "agent_key.created",
    "agent_key.revoked",
  ],
  "consumer:proj.kpi": ["observation.recomputed"],
  "consumer:cost.human_time": ["cost.posted"],
  // M1-02 contracts; the jobs are M1-05 (materialiser) and M1-07 (webhook delivery).
  // `task.created`: M2-03's task hook, for recurring work that names a live board.
  "job:recurring_work.materialise": [
    "recurring_work.materialised",
    "task.created",
    // M7-02: a task template's description becomes the task's page.
    "page.created",
  ],
  // M2-01 contracts; the inbox projection is M2-04.
  // INBOX-PREFS: a waiting item answered anywhere (an approval decided, expired or executed) clears.
  "consumer:proj.inbox": ["inbox_item.created", "inbox_item.done"],
  // INBOX-PREFS: a waiting item's one reminder, then the hand-on (domain/inbox/nudge.ts).
  "job:inbox.nudge": ["inbox_item.nudged", "inbox_item.escalation_due"],
  // HARDEN-pwa: Web Push delivery drops a subscription the push service no longer knows.
  "consumer:push.inbox": ["push_subscription.removed"],
  // M3-01 contracts; admission and the lease sweeper are M3-02, approvals and delegation
  // expiry M3-03, the budget consumer and the reservation sweeper M3-04.
  "job:wakeup.heartbeat": ["wakeup.requested"],
  // M3-02: tasks on a DE seat (`task.created`/`.assigned`); mentions are the chat route's own
  // (`POST …/threads/{id}/messages`, M3-03 at the M2-02 merge).
  "consumer:wakeup.triggers": ["wakeup.requested"],
  // T2-BOARD-WAKE: a `now` event on a DE-owned board wakes its owner at once; a due batch window
  // (job `board_wake.fire`) wakes it, or is dropped at the board's daily cap.
  // B5-DECIDE: with the company's board gate on, the classifier's cost on the owner's seat.
  "consumer:board.attention": ["wakeup.requested", "cost.posted"],
  "job:board_wake.fire": ["wakeup.requested", "wakeup.dropped"],
  "consumer:wakeup.admission": [
    "wakeup.coalesced",
    "wakeup.dropped",
    "run.queued",
    "budget.reserved",
    "budget.reservation_refused",
    "personal_crew.paused",
    // MANAGED-POLICY: a wake-up dropped for the agent policy carries its audit record
    "agent_policy.dispatch_refused",
    // DECISION-LEDGER: and its decision ledger row
    "agent_policy.decided",
  ],
  "job:run.lease_sweep": ["run.failed", "budget.settled"],
  // DC-08: a refused `runs/{id}/workers` rolls back; its reason is written after, in its own
  // transaction (domain/delegation/workers.ts `recordWorkerRefusal`).
  "admission:worker_refusal": ["run.worker_refused"],
  // EXPORT-CORE: `POST …/export` built the archive after its request committed; a secret-scan
  // hit or a build error refuses the download and is recorded in a transaction of its own
  // (domain/export/run.ts).
  "export:refusal": ["company.export_failed"],
  // MANAGED-POLICY: a `POST runs` the agent policy refuses rolls back; its audit record is
  // written after, in its own transaction (domain/agent-policy/dispatch.ts).
  "admission:policy_refusal": ["agent_policy.dispatch_refused", "agent_policy.decided"],
  // MANAGED-POLICY: an MCP tool call the agent policy refuses before its handler runs.
  // CORE-KEYS (flag `core_keys`): an Ask a policy row sets files an `agent_action` approval
  // instead of refusing, and an approved one is used up (executed) by the identical call.
  // DECISION-LEDGER: a refusal also writes its `agent_policy.decided` row.
  "mcp:policy_gate": [
    "agent_policy.dispatch_refused",
    "agent_policy.decided",
    "approval.requested",
    "approval.executed",
  ],
  // CORE-KEYS: a self-hosted DE's REST write meets the same gate (http/scope.ts): refused (its
  // record written after the rollback), filed as an `agent_action` approval, or a permit used.
  "rest:policy_gate": [
    "agent_policy.dispatch_refused",
    "agent_policy.decided",
    "approval.requested",
    "approval.executed",
  ],
  // DECISION-LEDGER: an agent write the policy allowed in its run writes its decision row in the
  // write's own transaction (domain/agent-policy/ledger.ts), whichever route or tool made it.
  "policy:in_run_decision": ["agent_policy.decided"],
  // CORE-AUTONOMY (flag `earned_autonomy`): the weekly review files an `autonomy_change` for an
  // agent meeting the thresholds (domain/agent-policy/autonomy-store.ts); its approval, applied,
  // raises the level (`consumer:approval.apply`).
  "job:autonomy.review": ["approval.requested", "agent.autonomy_changed"],
  // CORE-AUTONOMY: an incident (loop stop, budget stop, guardrail trip, reversal marked wrong)
  // drops the agent one level in the incident's own transaction, whichever route, consumer or
  // job raised it, and tells the seat's reviewers.
  "autonomy:demotion": ["agent.autonomy_changed", "inbox_item.created"],
  // AGENT-GUARDRAILS: an agent write the guardrails refuse rolls back; its record (and, past the
  // hourly cap, the pause) is written after, in its own transaction
  // (domain/agent-policy/guardrails.ts).
  "admission:agent_guardrail": ["agent.guardrail_tripped", "digital_employee.paused"],
  // BOARD-GATES: an agent's move out of a column only a person moves out of rolls back; its
  // record is written after, in its own transaction (domain/boards/column-gate.ts).
  "admission:column_locked": ["task.move_refused"],
  // Phase 1 inc 2: the job also retries transient execution failures (what the consumer
  // `approval.apply` emits when it applies, below).
  "job:approval.expire": [
    "approval.expired",
    "approval.executed",
    // STEP1-OUTBOX: a retried external action enters the outbox
    "outbox.queued",
    "message.created",
    "message.edited",
    "wakeup.requested",
    "task.updated",
    "task.status_changed",
    "task.due_changed",
    "task.priority_changed",
    "task.assigned",
    "task.unassigned",
    "task.field_changed",
    "task.file_changed",
    // Parity P5: an automation proposal's retried application
    "automation.created",
    "automation.updated",
    "automation.deleted",
    "responsibility.created",
    "recurring_work.created",
    // CALENDAR-C1: a `calendar_change`'s retried application
    "calendar_event.created",
    "calendar_event.updated",
    "calendar_event.cancelled",
    // COS-MODES: an Ask-me-first request past its timeout proceeds (the handoff) or escalates
    // (a new `task_change` approval to the CEO)
    "task.handed_off",
    "task.agent_directed",
    "approval.requested",
    // T6-BOARD-NOTE: a `board_note`'s retried application
    "page.created",
    "page.edited",
    "board.updated",
    // B2-GATES: a `gate_change`'s retried application
    "board.gates_changed",
  ],
  // COS-MODES: in Solo mode, a new head (a person other than the CEO leading agents) → one
  // suggestion to the CEO.
  "job:cos.mode_suggest": ["chief_of_staff.mode_suggested", "company.updated"],
  // AGENT-REPORTING (13 §Reporting duties): reminders, the DE's wake-up, late notices.
  "job:reporting.remind": ["reporting_duty.due", "reporting_duty.late", "wakeup.requested"],
  "job:delegation_request.expire": ["delegation_request.expired"],
  // M3-03: the single executor of approved payloads (a held agent post is posted) and the
  // requester's wake-up on a decision or an expiry; the requester's wake-up on an expired
  // delegation request.
  "consumer:approval.apply": [
    "message.created",
    // CORE-AUTONOMY: a confirmed `autonomy_change` raises the agent's level
    "agent.autonomy_changed",
    // AGENT-POLISH: a held post's placeholder says what became of it
    "message.edited",
    "wakeup.requested",
    "agent_room.turn_changed",
    // Phase 1 inc 2: the outcome, and an approved task_change applied as its decider (the task
    // domain's own events) or an approved file delete (the file trashed)
    "approval.executed",
    "task.updated",
    "task.status_changed",
    "task.due_changed",
    "task.priority_changed",
    "task.assigned",
    "task.unassigned",
    "task.field_changed",
    "task.file_changed",
    // Parity P5: an approved automation proposal (a rule, or a routine) applied as its decider
    "automation.created",
    "automation.updated",
    "automation.deleted",
    "responsibility.created",
    "recurring_work.created",
    // CALENDAR-C1: an approved `calendar_change` applied as its decider
    "calendar_event.created",
    "calendar_event.updated",
    "calendar_event.cancelled",
    // STEP1-OUTBOX: an approved external action enters the outbox
    "outbox.queued",
    // T3-TASK-ROLLUPS: an approved task metric created and put on the board owner's seat
    "metric_def.created",
    "seat_metric.changed",
    // T6-BOARD-NOTE: an approved `board_note` (the note page, the board's first `page_id`, its
    // check window) applied as its decider, with its rule changes (the automation events above)
    "page.created",
    "page.edited",
    "board.updated",
    // B2-GATES: an approved `gate_change` (a person's gates set as that person); a `task_batch`
    // applies through the task events above and posts its notes as messages
    "board.gates_changed",
  ],
  // STEP1-OUTBOX: the drain sends what is due through its executor; the outcome settles the
  // approval it came from and wakes the DE that asked for it
  "job:outbox.drain": [
    // CORE-FREEZE: a queued send of a frozen agent or company, with `settings.freeze.outbox`
    // = `cancel` (the default `hold` writes no event: the row waits)
    "outbox.cancelled",
    "outbox.sent",
    "outbox.failed",
    "outbox.dead",
    "approval.executed",
    "wakeup.requested",
    // a dead letter: the connection's owner (or the company's admins) are told
    "inbox_item.created",
  ],
  "consumer:delegation.wake": ["wakeup.requested"],
  // M4-01: a crew with each person membership (the consumer) and for people who had none (the
  // job); the crew follows its owner's membership status; admission pauses a crew at its cap.
  "consumer:crews.setup": [
    "personal_crew.created",
    "crew_agent.added",
    "personal_crew.paused",
    "personal_crew.resumed",
    "personal_crew.deactivated",
  ],
  "job:crews.ensure": ["personal_crew.created", "crew_agent.added"],
  // M4-05: a delivered status ask its target left unanswered past its deadline escalates.
  "job:status_ask.escalate": ["status_ask.escalated", "message.created"],
  // M4-03: the crew features' jobs (domain/crews): heartbeat wake-ups, the daily brief, the
  // rule-based triage (applied alone where the owner allows it), reminders coming due.
  "job:crews.heartbeat": ["wakeup.requested"],
  "job:crews.brief": ["topic.created", "message.created", "crew_brief.posted"],
  "job:crews.triage": [
    "crew_proposal.created",
    "crew_proposal.decided",
    "inbox_item.done",
    "inbox_item.snoozed",
  ],
  "job:reminders.fire": ["reminder.fired", "inbox_item.created"],
  // CHAT-SWITCH-B: "Send later" posts as its author when due (with the post's own events: a
  // mentioned agent is woken, or its wake-up dropped with why).
  "job:chat.scheduled_send": [
    "scheduled_message.sent",
    "message.created",
    "wakeup.requested",
    "wakeup.dropped",
  ],
  // DE-INITIATE: a DE's start that waited out quiet hours posts as the DE (a new thread, or a
  // DM opened then; the seat's message approval may hold it as a card).
  "job:chat.de_initiation_send": [
    "message.created",
    "topic.created",
    "channel.created",
    "approval.requested",
  ],
  // COS-SLICE1: the Chief of Staff's interval check and immediate kinds post a digest message
  // and wake the DE; the detector writes follow-up cards under messages.
  "job:cos.check": ["topic.created", "message.created", "wakeup.requested", "cos_digest.posted"],
  "consumer:suggestions.detect": ["suggestion.created"],
  // SEARCH-NOTIF
  "job:kpi.due": ["kpi.entry_due", "inbox_item.created"],
  // HARDEN: an operations signal past its threshold → one open item per signal for each agency
  // operator in HTVA; closed again once it clears (services/api/src/ops/alerts.ts).
  "job:ops.alerts": ["inbox_item.created", "inbox_item.done"],
  // MEETINGS: the day before a meeting, its pre-read is asked of the team's agent.
  "job:meeting.preread": ["meeting.agenda_built", "meeting.preread_requested", "wakeup.requested"],
  "consumer:cost.budget": [
    "budget.threshold_crossed",
    "budget.exceeded",
    "budget.pace_alert",
    "budget.reserve_reached",
    "approval.requested",
    "digital_employee.paused",
    "wakeup.dropped",
  ],
  // M3-04: the one charge basis of agent spend (`run.step_recorded`, `sandbox.stopped`).
  "consumer:cost.agent": ["cost.posted"],
  // M3-07: a run's per-run gateway key read for its spend and deleted at the run's end (or its
  // DE's pause); the spend its steps had not posted is booked once (`litellm:run:{run_id}`).
  "consumer:gateway.run_keys": ["cost.posted"],
  "job:budget.reservation_sweep": ["budget.released", "run.failed", "budget.settled"],
  // DE-BUDGET-PACE: a DE paused at its reserve resumes when the budget's period ends.
  "job:budget.period_resume": ["digital_employee.resumed", "budget.period_reset"],
  "job:price_list.mirror": ["price_list.changed"],
  // WALLET-P1: the platform tariff copied into each cell; every gateway cost row debited once
  // (the warning and the zero crossing are the debit's); the DEs paused at zero and resumed by a
  // credit (domain/wallet).
  "job:tariff.mirror": ["tariff.changed"],
  "consumer:wallet.debit": ["wallet.low", "wallet.empty", "wallet.restored", "tariff.changed"],
  // BILLING-PLANS: the daily dunning sweep (an open invoice past its due date is overdue and
  // opens the grace period; the reminder on the reminder day; suspension at the end of grace,
  // pausing the DEs; scheduled discounts put on the subscription; quotes past expiry), the yearly
  // maintenance invoice on a build's delivery anniversary, the subscription's lines kept in step
  // with people and DEs (people count, a DE's maintenance line, a template DE's setup fee), and
  // an AG over its DE's tier cap (refused; the event is written after the rollback).
  "job:billing.overdue_sweep": [
    "invoice.overdue",
    "subscription.past_due",
    "subscription.reminded",
    "subscription.suspended",
    "subscription.updated",
    "digital_employee.paused",
    "wakeup.dropped",
    "quote.changed",
    "de_maintenance.changed",
    "subscription.quantity_changed",
    "discount.ended",
  ],
  // the operations job (and any request, sweep or webhook running a company's queue): a billing
  // change refused, or failing five times in a row, is dead-lettered and the agency's operators
  // are told in HTVA's inbox
  "job:billing.operations": [
    "billing_operation.failed",
    "inbox_item.created",
    "subscription.updated",
  ],
  "job:billing.maintenance_anniversary": [
    "project.milestone_invoiced",
    "project.changed",
    "invoice.created",
    "invoice.finalized",
    "invoice.paid",
    "invoice.payment_failed",
  ],
  "consumer:billing.lines": [
    "subscription.quantity_changed",
    "de_maintenance.changed",
    "billing_charge.created",
    "invoice.created",
    "invoice.finalized",
  ],
  "refusal:workforce.ag_cap": ["workforce_agent.cap_refused"],
  "consumer:wallet.controls": [
    "digital_employee.paused",
    "digital_employee.resumed",
    "wakeup.dropped",
  ],
  // M4-02: catalog skill packs copied into each company, deprecations propagated.
  "job:skill_pack.mirror": ["skill_pack.published", "skill_pack.deprecated"],
  // AGENT-SAFETY: packs unused past the company's setting are archived (off unless set).
  "job:skill_pack.archive": ["skill_pack.archived"],
  // M5-03: the nightly forecast snapshot.
  "job:forecast.snapshot": ["forecast.snapshotted"],
  // M5-05: a live break-glass session (or an undecided request) runs out; the company's admins
  // hear of a request, start and end in their inbox; a published scorecard or a generated Friday
  // report is pushed to the control plane as the company's `kpi` roll-up.
  "job:break_glass.expire": ["access.break_glass_ended"],
  // BREAK-GLASS: any chat read (a GET, which emits nothing of its own) that a live session alone
  // let through logs the read (`auth/break-glass.ts`).
  "read:break_glass": ["access.break_glass_read"],
  // AGENT-SAFETY: a crew run's mail or calendar read (a GET) records its content screen's
  // verdict as a `note` step on the run (`domain/runs/content-screen.ts`).
  "read:content_screen": ["run.step_recorded"],
  // WEBHOOKS-IN: the incoming endpoint's 20th rejected signature from one address in 10 minutes
  // tells the owners and admins (the refused request answers 401; the alert commits with its log).
  "reject:inbound_webhook.signature": ["inbound_webhook.signatures_failing"],
  // WEBHOOKS-IN: an accepted delivery becomes one task on the target DE's seat, its preamble the
  // task's page and its payload a task file (the DE's wake-up is `wakeup.triggers`' as for any
  // assignment).
  // INC2-INTAKE: an event for a person's My agent goes on their personal space's board (made
  // once, "Intake", with its home channel, fields and views) and wakes the crew by name.
  // PROVISION-IN-PRODUCT: a new DE's set-up (and a manager's re-run): its monthly budget, its MCP
  // agent key, General space access, a self-hosted lead profile, the CoS digest on it, and a first
  // check-in task (on a board made once in the General space) that wakes it.
  "consumer:de.provision": [
    "budget.changed",
    "agent_key.created",
    "access.granted",
    "agent_profile.created",
    "agent_profile.updated",
    "board.created",
    "channel.created",
    "field_def.created",
    "view.created",
    "task.created",
    "page.created",
    "wakeup.requested",
  ],
  // DE-CONNECT: a DE connected to its agent container: its first agent spec version and its
  // tool set, its MCP agent key, the connections named for it, and a self-hosted lead profile
  // recording where it runs (the endpoint's registry rows are audit lines, not events).
  "consumer:de.connect": [
    "agent_spec.versioned",
    "agent_key.created",
    "connection.grant_changed",
    "agent_profile.created",
    "agent_profile.updated",
  ],
  // PROVISION-IN-PRODUCT: a new company's own system agent (a membership nobody in the company
  // sees) and its feature rules (entitlements with source `rule`).
  "job:provision.companies": ["company.entitlement_changed"],
  "consumer:inbound.task": [
    "task.created",
    "page.created",
    "task.file_changed",
    "board.created",
    "channel.created",
    "field_def.created",
    "view.created",
    "wakeup.requested",
  ],
  // INC2-INTAKE: the integration work queue — a dead letter tells the connection's owner (or the
  // company's owners and admins)
  "job:integrations.jobs": ["connection.job_dead", "inbox_item.created"],
  "consumer:break_glass.inbox": ["inbox_item.created"],
  // UPLOAD-PRIVATE-FIRST: an uploaded document's text read in the background; its uploader told
  // when a large one is ready or a read fails.
  "consumer:upload.extract": ["page.edited", "inbox_item.created"],
  "consumer:portfolio.push": ["portfolio_projection.published"],
  "consumer:portfolio.project": ["portfolio_projection.published"],
  // FOUNDER-OS phase 1: the daily sweep re-projects every held company (06:00 company time);
  // the alerts job keeps one Founder OS inbox item per open alert and grant holder.
  "job:portfolio.sweep": ["portfolio_projection.published"],
  "job:portfolio.alerts": ["inbox_item.created", "inbox_item.done"],
  // WOS-BUNDLE: five exhausted events in a row pause the hook (`webhook.paused`).
  "job:webhook.deliver": ["webhook.disabled", "webhook.paused"],
  // DC-01 contracts: the hand-off worker, confirm poller and mirror are DC-04 (the mirror with
  // ADI-02), the reflection routine DC-09, the drift-audit scheduler and runner DC-10.
  // Phase 2 increment 4: the hand-off applies callbacks kept until the binding and polls a lost
  // 202; the poll records late results of reconciled deploys (`digital_employee.panel_drift`).
  "job:deploy.handoff": [
    "deploy.handed_off",
    "deploy.confirmed",
    "deploy.failed",
    "digital_employee.panel_drift",
  ],
  "job:deploy.confirm_poll": ["deploy.confirmed", "deploy.failed", "digital_employee.panel_drift"],
  "job:deploy.reconcile_poll": ["digital_employee.panel_drift"],
  "consumer:deploy.mirror": ["digital_employee.deployed", "digital_employee.deploy_failed"],
  // Phase 2 increment 6: a shape-A run without routing evidence fails `profile_unroutable`
  // before any wake-up is delivered.
  "consumer:runtime.wakeup": ["run.failed", "budget.settled"],
  // MID-RUN-STEER: a message delivered into the running turn (a live ping on its wake-up), or put
  // back in the queue for the next run when the runtime could not take it.
  "consumer:runtime.steer": ["wakeup.coalesced", "wakeup.requested"],
  // LOOP-BREAKER: a loop stop tells the owning DE and the seat's person (inbox items, no wake).
  "consumer:run.loop_inbox": ["inbox_item.created"],
  "job:template_edit.reflect": ["template_edit.proposed"],
  // DC-07 (ADR-0019 §2, §3): a run refused at the verdict gate is parked after the request rolls
  // back (`run.waiting {reason: input}` and the supervisor's inbox items); the supervisor's
  // `run_needs_review` items on a `not_verified` / `inconclusive` verdict or a premise
  // escalation; a run left in review past 72 h ends (`failed`, reason `review_timeout`).
  "refusal:verdict_gate": ["run.waiting", "inbox_item.created"],
  "consumer:run.review_inbox": ["inbox_item.created"],
  // STRUCTURE-CHANGED: a reporting line moved — the audit record, then both leaders' items.
  "consumer:org.structure": ["structure.changed", "inbox_item.created"],
  // AGENT-POLISH: an ended run's wall and active time as its DE's `agent.run` activity.
  "consumer:run.agent_time": ["activity.recorded"],
  // AGENT-RECORD: a decided approval, a task the DE completed, and a person's take-over, cancel
  // or undo of the DE's work as its `output.*` / `task.completed` / `intervention.recorded`
  // activity (the agent record and its DE metrics).
  // DECISION-LEDGER: and a supervisor's review of an approval as their supervision time
  "consumer:agent_record.feed": [
    "activity.recorded",
    "time_entry.created",
    "time_entry.updated",
    "cost.posted",
  ],
  // DE-KPIS-LIVE: the DEs' ended runs the feeder did not read, as their run outcome and memory items.
  "job:agent_record.run_backfill": ["activity.recorded"],
  "job:run.review_timeout": ["run.failed", "budget.settled"],
  "job:drift_audit.schedule": ["drift_audit.scheduled"],
  "job:drift_audit.run": [
    "drift_audit.started",
    "drift_audit.passed",
    "drift_audit.failed",
    "drift_audit.errored",
    "task.created",
    // M7-02: the failure report (the task's body) becomes the task's page.
    "page.created",
    // DC-10: the audit's cost posts to the DE's seat (meter `drift_audit:{audit_id}`).
    "cost.posted",
  ],
  // M7-03: the Recall sync (tenant provisioning + nightly reconcile) and the callback consumer.
  // M7-05: DE and crew agents are held by the provision job (keys) and kept by every sync (cells,
  // revocation); the hire-as-DE copy is the provision job's.
  "job:recall.provision": [
    "recall.tenant_provisioned",
    "recall.reconciled",
    "recall.agent_key_issued",
    "recall.agent_revoked",
    "recall.memory_copied",
  ],
  // RECALL-FOLLOWUP: where no webhook is registered, the worker polls Recall's event feed and
  // records each event as the callback route does.
  "job:recall.events_poll": ["recall.notice_received"],
  // IMPORT (HARDEN): the import job (services/api imports/runs/job.ts) claims a queued import
  // and writes it as the system, `correlation_id` = the run: Zulip streams as archived channels
  // with resolved topics (messages emit nothing), ClickUp spaces and lists as spaces and boards
  // with their tasks (and body pages); invitations and external identities for the people.
  "job:import_run.run": [
    "import_run.started",
    "import_run.progressed",
    "import_run.succeeded",
    "import_run.failed",
    "import_run.cancelled",
    "space.created",
    "channel.created",
    "channel.archived",
    "topic.created",
    "topic.resolved",
    "board.created",
    "field_def.created",
    "view.created",
    "task.created",
    "page.created",
    "invite.created",
    "external_identity.created",
  ],
  "job:recall.reconcile": ["recall.reconciled", "recall.agent_revoked"],
  "consumer:recall.sync": ["recall.reconciled", "recall.agent_revoked"],
  // PARITY P4: a principal that lost view of a board (or a space's boards) loses its follows of
  // it and its tasks (05 fix #24).
  "consumer:access.invalidate": ["follow.changed"],
  // PARITY P4 wave 0: the access mutation helpers (domain/access/resolve.ts), called by the
  // parity seed and tests; P4's `GET|PUT …/access`, restrict and unrestrict routes (wave 1)
  // declare it in their `emits` too.
  "domain:access.mutate": ["access.changed"],
  // WORKFORCE: the ops registration of agents (AG; `infra/ops/register-agents.ts` →
  // `domain/org/workforce.ts` `registerAgents`). `workforce_agent.updated` is also the PATCH's.
  "domain:workforce.register": ["workforce_agent.registered", "workforce_agent.updated"],
  // WF-POLISH: the operating-profile sync (`infra/ops/htva-profile-sync.ts` →
  // `domain/org/profile-sync.ts`): what an agent or a DE is, and its seat's responsibilities.
  "domain:workforce.profile-sync": [
    "workforce_agent.updated",
    "digital_employee.updated",
    "responsibility.created",
  ],
  // RECALL-RUN-LIFECYCLE: a run's handed procedure filed when it ends (the DE filed none)
  "consumer:recall.run_lifecycle": ["procedure.run_recorded"],
  "consumer:recall.callbacks": [
    "promotion.proposed",
    "approval.requested",
    "task.created",
    "reminder.created",
    "decision.recorded",
    "inbox_item.created",
  ],
  // Parity P5 wave 1: a rule fires on a relayed event (the consumer) or on its schedule, a due
  // window or an overdue task (the job); its effects are the task, chat and wake-up writers'
  // own events, acting for the rule's owner; a refusal or a pause tells the owner (inbox).
  "consumer:automations.fire": [
    "automation.fired",
    "automation.refused",
    "automation.paused",
    "inbox_item.created",
    "task.status_changed",
    "task.priority_changed",
    "task.tagged",
    "task.assigned",
    "task.created",
    "task.moved",
    "message.created",
    "wakeup.requested",
  ],
  "job:automations.schedule": [
    "automation.fired",
    "automation.refused",
    "automation.paused",
    "inbox_item.created",
    "task.status_changed",
    "task.priority_changed",
    "task.tagged",
    "task.assigned",
    "task.created",
    "task.moved",
    "message.created",
    "wakeup.requested",
  ],
  // Parity P5 wave 1: the 24-hour and overdue reminders, once per task, kind and day.
  "job:task.due_reminders": ["task.due_soon", "task.overdue", "inbox_item.created"],
  // BOARD-RESULTS: the results sweep's quiet lines, and the Expires → Parked board rule's moves
  // (through the automation engine, which emits its own events).
  "job:post_results.sweep": ["task.results_due", "inbox_item.created"],
  // COS-BRIEF-RULES: a Chief of Staff report a brief rule sends to the recipient's inbox.
  "job:briefs.send": ["inbox_item.created"],
  // MEMORY-HYGIENE: one daily memory-review digest per reviewer (domain/memory/proposals.ts).
  "job:memory.review_digest": ["inbox_item.created"],
  // RECALL-INBOX-LOOP: the decision-check cards and Monday's memory review card (domain/memory/lane.ts).
  "job:memory.lane": ["inbox_item.created"],
  // MEMORY-QUALITY-WATCH: the facts check's archive suggestions, and their archiving once the
  // owner said yes (domain/memory/facts-check.ts).
  "job:memory.facts_check": ["memory_quality.archive_suggested", "memory_quality.archive_applied"],
  // Parity P5 wave 1: the routine history (`recurring_run`) and "after completion": the next
  // occurrence is scheduled from the last one's completion.
  "consumer:recurring.history": ["recurring_work.updated"],
} as const satisfies Record<string, readonly EventType[]>;
